import { useState, useEffect, useRef } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from "recharts";

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Syne:wght@400;600;700;800&display=swap');`;

const css = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #080810; --surface: #0f0f1a; --surface2: #161625; --surface3: #1c1c2e;
    --border: #ffffff0d; --border-bright: #ffffff1a; --text: #eeeef5; --muted: #7070a0;
    --green: #00ffaa; --red: #ff3366; --yellow: #ffd060; --blue: #44aaff;
    --purple: #a855f7; --accent: #7c3aed; --accent2: #c026d3;
  }
  body { font-family:'Syne',sans-serif; background:var(--bg); color:var(--text); min-height:100vh; }
  .mono { font-family:'Space Mono',monospace; }
  .app { max-width:1440px; margin:0 auto; padding:20px 24px; }

  .header { display:flex; align-items:center; justify-content:space-between; margin-bottom:20px; padding-bottom:16px; border-bottom:1px solid var(--border-bright); }
  .header-left { display:flex; align-items:center; gap:12px; }
  .logo-icon { width:40px; height:40px; background:linear-gradient(135deg,var(--accent),var(--accent2)); border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:18px; box-shadow:0 0 20px #7c3aed44; }
  .header-title { font-size:1.3em; font-weight:800; letter-spacing:-0.5px; }
  .header-sub { font-size:0.7em; color:var(--muted); font-family:'Space Mono',monospace; margin-top:1px; }
  .live-badge { display:flex; align-items:center; gap:6px; background:#00ffaa12; border:1px solid #00ffaa25; color:var(--green); font-size:0.65em; font-family:'Space Mono',monospace; padding:4px 10px; border-radius:100px; }
  .live-dot { width:6px; height:6px; border-radius:50%; background:var(--green); animation:blink 1.5s infinite; }
  @keyframes blink { 0%,100%{opacity:1;box-shadow:0 0 4px var(--green);} 50%{opacity:0.2;box-shadow:none;} }

  .tabs { display:flex; gap:4px; background:var(--surface); border:1px solid var(--border-bright); border-radius:10px; padding:4px; margin-bottom:20px; flex-wrap:wrap; }
  .tab { padding:6px 13px; border-radius:7px; font-size:0.7em; font-weight:700; cursor:pointer; border:none; background:transparent; color:var(--muted); transition:all 0.2s; font-family:'Syne',sans-serif; }
  .tab.active { background:var(--surface3); color:var(--text); box-shadow:0 1px 8px #0008; }
  .tab:hover:not(.active) { color:var(--text); }

  .main-grid { display:grid; grid-template-columns:260px 1fr; gap:16px; }
  .sidebar { display:flex; flex-direction:column; gap:12px; }
  .right-panel { display:flex; flex-direction:column; gap:14px; }
  .two-col { display:grid; grid-template-columns:1fr 1fr; gap:14px; }

  .card { background:var(--surface); border:1px solid var(--border); border-radius:14px; padding:16px; }
  .card-title { font-size:0.65em; text-transform:uppercase; letter-spacing:1.8px; color:var(--muted); margin-bottom:12px; font-family:'Space Mono',monospace; }

  .field { margin-bottom:10px; }
  .field label { display:block; font-size:0.68em; color:var(--muted); margin-bottom:4px; font-family:'Space Mono',monospace; }
  .field input,.field select { width:100%; background:var(--surface2); border:1px solid var(--border-bright); border-radius:7px; color:var(--text); font-family:'Space Mono',monospace; font-size:0.82em; padding:7px 9px; outline:none; transition:border-color 0.2s; }
  .field input:focus,.field select:focus { border-color:var(--accent); }
  .field select option { background:#1a1a2e; }
  .slider-row { display:flex; justify-content:space-between; font-size:0.68em; color:var(--muted); margin-bottom:4px; font-family:'Space Mono',monospace; }
  input[type=range] { width:100%; accent-color:var(--accent); border:none !important; padding:0 !important; background:transparent !important; }

  .bmark-row { display:flex; justify-content:space-between; align-items:center; padding:7px 0; border-bottom:1px solid var(--border); }
  .bmark-row:last-child { border-bottom:none; }
  .bmark-label { font-size:0.67em; color:var(--muted); font-family:'Space Mono',monospace; }
  .bmark-value { font-size:0.82em; font-weight:700; font-family:'Space Mono',monospace; color:var(--green); }

  .kpi-grid { display:grid; grid-template-columns:repeat(6,1fr); gap:8px; }
  .kpi-box { background:var(--surface2); border:1px solid var(--border); border-radius:10px; padding:10px 12px; position:relative; overflow:hidden; transition:border-color 0.2s; }
  .kpi-box:hover { border-color:var(--border-bright); }
  .kpi-box.green { border-color:#00ffaa22; } .kpi-box.red { border-color:#ff336622; } .kpi-box.yellow { border-color:#ffd06022; }
  .kpi-glow { position:absolute; top:0; right:0; width:60px; height:60px; border-radius:50%; filter:blur(20px); opacity:0.12; pointer-events:none; }
  .kpi-glow.green { background:var(--green); } .kpi-glow.red { background:var(--red); } .kpi-glow.yellow { background:var(--yellow); }
  .kpi-label { font-size:0.6em; color:var(--muted); font-family:'Space Mono',monospace; margin-bottom:6px; }
  .kpi-value { font-size:1em; font-weight:700; font-family:'Space Mono',monospace; }
  .kpi-value.green{color:var(--green);} .kpi-value.red{color:var(--red);} .kpi-value.yellow{color:var(--yellow);} .kpi-value.white{color:var(--text);}

  .inputs-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; }
  .inputs-grid .field { margin-bottom:0; }

  .alerts-list { display:flex; flex-direction:column; gap:6px; }
  .alert { display:flex; align-items:flex-start; gap:10px; padding:9px 13px; border-radius:8px; font-size:0.78em; border:1px solid transparent; }
  .alert.danger{background:#ff336610;border-color:#ff336628;color:#ff9aaa;}
  .alert.warning{background:#ffd06010;border-color:#ffd06028;color:#ffe080;}
  .alert.success{background:#00ffaa10;border-color:#00ffaa28;color:#60ffcc;}

  .smart-action { padding:11px 13px; border-radius:9px; margin-bottom:7px; border:1px solid transparent; transition:transform 0.15s; cursor:default; }
  .smart-action:hover { transform:translateX(3px); }
  .smart-action.alta{background:#ff335518;border-color:#ff335540;}
  .smart-action.media{background:#ffd06018;border-color:#ffd06040;}
  .smart-action.baixa{background:#00ffaa18;border-color:#00ffaa40;}
  .smart-action-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:5px; }
  .smart-action.alta .sa-area,.smart-action.alta .sa-pri{color:#ff8899;background:#ff335520;}
  .smart-action.media .sa-area,.smart-action.media .sa-pri{color:#ffe080;background:#ffd06020;}
  .smart-action.baixa .sa-area,.smart-action.baixa .sa-pri{color:#60ffcc;background:#00ffaa20;}
  .sa-area { font-size:0.58em; font-family:'Space Mono',monospace; text-transform:uppercase; letter-spacing:1px; padding:1px 6px; border-radius:4px; }
  .sa-pri  { font-size:0.58em; font-family:'Space Mono',monospace; font-weight:700; padding:1px 6px; border-radius:100px; }
  .sa-text { font-size:0.82em; font-weight:700; line-height:1.4; }
  .sa-sub  { font-size:0.7em; color:var(--muted); margin-top:3px; line-height:1.5; }

  .decision-table { width:100%; border-collapse:collapse; font-size:0.75em; }
  .decision-table th { font-family:'Space Mono',monospace; font-size:0.62em; text-transform:uppercase; letter-spacing:1px; color:var(--muted); padding:8px 12px; border-bottom:1px solid var(--border-bright); text-align:left; }
  .decision-table td { padding:9px 12px; border-bottom:1px solid var(--border); vertical-align:top; line-height:1.5; }
  .decision-table tr:last-child td { border-bottom:none; }
  .decision-table tr:hover td { background:var(--surface2); }

  .bump-card { background:var(--surface2); border:1px solid var(--border); border-radius:10px; padding:12px; position:relative; overflow:hidden; cursor:pointer; transition:border-color 0.2s,transform 0.15s; }
  .bump-card:hover { border-color:var(--border-bright); transform:translateY(-2px); }
  .bump-card.winner { border-color:#ffd06055; background:linear-gradient(135deg,#1c1c2e,#1a1520); }
  .bump-bar-bg { height:6px; background:var(--surface3); border-radius:3px; margin:8px 0 6px; }
  .bump-bar-fill { height:100%; border-radius:3px; transition:width 0.8s cubic-bezier(.4,0,.2,1); }

  .protocol-row { display:flex; gap:12px; padding:10px 0; border-bottom:1px solid var(--border); align-items:flex-start; }
  .protocol-row:last-child { border-bottom:none; }
  .protocol-icon { font-size:1.3em; flex-shrink:0; margin-top:2px; }
  .protocol-title { font-size:0.8em; font-weight:700; margin-bottom:3px; }
  .protocol-cond { font-size:0.68em; color:var(--muted); font-family:'Space Mono',monospace; line-height:1.6; }

  .ai-section { display:flex; flex-direction:column; gap:10px; }
  .ai-textarea { width:100%; background:var(--surface2); border:1px solid var(--border-bright); border-radius:9px; color:var(--text); font-family:'Space Mono',monospace; font-size:0.78em; padding:11px; resize:vertical; min-height:65px; outline:none; }
  .ai-textarea:focus { border-color:var(--accent); }
  .ai-textarea::placeholder { color:var(--muted); }
  .btn-primary { background:linear-gradient(135deg,var(--accent),var(--accent2)); color:white; border:none; border-radius:9px; padding:12px 18px; font-family:'Syne',sans-serif; font-size:0.88em; font-weight:700; cursor:pointer; transition:opacity 0.2s,transform 0.1s; display:flex; align-items:center; justify-content:center; gap:8px; box-shadow:0 4px 20px #7c3aed33; }
  .btn-primary:hover:not(:disabled) { opacity:0.9; transform:translateY(-1px); }
  .btn-primary:disabled { opacity:0.5; cursor:not-allowed; transform:none; }
  .btn-secondary { background:var(--surface2); border:1px solid var(--border-bright); color:var(--text); border-radius:8px; padding:8px 16px; font-family:'Space Mono',monospace; font-size:0.75em; cursor:pointer; }
  .ai-result { background:var(--surface2); border:1px solid var(--border-bright); border-radius:11px; padding:18px; font-size:0.8em; line-height:1.75; color:#c8c8e0; animation:fadeIn 0.4s ease; }
  @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
  .ai-result h2 { font-size:1em; font-weight:800; color:var(--text); margin:16px 0 7px; padding-bottom:5px; border-bottom:1px solid var(--border); }
  .ai-result h2:first-child { margin-top:0; }
  .ai-result strong { color:var(--text); }
  .ai-result ul,.ai-result ol { padding-left:18px; }
  .ai-result li { margin-bottom:4px; }
  .ai-result p { margin-bottom:7px; }
  .spinner  { width:14px; height:14px; border:2px solid #ffffff30; border-top-color:white; border-radius:50%; animation:spin 0.7s linear infinite; }
  .spinner2 { width:14px; height:14px; border:2px solid var(--border-bright); border-top-color:var(--accent); border-radius:50%; animation:spin 0.7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg)} }
  .loading { display:flex; align-items:center; gap:9px; color:var(--muted); font-family:'Space Mono',monospace; font-size:0.75em; padding:12px; }

  .history-summary { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin-bottom:12px; }
  .summary-box { background:var(--surface2); border:1px solid var(--border); border-radius:8px; padding:9px; text-align:center; }
  .summary-box-label { font-size:0.58em; color:var(--muted); font-family:'Space Mono',monospace; display:block; margin-bottom:3px; }
  .summary-box-value { font-size:0.9em; font-weight:700; font-family:'Space Mono',monospace; }
  .history-item { background:var(--surface2); border:1px solid var(--border); border-radius:9px; overflow:hidden; cursor:pointer; transition:border-color 0.2s; }
  .history-item:hover { border-color:var(--border-bright); }
  .history-header { display:flex; justify-content:space-between; align-items:center; padding:10px 14px; }
  .history-meta { font-size:0.68em; font-family:'Space Mono',monospace; color:var(--muted); }
  .history-badges { display:flex; gap:6px; }
  .badge { font-size:0.63em; font-family:'Space Mono',monospace; padding:2px 8px; border-radius:100px; }
  .badge.green{background:#00ffaa18;color:var(--green);} .badge.red{background:#ff336618;color:var(--red);} .badge.yellow{background:#ffd06018;color:var(--yellow);}
  .history-body { border-top:1px solid var(--border); padding:14px; font-size:0.76em; line-height:1.65; color:#a8a8c8; }
  .history-body h2 { font-size:1em; font-weight:800; color:var(--text); margin:12px 0 5px; }
  .history-body strong { color:var(--text); }
  .history-body ul,.history-body ol { padding-left:18px; }
  .history-body p { margin-bottom:5px; }

  .chart-wrap { height:200px; }
  .geo-row { display:flex; align-items:center; gap:10px; padding:8px 0; border-bottom:1px solid var(--border); }
  .geo-row:last-child { border-bottom:none; }
  .geo-bar { height:4px; border-radius:2px; margin-top:4px; transition:width 0.8s; }
  .feed-item { display:flex; align-items:flex-start; gap:10px; padding:8px 0; border-bottom:1px solid var(--border); animation:fadeIn 0.4s ease; }
  .feed-item:last-child { border-bottom:none; }
  .feed-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; margin-top:4px; }
  .feed-dot.compra{background:var(--green);box-shadow:0 0 6px var(--green);}
  .feed-dot.checkout{background:var(--yellow);box-shadow:0 0 6px var(--yellow);}
  .feed-dot.visita{background:var(--blue);box-shadow:0 0 6px var(--blue);}
  .feed-text { font-size:0.75em; line-height:1.4; }
  .feed-time { font-size:0.65em; color:var(--muted); font-family:'Space Mono',monospace; margin-top:2px; }
  .funnel-step { margin-bottom:10px; }
  .funnel-labels { display:flex; justify-content:space-between; font-size:0.67em; color:var(--muted); font-family:'Space Mono',monospace; margin-bottom:4px; }
  .funnel-bar-bg { height:8px; background:var(--surface2); border-radius:4px; overflow:hidden; }
  .funnel-bar-fill { height:100%; border-radius:4px; transition:width 0.8s cubic-bezier(.4,0,.2,1); }
  .funnel-dropoff { font-size:0.62em; color:var(--red); font-family:'Space Mono',monospace; margin-top:2px; }
`;

// ─── ANTI-BURRO: Safe JSON parse com Regex ───────────────────────────────────
const parseAIResponse = (text) => {
  try {
    const block = text.match(/```json\s*([\s\S]*?)```/);
    const raw = block ? block[1] : text.match(/\{[\s\S]*\}/)?.[0];
    return raw ? JSON.parse(raw) : null;
  } catch(e) { return null; }
};

function renderMarkdown(text) {
  return text.split("\n").map((line,i) => {
    if (line.startsWith("## ")||line.startsWith("### ")) return <h2 key={i}>{line.replace(/^#{2,3} /,"")}</h2>;
    const parts = line.split(/\*\*(.*?)\*\*/g);
    const r = parts.map((p,j)=>j%2===1?<strong key={j}>{p}</strong>:p);
    if (line.startsWith("- ")||line.startsWith("• ")) return <li key={i} style={{marginLeft:16}}>{r.slice(1)}</li>;
    if (/^\d+\. /.test(line)) return <li key={i} style={{marginLeft:16}}>{r}</li>;
    if (line.trim()==="") return <br key={i}/>;
    return <p key={i}>{r}</p>;
  });
}

function CustomTooltip({active,payload,label}) {
  if (!active||!payload?.length) return null;
  return (
    <div style={{background:"#1c1c2e",border:"1px solid #ffffff1a",borderRadius:8,padding:"10px 14px",fontFamily:"Space Mono",fontSize:"0.72em"}}>
      <div style={{color:"#7070a0",marginBottom:6}}>{label}</div>
      {payload.map((p,i)=><div key={i} style={{color:p.color,marginBottom:2}}>{p.name}: {["Gasto","Receita"].includes(p.name)?`R$${p.value}`:p.value}</div>)}
    </div>
  );
}

function SmartActionCard({action,priority,area,sub,source="regra"}) {
  return (
    <div className={`smart-action ${priority}`}>
      <div className="smart-action-header">
        <span className="sa-area">{area}</span>
        <div style={{display:"flex",gap:5}}>
          {source==="ia"&&<span style={{fontSize:"0.58em",fontFamily:"Space Mono",color:"#a855f7",background:"#a855f720",padding:"1px 6px",borderRadius:100}}>IA</span>}
          <span className="sa-pri">🔥 {priority.toUpperCase()}</span>
        </div>
      </div>
      <div className="sa-text">{action}</div>
      {sub&&<div className="sa-sub">{sub}</div>}
    </div>
  );
}

function GuardianPanel({cpc,ctr,gasto,conversoes,freq}) {
  const [L,setL] = useState({cpc_max:0.50,ctr_min:1.0,gsem:30,freq_max:3.5,conv_min:1});
  const rules = [
    {label:"CPC Máx",   field:"cpc_max", unit:"R$",step:"0.01",check:()=>cpc>L.cpc_max,          bad:()=>`CPC R$${cpc.toFixed(2)} acima`,       ok:()=>`CPC ok`},
    {label:"CTR Mín",   field:"ctr_min", unit:"%", step:"0.1", check:()=>ctr<L.ctr_min,           bad:()=>`CTR ${ctr.toFixed(2)}% baixo`,         ok:()=>`CTR ok`},
    {label:"Gasto s/cv",field:"gsem",    unit:"R$",step:"1",   check:()=>gasto>L.gsem&&conversoes===0, bad:()=>`R$${gasto.toFixed(0)} sem conv!`, ok:()=>conversoes>0?`${conversoes} conv ok`:`ok`},
    {label:"Freq Máx",  field:"freq_max",unit:"x", step:"0.1", check:()=>freq>L.freq_max,          bad:()=>`Freq ${freq.toFixed(1)}x saturando`,   ok:()=>`Freq ok`},
    {label:"Conv Mín",  field:"conv_min",unit:"",  step:"1",   check:()=>conversoes<L.conv_min,    bad:()=>`${conversoes} conv abaixo`,            ok:()=>`${conversoes} conv ok`},
  ];
  return (
    <div style={{display:"flex",flexDirection:"column",gap:7}}>
      {rules.map(r=>{
        const t=r.check();
        return (
          <div key={r.field} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 11px",background:t?"#ff336610":"#00ffaa08",border:`1px solid ${t?"#ff336625":"#00ffaa18"}`,borderRadius:8}}>
            <span style={{fontSize:"0.9em",flexShrink:0}}>{t?"🔴":"✅"}</span>
            <div style={{flex:1}}>
              <div style={{fontSize:"0.63em",color:"var(--muted)",fontFamily:"Space Mono",marginBottom:1}}>{r.label}</div>
              <div style={{fontSize:"0.74em",color:t?"#ff9aaa":"#55ffbb"}}>{t?r.bad():r.ok()}</div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
              <input type="number" step={r.step} value={L[r.field]} onChange={e=>setL(p=>({...p,[r.field]:+e.target.value}))}
                style={{width:58,background:"var(--surface)",border:"1px solid var(--border-bright)",borderRadius:6,color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.72em",padding:"3px 6px",outline:"none",textAlign:"right"}}/>
              <span style={{fontSize:"0.62em",color:"var(--muted)",fontFamily:"Space Mono"}}>{r.unit}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FunnelChart({impressoes,cliques,checkouts,vendas}) {
  const s=(n,d)=>d>0?(n/d)*100:0;
  const steps=[
    {name:"Impressões",val:impressoes,pct:100,color:"#7c3aed"},
    {name:"Cliques",val:cliques,pct:s(cliques,impressoes),color:"#a855f7"},
    {name:"Checkouts",val:checkouts,pct:s(checkouts,cliques),color:"#c026d3"},
    {name:"Vendas",val:vendas,pct:s(vendas,checkouts||1),color:"#00ffaa"},
  ];
  return (
    <div>
      {steps.map((st,i)=>{
        const d=i>0?100-st.pct:null;
        return (
          <div className="funnel-step" key={i}>
            <div className="funnel-labels"><span>{st.name}</span><span style={{color:"var(--text)"}}>{st.val.toLocaleString()} <span style={{color:"var(--muted)"}}>({st.pct.toFixed(1)}%)</span></span></div>
            <div className="funnel-bar-bg"><div className="funnel-bar-fill" style={{width:`${Math.min(st.pct,100)}%`,background:`linear-gradient(90deg,${st.color}cc,${st.color})`}}/></div>
            {d!==null&&d>0&&<div className="funnel-dropoff">↳ {d.toFixed(1)}% drop-off</div>}
          </div>
        );
      })}
    </div>
  );
}

const GEO=[
  {flag:"🇧🇷",country:"Brasil",   roas:2.5,cpa:25,color:"#00ffaa"},
  {flag:"🇲🇽",country:"México",   roas:3.2,cpa:18,color:"#a855f7"},
  {flag:"🇨🇱",country:"Chile",    roas:4.5,cpa:22,color:"#ffd060"},
  {flag:"🇨🇴",country:"Colômbia", roas:1.8,cpa:12,color:"#ff6699"},
  {flag:"🇦🇷",country:"Argentina",roas:2.1,cpa:15,color:"#44aaff"},
];
const GEO_MAX = Math.max(...GEO.map(g=>g.roas));

const CITIES=["São Paulo","Ciudad de México","Santiago","Bogotá","Buenos Aires","Lima","Porto Alegre","Monterrey"];
const BUMPS_NAMES=["Áudios de Reprogramação","Código Secreto","Manual dos Anjos","Oração dos Milagres","Protocolo 21 Dias"];
const EVTS=[
  {type:"compra",icon:"🛒",gen:(c,b)=>`Compra em ${c} + "${b}"`},
  {type:"checkout",icon:"⚡",gen:(c)=>`Checkout iniciado em ${c}`},
  {type:"visita",icon:"👁",gen:(c)=>`Visita em ${c}`},
];

function LiveFeed() {
  const [evs,setEvs]=useState(()=>Array.from({length:5},(_,i)=>{
    const e=EVTS[Math.floor(Math.random()*EVTS.length)];
    const c=CITIES[Math.floor(Math.random()*CITIES.length)];
    const b=BUMPS_NAMES[Math.floor(Math.random()*BUMPS_NAMES.length)];
    return {id:i,type:e.type,icon:e.icon,text:e.gen(c,b),ago:(i+1)*2};
  }));
  useEffect(()=>{
    const iv=setInterval(()=>{
      const e=EVTS[Math.floor(Math.random()*EVTS.length)];
      const c=CITIES[Math.floor(Math.random()*CITIES.length)];
      const b=BUMPS_NAMES[Math.floor(Math.random()*BUMPS_NAMES.length)];
      setEvs(p=>[{id:Date.now(),type:e.type,icon:e.icon,text:e.gen(c,b),ago:0},...p.slice(0,6).map(x=>({...x,ago:x.ago+1}))]);
    },4000);
    return ()=>clearInterval(iv);
  },[]);
  return <div>{evs.map(e=>(
    <div className="feed-item" key={e.id}>
      <div className={`feed-dot ${e.type}`}/>
      <div><div className="feed-text">{e.icon} {e.text}</div><div className="feed-time">{e.ago===0?"agora mesmo":`há ${e.ago} min`}</div></div>
    </div>
  ))}</div>;
}

function genWeek(gasto,conv,ticket) {
  // Determinístico — padrão semanal realista. Atualiza quando dados reais chegam da VPS/Make.
  const seed=[0.82,0.74,0.95,0.68,1.10,0.88,0.77];
  return ["Seg","Ter","Qua","Qui","Sex","Sáb","Dom"].map((d,i)=>{
    const n=seed[i];
    return {dia:d,Gasto:+(gasto*n).toFixed(0),Conversões:Math.max(0,Math.round(conv*n)),Receita:Math.round(conv*n)*ticket};
  });
}


// ─── DETAILED RECOMMENDATIONS — v3: Pro Manager Mode ─────────────────────────
// Cobre: escala, recuperação, salvamento, quando deletar, ticket, produtos, funil
function DetailedRecommendations({ roas, cpa, ctr, freq, hookRate, taxaConv, conversoes,
  alcance, impressoes, lucro, breakEven, cpaIdeal, aiAcoes, ticket, margem, checkouts, gasto }) {

  const recs = [];
  const taxaFechamento = checkouts > 0 ? (conversoes / checkouts) * 100 : 0;
  const semConversao   = conversoes === 0 && gasto > cpaIdeal * 2;
  const lucroPorVenda  = ticket * (margem / 100);

  // ════════════════════════════════════════════════════════════════
  // 🟢 ESCALA — quando e como escalar cada tipo
  // ════════════════════════════════════════════════════════════════

  if (roas > breakEven * 1.5 && freq < 2.0 && conversoes >= 5) {
    recs.push({ badge:"green", categoria:"ESCALA", titulo:"🚀 Escala Vertical Agressiva — CBO",
      acao:"Subir orçamento 30% a cada 48h. Migrar para CBO se estiver em ABO.",
      como:"1. No Ads Manager, edite o conjunto vencedor → aumente orçamento diário em 30%.\n2. Se tiver 2+ conjuntos lucrativos, mova o budget para nível de campanha (CBO) — o algoritmo vai distribuir para o melhor.\n3. Aguarde 48h antes de novo aumento. O aprendizado reinicia a cada grande alteração.\n4. Se CPA subir > 20%, reduza 15% e aguarde 72h antes de nova tentativa.\n5. Meta: escalar até o ROAS cair para 1.2x o break-even, aí estabilize.",
      porque:`ROAS ${roas.toFixed(2)}x com frequência ${freq.toFixed(1)}x baixa = algoritmo em fase de exploração eficiente. O leilão ainda tem muito inventário novo. Este é o momento mais raro e mais valioso — escale com coragem mas com método.`,
      metrica:`ROAS ${roas.toFixed(2)}x / Break-even ${breakEven.toFixed(2)}x / Freq ${freq.toFixed(1)}x`
    });
  } else if (roas > breakEven * 1.3 && freq < 2.5 && conversoes >= 3) {
    recs.push({ badge:"green", categoria:"ESCALA", titulo:"📈 Escala Vertical Moderada — +15% a cada 3 dias",
      acao:"Aumentar orçamento em 15% a cada 3 dias, monitorando CPA de perto.",
      como:"1. Aumente 15% no orçamento do conjunto.\n2. Monitore o CPA nas primeiras 24h após cada aumento.\n3. Se CPA ficar estável (variação < 15%), repita em 3 dias.\n4. Se CPA subir > 15%, pause 48h antes de nova tentativa.\n5. Nunca aumente mais de 2x em menos de 5 dias — o algoritmo perde o aprendizado.",
      porque:`ROAS ${roas.toFixed(2)}x lucrativo, mas aumentos agressivos podem romper o aprendizado do algoritmo. Escala gradual preserva o CPA e mantém a fase de exploração estável.`,
      metrica:`ROAS ${roas.toFixed(2)}x / Freq ${freq.toFixed(1)}x / Conv ${conversoes}`
    });
  }

  if (roas > breakEven && freq > 3.0 && ctr > 1.0) {
    recs.push({ badge:"green", categoria:"ESCALA", titulo:"🌎 Escala Horizontal — Novos Públicos e Países",
      acao:"Duplicar o conjunto vencedor para novos segmentos sem alterar o criativo.",
      como:"1. Duplique o conjunto (Ads Manager → botão 'Copiar').\n2. Na cópia, troque APENAS o público:\n   → Opção A: novo interesse relacionado\n   → Opção B: Lookalike 1% dos seus compradores\n   → Opção C: novo país (MX, CL, CO em sequência)\n3. Mantenha EXATAMENTE o mesmo criativo, orçamento e CTA.\n4. Compare os dois conjuntos após 3 dias. Pause o de pior CPA.\n5. Repita o processo com o país/público vencedor.",
      porque:`Frequência ${freq.toFixed(1)}x indica que o público atual está saturando. Expandir horizontalmente com o criativo vencedor é a forma mais segura de crescer sem riscos — você já sabe que o anúncio converte, só está mudando quem vê.`,
      metrica:`Freq ${freq.toFixed(1)}x / CTR ${ctr.toFixed(2)}% / ROAS ${roas.toFixed(2)}x`
    });
  }

  if (roas > breakEven * 1.2 && taxaConv > 1.5 && conversoes >= 5) {
    recs.push({ badge:"green", categoria:"ESCALA", titulo:"💰 Escala de Oferta — Aumentar Ticket Médio",
      acao:"Adicionar upsell imediato pós-compra e fortalecer os bumps.",
      como:`1. Implemente upsell de R$${(ticket * 0.6).toFixed(0)}–R$${(ticket * 1.2).toFixed(0)} na página de obrigado (entrega em 60s após compra).\n2. Renomeie bumps que vendem pouco — teste nomes mais urgentes ('Acesso Imediato' vs 'E-book').\n3. Adicione 1 novo produto físico ou digital como downsell (50% do preço do produto principal).\n4. Teste aumentar o ticket principal em 15–20% — se a taxa de conversão cair menos de 10%, o lucro total aumenta.\n5. Cada R$1 de LTV adicional = você pode pagar mais pelo CPA e ainda lucrar.`,
      porque:`Campanha lucrativa com boa conversão é o momento certo para maximizar o valor por cliente. Aumentar LTV reduz a pressão sobre o ROAS e permite escalar mais agressivamente o orçamento.`,
      metrica:`Taxa conv ${taxaConv.toFixed(2)}% / ROAS ${roas.toFixed(2)}x / ${conversoes} conv`
    });
  }

  if (alcance > 0 && impressoes > 0 && alcance / impressoes > 0.7 && roas >= breakEven && freq < 1.8) {
    recs.push({ badge:"green", categoria:"ESCALA", titulo:"⚡ Escala de Criativos — Multiplicar Vencedores",
      acao:"Criar 3 variações do criativo campeão com pequenas mudanças para multiplicar o alcance.",
      como:"1. Identifique o criativo com maior ROAS e menor CPA (use o relatório por anúncio).\n2. Crie 3 variações mudando UMA coisa por vez: gancho, miniatura, texto do botão.\n3. Agrupe-as em um novo conjunto com o mesmo público e orçamento.\n4. Em 3 dias, pause as 2 piores e escale a melhor.\n5. Repetir esse ciclo a cada 7 dias mantém o algoritmo em exploração constante sem saturar.",
      porque:`Frequência baixa e alcance amplo indicam que há muito público para atingir. Multiplicar criativos permite que o algoritmo teste variações e identifique qual ressoa com cada sub-segmento do público.`,
      metrica:`Freq ${freq.toFixed(1)}x / Alcance ${alcance.toLocaleString()}`
    });
  }

  // ════════════════════════════════════════════════════════════════
  // 🟡 RECUPERAÇÃO — salvar campanhas que estão caindo
  // ════════════════════════════════════════════════════════════════

  if (ctr < 1.0 && hookRate < 2.0) {
    recs.push({ badge:"red", categoria:"RECUPERAÇÃO", titulo:"🎨 Criativo Fraco — Operação Gancho",
      acao:"Criar 3 novos ganchos (primeiros 3s) e testar em modo de isolamento.",
      como:"1. Analise os comentários do anúncio atual — a dor real está lá.\n2. Crie 3 variações mudando APENAS os primeiros 3 segundos:\n   → Tipo choque: 'Você está perdendo R$X por mês sem saber'\n   → Tipo promessa: 'Em 7 dias sua vida vai mudar com isso'\n   → Tipo prova: 'Essa mulher fez X em 30 dias com esse método'\n3. Desative o anúncio atual. Suba as 3 variações com o MESMO orçamento.\n4. Após 48h, pause os 2 de pior hook rate.\n5. Se nenhum passar de 2% de hook rate, o problema pode ser o PÚBLICO, não o criativo.",
      porque:`CTR ${ctr.toFixed(2)}% e Hook Rate ${hookRate.toFixed(2)}% — o anúncio não está parando o scroll. Sem os primeiros 3 segundos funcionando, todo o investimento em produção, oferta e página de vendas é desperdiçado.`,
      metrica:`CTR ${ctr.toFixed(2)}% | Hook ${hookRate.toFixed(2)}%`
    });
  }

  if (ctr > 1.5 && taxaConv < 1.0 && taxaFechamento < 30) {
    recs.push({ badge:"red", categoria:"RECUPERAÇÃO", titulo:"🖥️ Funil Vazando — Problema na Página de Vendas",
      acao:"Otimizar landing page urgentemente: velocidade, headline e checkout.",
      como:"1. Teste velocidade no PageSpeed Insights — se < 80, otimize imagens e hospedagem.\n2. Reescreva a headline: ela deve repetir a promessa exata do anúncio (continuidade de mensagem).\n3. Coloque prova social ACIMA da dobra — depoimento real com foto e resultado específico.\n4. Simplifique o checkout: remova campos desnecessários, adicione opções de pagamento locais (PIX, parcelamento).\n5. Instale Hotjar ou Microsoft Clarity — veja onde as pessoas saem e corrija.\n6. Teste colocar o botão de compra em 3 posições diferentes na página.",
      porque:`CTR ${ctr.toFixed(2)}% ótimo (anúncio funciona) mas ${taxaFechamento.toFixed(0)}% dos que chegam no checkout não fecham. O problema está DEPOIS do clique. Cada melhoria na página aumenta lucro sem gastar mais em anúncio.`,
      metrica:`CTR ${ctr.toFixed(2)}% | Conv ${taxaConv.toFixed(2)}% | Fechamento ${taxaFechamento.toFixed(0)}%`
    });
  }

  if (checkouts > conversoes * 3 && taxaFechamento < 20) {
    recs.push({ badge:"red", categoria:"RECUPERAÇÃO", titulo:"🛒 Abandono de Checkout — Recuperação de Receita Perdida",
      acao:"Ativar fluxo de recuperação automático (email + WhatsApp) nas primeiras 2 horas.",
      como:"1. Configure automação: se checkout iniciado e não comprou em 30 minutos → dispare email.\n2. Sequência de 3 mensagens:\n   → 30min: 'Você esqueceu algo' + link direto\n   → 2h: Prova social + urgência ('Apenas X unidades')\n   → 24h: Oferta especial de R$X de desconto ou bônus extra\n3. Adicione WhatsApp click-to-chat na página de checkout.\n4. Revise o checkout: parcelamento visível, selos de segurança, garantia explícita.\n5. Teste remover campos e deixar só nome + email + cartão.",
      porque:`${checkouts} checkouts vs ${conversoes} vendas — mais de ${100 - Math.round(taxaFechamento)}% das pessoas saem sem comprar. Cada recuperação é receita que você já pagou para gerar. Fluxo de recuperação profissional pode salvar 15–30% dessas vendas.`,
      metrica:`${checkouts} checkouts | ${conversoes} vendas | ${taxaFechamento.toFixed(0)}% fechamento`
    });
  }

  if (cpa > cpaIdeal * 1.5) {
    recs.push({ badge:"red", categoria:"RECUPERAÇÃO", titulo:"💰 CPA Crítico — Teto de Lance + Novo Público",
      acao:`Ativar lance com teto em R$${(cpaIdeal * 1.15).toFixed(2)} e criar lookalike de compradores.`,
      como:`1. Vá ao conjunto de anúncios → Estratégia de lance → 'Custo por resultado máximo'.\n2. Defina o teto em R$${(cpaIdeal * 1.15).toFixed(2)} (15% acima do ideal para não travar entregas).\n3. Crie um Custom Audience com seus compradores dos últimos 30 dias.\n4. Gere um Lookalike 1% desse audience e use em um novo conjunto paralelo.\n5. Pause os públicos com CPC > R$${(cpaIdeal * 0.3).toFixed(2)} e sem conversão em 48h.\n6. Aguarde 5–7 dias para o algoritmo se recalibrar com o teto.`,
      porque:`CPA R$${cpa.toFixed(2)} (${(((cpa/cpaIdeal)-1)*100).toFixed(0)}% acima do ideal). O algoritmo está pagando caro por conversões de baixa qualidade. O teto de lance força ele a encontrar conversões mais baratas. O lookalike de compradores qualifica o público.`,
      metrica:`CPA R$${cpa.toFixed(2)} | Ideal R$${cpaIdeal.toFixed(2)} | Desvio ${(((cpa/cpaIdeal)-1)*100).toFixed(0)}%`
    });
  } else if (cpa > cpaIdeal * 1.2) {
    recs.push({ badge:"yellow", categoria:"RECUPERAÇÃO", titulo:"💸 CPA Acima do Ideal — Ajuste Preventivo",
      acao:`Ativar lance com teto em R$${cpaIdeal.toFixed(2)} e revisar segmentação.`,
      como:`1. Adicione teto de CPA em R$${cpaIdeal.toFixed(2)} na estratégia de lance.\n2. Exclua públicos que viram o anúncio > 4x (segmentos de remarketing antigos).\n3. Revise as exclusões de público — adicione compradores anteriores para não pagar por quem já converteu.\n4. Aguarde 3 dias e reavalie.`,
      porque:`CPA ${(((cpa/cpaIdeal)-1)*100).toFixed(0)}% acima. Ainda recuperável com ajustes simples. Agir agora previne que vire um problema crítico.`,
      metrica:`CPA R$${cpa.toFixed(2)} | Ideal R$${cpaIdeal.toFixed(2)}`
    });
  }

  if (freq > 3.0 && freq <= 3.5 && roas >= breakEven) {
    recs.push({ badge:"yellow", categoria:"RECUPERAÇÃO", titulo:"🔄 Saturação Iniciando — Renovação Preventiva de Criativo",
      acao:"Criar variante do criativo com nova estética antes que a saturação derrube o ROAS.",
      como:"1. Mantenha a ESTRUTURA: mesma oferta, mesmo público, mesmo CTA.\n2. Mude a ESTÉTICA: cor dominante, música de fundo, voz do locutor, ângulo da câmera.\n3. Ative o novo criativo no mesmo conjunto e deixe rodando em paralelo.\n4. Após 3 dias, pause o de menor CTR.\n5. Regra dos profissionais: suba criativo novo a cada 7 dias, mesmo que o atual ainda esteja bom.",
      porque:`Frequência ${freq.toFixed(1)}x — o público começa a 'ignorar' o anúncio. A mudança de estética 'reseta' a percepção sem perder o aprendizado do algoritmo sobre a audiência.`,
      metrica:`Freq ${freq.toFixed(1)}x | ROAS ${roas.toFixed(2)}x`
    });
  }

  if (hookRate < 1.0 && ctr >= 1.0) {
    recs.push({ badge:"yellow", categoria:"RECUPERAÇÃO", titulo:"🎣 Hook Rate Baixo — Reescrever Abertura Sem Tocar no Restante",
      acao:"Gravar nova abertura de 3s e montar novo anúncio mantendo o corpo.",
      como:"1. Edite o vídeo: corte os primeiros 3–5 segundos e grave novos.\n2. Teste 3 tipos de gancho na abertura:\n   → Curiosidade: 'O que ninguém te contou sobre X'\n   → Urgência: 'Isso vai sair do ar em 48h'\n   → Problema: 'Se você tem X problema, esse vídeo é para você'\n3. Suba cada versão como anúncio separado no mesmo conjunto.\n4. Após 48h, mantenha apenas o de melhor hook rate.",
      porque:`Hook rate ${hookRate.toFixed(2)}% indica que < 1% para para ver. CTR ok mostra que quando para, converte — o problema é especificamente na abertura.`,
      metrica:`Hook ${hookRate.toFixed(2)}% | CTR ${ctr.toFixed(2)}%`
    });
  }

  // ════════════════════════════════════════════════════════════════
  // 🔴 DELETAR / PAUSAR — quando parar de insistir
  // ════════════════════════════════════════════════════════════════

  if (freq > 3.5 && roas < breakEven) {
    recs.push({ badge:"red", categoria:"DELETAR / PAUSAR", titulo:"⛔ PAUSAR AGORA — Prejuízo + Saturação Total",
      acao:"Pausar o conjunto imediatamente. Não tente salvar — crie um novo do zero.",
      como:"1. Pause o conjunto (não delete — você pode reativar para testes futuros).\n2. Anote o que foi testado: público, criativo, oferta, período.\n3. Espere 7 dias antes de reativar qualquer elemento desse conjunto.\n4. Novo conjunto = novo criativo + novo público + mesmo produto.\n5. Reinicie com 40% do orçamento anterior para revalidar.\n6. Se após 3 dias o novo conjunto também não converter, o problema pode ser a OFERTA — revise o produto/preço.",
      porque:`ROAS ${roas.toFixed(2)}x abaixo do break-even (${breakEven.toFixed(2)}x) com frequência ${freq.toFixed(1)}x. Você está pagando para mostrar um anúncio rejeitado para pessoas que já ignoraram. Cada R$1 gasto agora é prejuízo garantido.`,
      metrica:`ROAS ${roas.toFixed(2)}x | Break-even ${breakEven.toFixed(2)}x | Freq ${freq.toFixed(1)}x`
    });
  }

  if (semConversao) {
    recs.push({ badge:"red", categoria:"DELETAR / PAUSAR", titulo:"🚨 Gasto Sem Conversão — Pausar e Diagnosticar",
      acao:`R$${gasto.toFixed(0)} gastos sem venda. Pausar agora e identificar o gargalo antes de continuar.`,
      como:"1. PAUSE TUDO imediatamente.\n2. Diagnóstico em ordem:\n   → CTR > 1%? Criativo ok, problema no funil.\n   → CTR < 1%? Criativo ruim ou público errado.\n   → Checkouts > 0 mas 0 vendas? Problema no checkout (preço, forma de pagamento, bugs).\n   → Checkouts = 0? Problema na landing page.\n3. Corrija apenas 1 elemento por vez. Reinicie com orçamento mínimo (R$20/dia).\n4. Não escale enquanto não entender o gargalo.",
      porque:`Sem conversões, qualquer gasto é desperdício. O diagnóstico correto do funil (criativo → landing → checkout) economiza dinheiro e tempo. Parar não é fracasso — é decisão profissional.`,
      metrica:`R$${gasto.toFixed(0)} gastos | 0 conversões | CPA ideal R$${cpaIdeal.toFixed(2)}`
    });
  }

  if (roas < breakEven * 0.7 && conversoes > 0 && freq < 2.5) {
    recs.push({ badge:"red", categoria:"DELETAR / PAUSAR", titulo:"📉 ROAS Crítico — Pausar e Reformular Oferta",
      acao:"ROAS muito abaixo do break-even mesmo com conversões. O produto/preço precisa ser reformulado.",
      como:"1. Pause o conjunto por 48h para análise sem gastar.\n2. Calcule: existe algum ajuste de preço que tornaria isso lucrativo?\n3. Teste aumentar o ticket em 20% — se a conversão cair proporcionalmente, a receita pode até subir.\n4. Adicione urgência real (bônus por tempo limitado) para justificar o preço.\n5. Se após reformulação ainda não fechar o break-even, considere mudar o produto ou segmento.",
      porque:`ROAS ${roas.toFixed(2)}x com frequência baixa (${freq.toFixed(1)}x) — não é saturação. O problema é estrutural: o custo de aquisição não fecha com o ticket atual. Reformular antes de escalar evita prejuízo maior.`,
      metrica:`ROAS ${roas.toFixed(2)}x | Break-even ${breakEven.toFixed(2)}x | Ticket R$${ticket}`
    });
  }

  // ════════════════════════════════════════════════════════════════
  // 🔵 TICKET / PRODUTO — estratégias de LTV e portfólio
  // ════════════════════════════════════════════════════════════════

  if (roas >= breakEven && taxaConv > 1.0 && lucro > 0) {
    recs.push({ badge:"green", categoria:"PRODUTO / LTV", titulo:"🛍️ Hora de Aumentar o Ticket — Teste de Preço",
      acao:`Testar aumento de 15–20% no ticket (de R$${ticket} para R$${(ticket * 1.18).toFixed(0)}).`,
      como:`1. Crie uma landing page alternativa com o novo preço.\n2. Divida o tráfego 50/50 entre o preço atual e o novo (teste A/B no Ads Manager).\n3. Monitore por 5 dias: se a taxa de conversão cair menos de 10%, o lucro total aumentou.\n4. Justifique o aumento com novo bônus, garantia estendida ou nome mais premium.\n5. Calcule: R$${(ticket * 1.18).toFixed(0)} × mesmo volume de conversões = R$${(conversoes * ticket * 0.18).toFixed(0)}/dia a mais sem aumentar o gasto.`,
      porque:`Com campanha lucrativa e conversão sólida, testar o teto de preço é a alavanca mais poderosa. Um aumento de 15% no ticket com apenas 5% de queda na conversão já gera mais lucro total.`,
      metrica:`Ticket R$${ticket} | Conv ${taxaConv.toFixed(2)}% | ROAS ${roas.toFixed(2)}x`
    });
  }

  if (roas >= breakEven * 1.2 && conversoes >= 3) {
    recs.push({ badge:"green", categoria:"PRODUTO / LTV", titulo:"📦 Adicionar Novos Produtos ao Portfólio",
      acao:"Criar complemento digital ou físico para aumentar LTV sem novo custo de aquisição.",
      como:`1. Analise o produto principal: o que o cliente precisará DEPOIS de comprar?\n2. Crie um complemento óbvio:\n   → Produto digital: audiograma, planilha, template, sessão extra\n   → Produto físico: kit, versão premium, edição especial\n3. Preço sugerido: R$${(ticket * 0.4).toFixed(0)}–R$${(ticket * 0.6).toFixed(0)} (40–60% do principal)\n4. Apresente como upsell na página de obrigado ou por email 24h após a compra.\n5. Só escale o novo produto após validar no orgânico ou com orçamento mínimo.`,
      porque:`O custo de vender para quem já comprou é quase zero. Um portfólio de 3–4 produtos com upsell/downsell pode dobrar o LTV sem aumentar um centavo em anúncios.`,
      metrica:`LTV atual: ~R$${(conversoes > 0 ? (conversoes * ticket / conversoes).toFixed(0) : ticket)} | Potencial com upsell: R$${(ticket * 1.5).toFixed(0)}`
    });
  }

  if (roas < breakEven && cpa > cpaIdeal * 1.3 && taxaConv > 0.5) {
    recs.push({ badge:"yellow", categoria:"PRODUTO / LTV", titulo:"📉 ROAS Baixo mas Há Conversões — Tentar Downsell",
      acao:`Criar oferta alternativa a R$${(ticket * 0.5).toFixed(0)} (metade do ticket) para recuperar o CPA.`,
      como:`1. Crie uma versão reduzida do produto principal (menos bônus, versão básica).\n2. Apresente como downsell para quem abandona o checkout.\n3. Preço: R$${(ticket * 0.45).toFixed(0)}–R$${(ticket * 0.55).toFixed(0)}\n4. Com o preço menor, o CPA ideal seria R$${(ticket * 0.5 * (margem/100)).toFixed(0)} — verifique se fecha o break-even.\n5. Se o downsell converter bem, use-o como produto de entrada e o principal como upsell.`,
      porque:`Há tráfego qualificado mas o preço pode estar alto. Um downsell recupera receita de quem não compraria pelo valor cheio e ainda alimenta o LTV com oportunidades futuras.`,
      metrica:`ROAS ${roas.toFixed(2)}x | CPA R$${cpa.toFixed(2)} | Conv ${taxaConv.toFixed(2)}%`
    });
  }

  // ════════════════════════════════════════════════════════════════
  // 🟢 OPORTUNIDADES — maximizar o que já funciona
  // ════════════════════════════════════════════════════════════════

  if (hookRate > 3.0 && taxaConv < 1.5 && conversoes >= 2) {
    recs.push({ badge:"green", categoria:"OPORTUNIDADE", titulo:"🧪 Lookalike de Compradores — Público de Alta Qualidade",
      acao:"Criar Custom Audience de compradores e gerar Lookalike 1% para escalar com qualidade.",
      como:"1. Vá em Públicos → Criar Custom Audience → Lista de clientes.\n2. Suba a lista de compradores dos últimos 30–60 dias (mín. 100 pessoas).\n3. Crie Lookalike 1% (mais preciso) e 3% (mais volume) desse audience.\n4. Use o 1% em um novo conjunto com o criativo campeão e orçamento inicial de 20–30% do atual.\n5. Após 3 dias comparando CPAs, escale o vencedor.",
      porque:`Hook rate ${hookRate.toFixed(2)}% excelente — o criativo atrai muito bem. O problema pode ser que o público atual não é o comprador ideal. Lookalike de quem já comprou tem intenção muito maior.`,
      metrica:`Hook ${hookRate.toFixed(2)}% | Conv ${taxaConv.toFixed(2)}%`
    });
  }

  if (roas > breakEven * 1.2 && freq < 2.0 && conversoes >= 3) {
    recs.push({ badge:"green", categoria:"OPORTUNIDADE", titulo:"🔁 Retargeting Sequencial — Reengajar Quem Visitou",
      acao:"Criar campanha de remarketing para visitantes da página que não compraram.",
      como:"1. Crie Custom Audience de pessoas que visitaram sua landing page nos últimos 7 dias mas não compraram.\n2. Crie conjunto de remarketing separado com mensagem diferente:\n   → Foco em: superar objeção principal, mostrar prova social, criar urgência real.\n3. Orçamento: 10–15% do orçamento total.\n4. Não use o mesmo criativo do topo — a pessoa já te conhece.\n5. Exclua compradores do remarketing para não irritar quem já converteu.",
      porque:`Quem já visitou a página tem 3–5x mais chance de comprar que tráfego frio. Remarketing sequencial bem feito pode ter CPA 40–60% menor que a campanha principal.`,
      metrica:`ROAS ${roas.toFixed(2)}x | Freq ${freq.toFixed(1)}x | ${conversoes} conv`
    });
  }

  // Badge colors
  const BC={green:"#00ffaa",yellow:"#ffd060",red:"#ff3366"};
  const BB={green:"#00ffaa10",yellow:"#ffd06010",red:"#ff336610"};
  const hasIaDetail = aiAcoes?.some(a => a.como_fazer);

  // Agrupar por categoria
  const cats = [...new Set(recs.map(r => r.categoria))];

  const catIcon = {
    "ESCALA":"📈", "RECUPERAÇÃO":"🔧", "DELETAR / PAUSAR":"⛔",
    "PRODUTO / LTV":"📦", "OPORTUNIDADE":"⚡"
  };
  const catColor = {
    "ESCALA":"#00ffaa", "RECUPERAÇÃO":"#ffd060", "DELETAR / PAUSAR":"#ff3366",
    "PRODUTO / LTV":"#a855f7", "OPORTUNIDADE":"#44aaff"
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {hasIaDetail && (
        <div style={{background:"#a855f710",border:"1px solid #a855f730",borderRadius:9,padding:"10px 14px",fontSize:"0.75em",fontFamily:"Space Mono",color:"#c8a0f0"}}>
          🤖 IA ativa — análise complementar disponível na aba 🤖 IA
        </div>
      )}
      {recs.length === 0 ? (
        <div style={{background:"#00ffaa10",border:"1px solid #00ffaa30",borderRadius:10,padding:"16px 18px",color:"#60ffcc",fontWeight:700,fontSize:"0.88em"}}>
          ✅ Métricas dentro do esperado. Nenhuma ação urgente detectada. Continue monitorando.
        </div>
      ) : (
        cats.map(cat => (
          <div key={cat}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${catColor[cat]}33`}}>
              <span style={{fontSize:"1em"}}>{catIcon[cat]}</span>
              <span style={{fontSize:"0.62em",fontFamily:"Space Mono",fontWeight:700,textTransform:"uppercase",letterSpacing:"1.5px",color:catColor[cat]}}>{cat}</span>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {recs.filter(r=>r.categoria===cat).map((r,i) => (
                <div key={i}
                  style={{background:BB[r.badge],borderRadius:12,padding:"16px 18px",border:`1px solid ${BC[r.badge]}33`,transition:"transform 0.15s,box-shadow 0.15s",cursor:"default"}}
                  onMouseEnter={e=>{e.currentTarget.style.transform="translateX(4px)";e.currentTarget.style.boxShadow=`-3px 0 0 ${BC[r.badge]}`}}
                  onMouseLeave={e=>{e.currentTarget.style.transform="translateX(0)";e.currentTarget.style.boxShadow="none"}}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,marginBottom:14,flexWrap:"wrap"}}>
                    <span style={{fontSize:"0.95em",fontWeight:800,lineHeight:1.3}}>{r.titulo}</span>
                    <div style={{display:"flex",gap:6,flexShrink:0}}>
                      <span style={{fontSize:"0.58em",fontFamily:"Space Mono",padding:"3px 9px",borderRadius:100,background:BB[r.badge],color:BC[r.badge],border:`1px solid ${BC[r.badge]}44`,fontWeight:700}}>
                        {r.badge==="red"?"🔴 URGENTE":r.badge==="yellow"?"🟡 ATENÇÃO":"🟢 OPORTUNIDADE"}
                      </span>
                      <span style={{fontSize:"0.58em",fontFamily:"Space Mono",padding:"3px 9px",borderRadius:100,background:"var(--surface3)",color:"var(--muted)",border:"1px solid var(--border)"}}>
                        {r.metrica}
                      </span>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
                    <div style={{flex:"1 1 220px"}}>
                      <div style={{fontSize:"0.6em",color:BC[r.badge],fontFamily:"Space Mono",textTransform:"uppercase",letterSpacing:"1px",marginBottom:5}}>🎯 Ação Imediata</div>
                      <div style={{fontWeight:700,fontSize:"0.85em",marginBottom:14,lineHeight:1.4,color:"var(--text)"}}>{r.acao}</div>
                      <div style={{fontSize:"0.6em",color:"var(--muted)",fontFamily:"Space Mono",textTransform:"uppercase",letterSpacing:"1px",marginBottom:6}}>🔧 Como Fazer — Passo a Passo</div>
                      <div style={{fontSize:"0.78em",lineHeight:1.9,color:"#c8c8e8",whiteSpace:"pre-line"}}>{r.como}</div>
                    </div>
                    <div style={{flex:"0 1 200px",background:"var(--surface)",borderRadius:9,padding:"13px 15px",alignSelf:"flex-start",minWidth:160}}>
                      <div style={{fontSize:"0.6em",color:"var(--muted)",fontFamily:"Space Mono",textTransform:"uppercase",letterSpacing:"1px",marginBottom:7}}>💡 Por que funciona</div>
                      <div style={{fontSize:"0.78em",lineHeight:1.8,color:"#a0a0c8"}}>{r.porque}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
export default function App() {
  const [tab,setTab]=useState("dashboard");

  const [ticket,setTicket]=useState(70);
  const [orcamento,setOrcamento]=useState(100);
  const [margem,setMargem]=useState(40);
  const [objetivo,setObjetivo]=useState("Conversão");
  const [plataforma,setPlataforma]=useState("Meta Ads");
  const [nicho,setNicho]=useState("");

  const [cpc,setCpc]=useState(0.30);
  const [ctr,setCtr]=useState(2.0);
  const [cpm,setCpm]=useState(8.0);
  const [impressoes,setImpressoes]=useState(10000);
  const [cpa,setCpa]=useState(25.0);
  const [conversoes,setConversoes]=useState(4);
  const [roas,setRoas]=useState(2.5);
  const [freq,setFreq]=useState(1.8);
  const [cliques,setCliques]=useState(200);
  const [alcance,setAlcance]=useState(8000);
  const [gasto,setGasto]=useState(100.0);
  const [checkouts,setCheckouts]=useState(8);

  const BP=3.99;
  const [bumps,setBumps]=useState([
    {id:1,nome:"Áudios de Reprogramação",vendas:12,ativo:true},
    {id:2,nome:"Código Secreto da Atração",vendas:8,ativo:true},
    {id:3,nome:"Manual dos Anjos",vendas:5,ativo:true},
    {id:4,nome:"Oração dos Milagres",vendas:9,ativo:true},
    {id:5,nome:"Protocolo 21 Dias",vendas:3,ativo:false},
  ]);
  const maxBV=Math.max(...bumps.map(b=>b.vendas));
  const totalBR=bumps.reduce((s,b)=>s+b.vendas*BP,0);
  const bestBump=bumps.reduce((a,b)=>b.vendas>a.vendas?b:a);

  const [webhookUrl,setWebhookUrl]=useState("");
  const [makeStatus,setMakeStatus]=useState("idle");
  const [makeData,setMakeData]=useState(null);
  const [makeLastSync,setMakeLastSync]=useState(null);
  const [autoSync,setAutoSync]=useState(false);
  const syncRef=useRef(null);

  // ── MODO PRO: conexão direta com VPS Hostinger ──────────────────────────
  const [modoPro,setModoPro]=useState(false);
  const [apiUrl,setApiUrl]=useState("");
  const [apiProduto,setApiProduto]=useState("");
  const [apiStatus,setApiStatus]=useState("idle");
  const [apiLastSync,setApiLastSync]=useState(null);
  const proSyncRef=useRef(null);

  const [contexto,setContexto]=useState("");

  // ── Criativos v2 ─────────────────────────────────────────────────────────
  const genHist7=(baseCTR,baseCPC)=>["Seg","Ter","Qua","Qui","Sex","Sáb","Dom"].map((d,i)=>({
    dia:d,ctr:+(baseCTR*(0.8+Math.random()*0.4)).toFixed(2),cpc:+(baseCPC*(0.85+Math.random()*0.3)).toFixed(2)
  }));
  const [criativos,setCriativos]=useState([
    {id:"cr1",nome:"VSL Principal v3",campanha:"c1",formato:"video",status:"ativo",thumb:null,
     tags:["topo de funil","ugc","vídeo"],
     copies:["Você está cansado de...?","O método que ninguém te contou"],
     briefing:{cores:["#7c3aed","#c026d3"],referencia:"Tom direto, urgência",formato:"9:16 – Stories"},
     metricas:{impressoes:48200,cliques:1930,ctr:4.0,cpc:1.85,conversoes:97,cpa:36.8,gasto:3563,receita:6790},
     hist7:genHist7(4.0,1.85),favorito:false},
    {id:"cr2",nome:"Carrossel Benefícios",campanha:"c1",formato:"carrossel",status:"ativo",thumb:null,
     tags:["remarketing","carrossel"],
     copies:["5 razões para você mudar hoje"],
     briefing:{cores:["#44aaff","#080810"],referencia:"Educativo, suave",formato:"1:1 – Feed"},
     metricas:{impressoes:31000,cliques:680,ctr:2.19,cpc:3.12,conversoes:28,cpa:75.7,gasto:2121,receita:1960},
     hist7:genHist7(2.19,3.12),favorito:false},
    {id:"cr3",nome:"Imagem Prova Social",campanha:"c2",formato:"imagem",status:"pausado",thumb:null,
     tags:["topo de funil","imagem"],
     copies:["'Eu nunca imaginei que funcionaria tão rápido'"],
     briefing:{cores:["#00ffaa","#080810"],referencia:"Depoimento real, screenshot",formato:"4:5 – Feed"},
     metricas:{impressoes:19400,cliques:291,ctr:1.5,cpc:5.44,conversoes:9,cpa:176.0,gasto:1584,receita:630},
     hist7:genHist7(1.5,5.44),favorito:true},
  ]);
  const [criativoModal,setCriativoModal]=useState(null);  // null | "novo" | obj | {mode:"detalhe",cr}
  const [criativoView,setCriativoView]=useState("cards");
  const [criativoFiltroStatus,setCriativoFiltroStatus]=useState("todos");
  const [criativoCampanha,setCriativoCampanha]=useState("todas");
  const [criativoSearch,setCriativoSearch]=useState("");
  const [criativoSortBy,setCriativoSortBy]=useState("ctr");
  const [criativoFiltroTag,setCriativoFiltroTag]=useState("todas");
  const [criativoLoadingApi,setCriativoLoadingApi]=useState(false);

  const CAMPANHAS_MOCK=[
    {id:"c1",name:"Produto Principal – Conversão"},
    {id:"c2",name:"Tráfego Frio – Públicos Novos"},
    {id:"c3",name:"Retargeting – Abandono"},
  ];
  const STATUS_CR={
    ativo:{label:"Ativo",color:"#00ffaa"},
    pausado:{label:"Pausado",color:"#ffd060"},
    rascunho:{label:"Rascunho",color:"#7070a0"},
  };
  const ALL_TAGS=["topo de funil","remarketing","ugc","vídeo","imagem","carrossel","promoção","objeção"];

  // ── API stubs (substitua a URL pela sua VPS quando o backend estiver pronto) ──
  const API_BASE = apiUrl || "https://api.seusite.com"; // vem do campo Modo Pro
  async function apiGetCriativos(){
    /* BACKEND PRONTO: descomente e remova o return abaixo
    setCriativoLoadingApi(true);
    try{
      const r=await fetch(`${API_BASE}/api/criativos`);
      const d=await r.json();
      setCriativos(d);
    }catch(e){console.error("Erro GET criativos",e);}
    finally{setCriativoLoadingApi(false);}
    */
    return; // modo demo: usa o state local
  }
  async function apiSaveCriativo(form){
    /* BACKEND PRONTO: descomente
    const method=form.id?"PUT":"POST";
    const url=form.id?`${API_BASE}/api/criativos/${form.id}`:`${API_BASE}/api/criativos`;
    await fetch(url,{method,headers:{"Content-Type":"application/json"},body:JSON.stringify(form)});
    */
    if(!form.id) setCriativos(p=>[...p,{...form,id:"cr"+Date.now(),hist7:genHist7(form.metricas.ctr||0,form.metricas.cpc||0)}]);
    else setCriativos(p=>p.map(c=>c.id===form.id?{...form,hist7:c.hist7}:c));
    setCriativoModal(null);
  }
  async function apiDeleteCriativo(id){
    /* BACKEND PRONTO: await fetch(`${API_BASE}/api/criativos/${id}`,{method:"DELETE"}); */
    setCriativos(p=>p.filter(c=>c.id!==id));
  }
  function saveCriativo(form){ apiSaveCriativo(form); }
  function duplicarCriativo(cr){
    const copia={...JSON.parse(JSON.stringify(cr)),id:"cr"+Date.now(),nome:cr.nome+" (cópia)",status:"rascunho",favorito:false};
    setCriativos(p=>[...p,copia]);
  }
  function toggleFavCriativo(id){
    setCriativos(p=>p.map(c=>c.id===id?{...c,favorito:!c.favorito}:c));
  }

  // ── Espionagem ────────────────────────────────────────────────────────────
  const [espSearch,setEspSearch]=useState("");
  const [espPlataforma,setEspPlataforma]=useState("Meta");
  const [espPais,setEspPais]=useState("BR");
  const [espMidia,setEspMidia]=useState("todos");
  const [espData,setEspData]=useState("30");
  const [espLoading,setEspLoading]=useState(false);
  const [espResultados,setEspResultados]=useState([]);
  const [espModal,setEspModal]=useState(null);
  const [espFavs,setEspFavs]=useState([]);
  const [espApiKey,setEspApiKey]=useState("");
  const [espAba,setEspAba]=useState("busca"); // "busca" | "favoritos"

  // Mock de anúncios para demo (substitua por chamada real à Meta Ad Library API)
  const ESP_MOCK=[
    {id:"ad1",anunciante:"Método Financeiro Pro",thumb:null,formato:"video",plataforma:"Meta",
     copy:"Você sabia que 97% das pessoas perdem dinheiro por não conhecer esse método?",
     cta:"Saiba Mais",inicio:"2024-01-15",pais:"BR",adId:"458291847362",
     stats:{likes:2400,comments:312,shares:89},lp:"https://metodopro.com.br"},
    {id:"ad2",anunciante:"Curso Tráfego Elite",thumb:null,formato:"imagem",plataforma:"Meta",
     copy:"De R$0 a R$10k por mês gerenciando tráfego pago. Sem experiência necessária.",
     cta:"Inscreva-se",inicio:"2024-01-20",pais:"BR",adId:"512847362918",
     stats:{likes:1820,comments:204,shares:67},lp:"https://trafegoe.com.br"},
    {id:"ad3",anunciante:"Fisio em Casa",thumb:null,formato:"carrossel",plataforma:"Meta",
     copy:"5 exercícios que eliminam a dor nas costas em 7 dias (comprovado por fisioterapeutas)",
     cta:"Ver Mais",inicio:"2024-01-18",pais:"BR",adId:"672918473625",
     stats:{likes:5600,comments:891,shares:342},lp:"https://fisioemcasa.com"},
    {id:"ad4",anunciante:"Academia Digital",thumb:null,formato:"video",plataforma:"TikTok",
     copy:"POV: você finalmente aprende a investir sem precisar de banco ou corretora",
     cta:"Começar Agora",inicio:"2024-01-22",pais:"BR",adId:"891234567890",
     stats:{likes:12400,comments:1203,shares:987},lp:"https://academiadi.com.br"},
    {id:"ad5",anunciante:"Protocolo Slim",thumb:null,formato:"imagem",plataforma:"Meta",
     copy:"Mulher de 52 anos perde 18kg em 3 meses usando o protocolo que médicos não divulgam",
     cta:"Ver Protocolo",inicio:"2024-01-10",pais:"BR",adId:"345678901234",
     stats:{likes:8900,comments:2100,shares:1456},lp:"https://protocoloslim.com"},
    {id:"ad6",anunciante:"Mentoria Negócios",thumb:null,formato:"video",plataforma:"Meta",
     copy:"Como saí do emprego CLT e hoje faturei R$47.000 em um mês trabalhando de casa",
     cta:"Quero Saber Como",inicio:"2024-01-25",pais:"MX",adId:"901234567890",
     stats:{likes:3200,comments:567,shares:234},lp:"https://mentorianegocios.com"},
  ];

  async function buscarEspionagem(){
    setEspLoading(true);
    setEspResultados([]);
    /* BACKEND PRONTO (Meta Ad Library API):
    try{
      // Requer token de acesso do Facebook e aceitação dos termos da Ad Library API
      const url=`https://graph.facebook.com/v18.0/ads_archive?access_token=${espApiKey}&search_terms=${encodeURIComponent(espSearch)}&ad_reached_countries=${espPais}&media_type=${espMidia==="todos"?"ALL":espMidia.toUpperCase()}&fields=id,ad_creative_body,ad_creative_link_url,page_name,ad_snapshot_url,ad_delivery_start_time&limit=20`;
      const r=await fetch(url);
      const d=await r.json();
      setEspResultados(d.data||[]);
    }catch(e){console.error("Meta Ad Library error",e);}
    */
    // Demo: filtra o mock
    await new Promise(r=>setTimeout(r,900));
    let res=ESP_MOCK.filter(a=>{
      if(espPlataforma!=="Todos"&&a.plataforma!==espPlataforma)return false;
      if(espMidia!=="todos"&&a.formato!==espMidia)return false;
      if(espPais!=="Todos"&&a.pais!==espPais)return false;
      if(espSearch&&!a.copy.toLowerCase().includes(espSearch.toLowerCase())&&!a.anunciante.toLowerCase().includes(espSearch.toLowerCase()))return false;
      return true;
    });
    setEspResultados(res);
    setEspLoading(false);
  }
  function toggleEspFav(ad){
    setEspFavs(p=>p.find(f=>f.id===ad.id)?p.filter(f=>f.id!==ad.id):[...p,ad]);
  }
  const [diasRodando,setDiasRodando]=useState("");
  const [tipoCriativo,setTipoCriativo]=useState("UGC");
  const [tipoPublico,setTipoPublico]=useState("Lookalike 1%");
  const [loading,setLoading]=useState(false);
  const [resultado,setResultado]=useState(null);
  const [aiParsed,setAiParsed]=useState(null);
  const [historico,setHistorico]=useState([]);
  const [expandedHist,setExpandedHist]=useState(null);

  const cpaIdeal=ticket*0.3;
  const lpv=ticket*(margem/100);
  const breakEven=lpv>0?ticket/lpv:0;
  const vendasBreak=cpaIdeal>0?orcamento/cpaIdeal:0;
  const receita=conversoes*ticket;
  const lucro=conversoes*lpv-gasto;
  const lucroB=lucro+totalBR;
  const cpl=cliques>0?gasto/cliques:0;
  const taxaConv=cliques>0?(conversoes/cliques)*100:0;
  const cpmReal=impressoes>0?(gasto/impressoes)*1000:0;
  const hookRate=impressoes>0?(cliques/impressoes)*100:0;
  const efCpa=cpa>0?(cpaIdeal/cpa)*100:0;
  const roi=gasto>0?((receita-gasto)/gasto)*100:0;
  const roiB=gasto>0?(((receita+totalBR)-gasto)/gasto)*100:0;
  const projMensal=lucro*30;
  const projBumps=lucroB*30;
  const [chartData,setChartData]=useState(()=>genWeek(gasto,conversoes,ticket));

  const alerts=[];
  if(cpa>cpaIdeal*1.5)              alerts.push({type:"danger", msg:`CPA R$${cpa.toFixed(2)} — ${(((cpa/cpaIdeal)-1)*100).toFixed(0)}% acima do ideal`});
  if(roas<breakEven)                alerts.push({type:"danger", msg:`ROAS ${roas.toFixed(2)}x abaixo do break-even (${breakEven.toFixed(2)}x)`});
  if(freq>3.5)                      alerts.push({type:"warning",msg:`Frequência ${freq.toFixed(1)}x — público saturando`});
  if(ctr<1.0)                       alerts.push({type:"warning",msg:`CTR ${ctr.toFixed(2)}% baixo — criativo fraco`});
  if(taxaConv<0.5)                  alerts.push({type:"warning",msg:`Taxa conv. ${taxaConv.toFixed(2)}% — gargalo no funil`});
  if(lucro>0&&roas>=breakEven*1.3)  alerts.push({type:"success",msg:`Lucrativo ROAS ${roas.toFixed(2)}x — considere escalar`});
  if(hookRate>3)                    alerts.push({type:"success",msg:`Hook rate ${hookRate.toFixed(2)}% excelente`});

  // 🛡️ ANTI-BURRO: Smart Actions fixas como BACKUP quando API cai
  const fixedActions=[];
  if(ctr<1.0)          fixedActions.push({area:"Criativo",    priority:"alta", action:"CTR < 1% — crie 3 novos ganchos (primeiros 3s)",sub:"Ninguém para para ver. Problema de criativo."});
  if(cpa>cpaIdeal*1.3) fixedActions.push({area:"Lances",      priority:"alta", action:`CPA alto — ative lance com teto R$${cpaIdeal.toFixed(2)}`,sub:"Limite o custo por conversão."});
  if(taxaConv<1.0)     fixedActions.push({area:"Landing Page",priority:"alta", action:"Conv. baixa — revise headline e velocidade (Hostinger)",sub:"CTR alto + conv. baixa = problema na página."});
  if(freq>3.0)         fixedActions.push({area:"Criativo",    priority:"media",action:`Freq ${freq.toFixed(1)}x — suba novo criativo preventivamente`,sub:"Regra: novo criativo a cada 7 dias."});
  if(roas>=breakEven*1.3&&freq<2.5) fixedActions.push({area:"Escala",priority:"baixa",action:`ROAS ${roas.toFixed(2)}x — aumente orçamento +20%`,sub:"Escale +20% a cada 48h. Nunca dobre."});
  if(hookRate<1.0)     fixedActions.push({area:"Criativo",    priority:"alta", action:"Hook rate < 1% — reescreva o gancho de abertura",sub:"Se não para, o resto não importa."});

  const smartActions=aiParsed?.acoes||fixedActions;
  const smartSrc=aiParsed?.acoes?"ia":"regra";

  async function analisar() {
    setLoading(true);setResultado(null);setAiParsed(null);
    const dados={
      plataforma,objetivo,nicho,ticket,orcamento,margem,
      cpa_ideal:+cpaIdeal.toFixed(2),break_even:+breakEven.toFixed(2),
      metricas:{cpc,ctr,cpm,impressoes,cliques,alcance,freq,cpa_real:cpa,conversoes,roas,gasto,checkouts},
      derivadas:{receita:+receita.toFixed(2),lucro:+lucro.toFixed(2),lucro_com_bumps:+lucroB.toFixed(2),taxaConv:+taxaConv.toFixed(2),roi:+roi.toFixed(1),roiB:+roiB.toFixed(1),hookRate:+hookRate.toFixed(2),efCpa:+efCpa.toFixed(1)},
      bumps:{total_receita:+totalBR.toFixed(2),melhor:bestBump.nome,items:bumps.map(b=>({nome:b.nome,vendas:b.vendas,receita:+(b.vendas*BP).toFixed(2)}))},
      alertas:alerts.map(a=>a.msg),
      ctx:{dias:diasRodando,criativo:tipoCriativo,publico:tipoPublico,extra:contexto}
    };

    const prompt=`Aja como Diretor de Tráfego Pago Sênior especialista em ${plataforma} e LATAM.
REGRA: Se Hook Rate baixo → foque criativos. Se Taxa Conv baixa → foque Landing Page. Se ROAS > break-even 30% por 3 dias → escale +20% a cada 48h.

Analise os dados e retorne PRIMEIRO um bloco \`\`\`json ... \`\`\` com ações estruturadas, DEPOIS o diagnóstico completo.

Dados: ${JSON.stringify(dados)}

JSON obrigatório:
{
  "veredito": "ESCALAR|PAUSAR|OTIMIZAR",
  "score": 0-100,
  "acoes": [
    {
      "area": "string",
      "priority": "alta|media|baixa",
      "action": "string",
      "sub": "string",
      "como_fazer": "passo a passo numerado para executar a ação",
      "porque": "explicação do motivo com base nos dados analisados"
    }
  ],
  "projecao_7d": {"gasto":0,"receita":0,"lucro":0,"conversoes":0},
  "risco_saturacao": "baixo|medio|alto",
  "bump_insight": "qual bump priorizar e por quê"
}

Depois responda:
## 1. 📊 A CAMPANHA ESTÁ PERFORMANDO BEM?
## 2. 📈 DEVO AUMENTAR O ORÇAMENTO?
## 3. ⏸️ DEVO REDUZIR OU PAUSAR?
## 4. ⚡ AJUSTES IMEDIATOS — 24-48H
## 5. 🔥 RISCO DE SATURAÇÃO?
## 6. 📅 PROJEÇÃO 7 DIAS
## 🎨 LABORATÓRIO DE CRIATIVOS
## 🧠 INSIGHT DO CONSELHEIRO`;

    try {
      const res=await fetch("https://api.anthropic.com/v1/messages",{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:2000,messages:[{role:"user",content:prompt}]})
      });
      const data=await res.json();
      const text=data.content?.map(b=>b.text||"").join("")||"Erro.";
      const parsed=parseAIResponse(text);
      if(parsed?.acoes) setAiParsed(parsed);
      const clean=text.replace(/```json[\s\S]*?```/,"").trim();
      setResultado(clean);
      setHistorico(p=>[{id:Date.now(),hora:new Date().toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"}),plataforma,cpa,roas,roi,lucro,resultado:clean,parsed},...p]);
    } catch(e){setResultado("Erro ao conectar. Tente novamente.");}
    setLoading(false);
  }


  // ── Aplica dados recebidos (Make ou VPS) nos estados do dashboard ──────────
  function applyData(d){
    if(d.conta){
      const c=d.conta;
      if(c.gasto      != null) setGasto(+c.gasto);
      if(c.impressoes != null) setImpressoes(+c.impressoes);
      if(c.cliques    != null) setCliques(+c.cliques);
      if(c.ctr        != null) setCtr(+c.ctr);
      if(c.cpc        != null) setCpc(+c.cpc);
      if(c.cpm        != null) setCpm(+c.cpm);
      if(c.alcance    != null) setAlcance(+c.alcance);
      if(c.frequencia != null) setFreq(+c.frequencia);
      if(c.conversoes != null) setConversoes(+c.conversoes);
      if(c.roas       != null) setRoas(+c.roas);
      if(c.checkouts  != null) setCheckouts(+c.checkouts);
      if(c.gasto && c.conversoes && +c.conversoes > 0)
        setCpa(+(+c.gasto / +c.conversoes).toFixed(2));
    }
    // Aceita histórico semanal direto da VPS
    if(Array.isArray(d.historico) && d.historico.length > 0)
      setChartData(d.historico);
    // Aceita bumps da VPS
    if(Array.isArray(d.bumps) && d.bumps.length > 0)
      setBumps(d.bumps);
    // Aceita config do produto
    if(d.config){
      if(d.config.ticket  != null) setTicket(+d.config.ticket);
      if(d.config.margem  != null) setMargem(+d.config.margem);
      if(d.config.nicho)           setNicho(d.config.nicho);
    }
  }
  // mantém alias para compatibilidade
  function applyMake(d){ setMakeData(d); applyData(d); }

  // ── Make webhook (GET de URL configurada) ───────────────────────────────
  async function testWebhook(){
    if(!webhookUrl.trim()) return;
    setMakeStatus("testing");
    try{
      const r=await fetch(webhookUrl);
      const d=await r.json();
      applyMake(d);
      setMakeStatus("connected");
      setMakeLastSync(new Date());
    } catch(e){ setMakeStatus("error"); }
  }

  // ── VPS API (Modo Pro) ──────────────────────────────────────────────────
  async function fetchFromVPS(){
    if(!apiUrl.trim()) return;
    setApiStatus("fetching");
    const qs = apiProduto ? `?produto=${encodeURIComponent(apiProduto)}` : "";
    try{
      const r=await fetch(`${apiUrl.replace(/\/$/,"")}/get-metrics${qs}`);
      if(!r.ok) throw new Error("HTTP "+r.status);
      const d=await r.json();
      applyData(d);
      setApiStatus("ok");
      setApiLastSync(new Date());
    } catch(e){ setApiStatus("error"); }
  }

  // ── Auto-sync Make (a cada 5 min quando conectado) ──────────────────────
  useEffect(()=>{
    if(autoSync && makeStatus==="connected" && webhookUrl)
      syncRef.current=setInterval(testWebhook, 5*60*1000);
    else clearInterval(syncRef.current);
    return()=>clearInterval(syncRef.current);
  },[autoSync,makeStatus,webhookUrl]);

  // ── Auto-sync VPS Modo Pro (a cada 2 min quando ativo) ──────────────────
  useEffect(()=>{
    if(modoPro && apiStatus==="ok" && apiUrl){
      proSyncRef.current=setInterval(fetchFromVPS, 2*60*1000);
    } else clearInterval(proSyncRef.current);
    return()=>clearInterval(proSyncRef.current);
  },[modoPro,apiStatus,apiUrl]);

  // ── Quando Modo Pro é ativado, tenta fetch imediato ─────────────────────
  useEffect(()=>{
    if(modoPro && apiUrl) fetchFromVPS();
    if(!modoPro) setApiStatus("idle");
  },[modoPro]);

  // ── Demo: regenera chart quando métricas mudam (só no modo demo) ─────────
  useEffect(()=>{
    if(!modoPro && apiStatus!=="ok")
      setChartData(genWeek(gasto,conversoes,ticket));
  },[gasto,conversoes,ticket,modoPro]);

  const gc=(ok,w)=>ok?"green":w?"yellow":"red";

  const TABS=[
    {id:"dashboard",  label:"📊 Dashboard"},
    {id:"bumps",      label:"💰 Order Bumps"},
    {id:"decisao",    label:"🧠 Decisão Sênior"},
    {id:"plano",      label:"📋 Plano de Ação"},
    {id:"analytics",  label:"📈 Analytics"},
    {id:"latam",      label:"🌎 LATAM"},
    {id:"ia",         label:"🤖 IA"},
    {id:"guardian",   label:"🛡️ Guardião"},
    {id:"make",       label:"🔗 Make"},
    {id:"criativos",  label:"🎨 Criativos"},
    {id:"espionagem", label:"🕵️ Espionagem"},
    {id:"glossario",  label:"📚 Glossário"},
  ];

  return (
    <>
      <style>{FONTS}{css}</style>
      <div className="app">

        {/* HEADER */}
        <div className="header">
          <div className="header-left">
            <div className="logo-icon">🚀</div>
            <div>
              <div className="header-title">IA Media Buyer</div>
              <div className="header-sub">CÓDIGO DIVINO · LATAM · {plataforma}</div>
            </div>
          </div>
          <div style={{display:"flex",gap:10,alignItems:"center"}}>
            {/* ── Modo Demo / Pro toggle ── */}
            <div style={{display:"flex",alignItems:"center",gap:8,background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:100,padding:"4px 12px"}}>
              <span style={{fontSize:"0.6em",fontFamily:"Space Mono",color:!modoPro?"var(--yellow)":"var(--muted)",fontWeight:!modoPro?700:400}}>DEMO</span>
              <div onClick={()=>setModoPro(p=>!p)} style={{width:34,height:18,borderRadius:9,background:modoPro?"var(--accent)":"var(--surface3)",cursor:"pointer",position:"relative",border:"1px solid var(--border-bright)",transition:"background 0.2s"}}>
                <div style={{position:"absolute",top:3,left:modoPro?17:3,width:11,height:11,borderRadius:"50%",background:modoPro?"white":"#7070a0",transition:"left 0.2s"}}/>
              </div>
              <span style={{fontSize:"0.6em",fontFamily:"Space Mono",color:modoPro?"var(--accent)":"var(--muted)",fontWeight:modoPro?700:400}}>PRO</span>
            </div>
            {/* ── Status badges ── */}
            {modoPro && apiStatus==="ok" && <span style={{fontSize:"0.62em",fontFamily:"Space Mono",color:"var(--accent)",background:"#7c3aed14",border:"1px solid #7c3aed30",padding:"3px 10px",borderRadius:100}}>⚡ VPS LIVE</span>}
            {modoPro && apiStatus==="fetching" && <span style={{fontSize:"0.62em",fontFamily:"Space Mono",color:"var(--yellow)",background:"#ffd06010",border:"1px solid #ffd06030",padding:"3px 10px",borderRadius:100}}>⏳ Sincronizando</span>}
            {modoPro && apiStatus==="error" && <span style={{fontSize:"0.62em",fontFamily:"Space Mono",color:"var(--red)",background:"#ff336610",border:"1px solid #ff336630",padding:"3px 10px",borderRadius:100}}>🔴 VPS Erro</span>}
            {!modoPro && makeStatus==="connected" && <span style={{fontSize:"0.62em",fontFamily:"Space Mono",color:"var(--green)",background:"#00ffaa12",border:"1px solid #00ffaa25",padding:"3px 10px",borderRadius:100}}>🔗 Make LIVE</span>}
            {aiParsed&&<span style={{fontSize:"0.62em",fontFamily:"Space Mono",color:"var(--purple)",background:"#a855f712",border:"1px solid #a855f725",padding:"3px 10px",borderRadius:100}}>🤖 IA Ativa</span>}
            <div className="live-badge"><div className="live-dot"/>{modoPro?"PRO":"DEMO"}</div>
          </div>
        </div>

        <div className="tabs">{TABS.map(t=><button key={t.id} className={`tab ${tab===t.id?"active":""}`} onClick={()=>setTab(t.id)}>{t.label}</button>)}</div>

        {/* ═══ DASHBOARD ═══ */}
        {tab==="dashboard"&&(
          <div className="main-grid">
            <div className="sidebar">
              <div className="card">
                <div className="card-title">⚙ Configuração</div>
                {[["Ticket Médio (R$)",ticket,setTicket],["Orçamento Diário (R$)",orcamento,setOrcamento]].map(([l,v,s])=>(
                  <div className="field" key={l}><label>{l}</label><input type="number" value={v} onChange={e=>s(+e.target.value)}/></div>
                ))}
                <div className="field"><div className="slider-row"><span>Margem</span><span style={{color:"var(--green)"}}>{margem}%</span></div><input type="range" min={10} max={90} value={margem} onChange={e=>setMargem(+e.target.value)}/></div>
                <div className="field"><label>Objetivo</label><select value={objetivo} onChange={e=>setObjetivo(e.target.value)}>{["Conversão","Leads","Reconhecimento","Tráfego"].map(o=><option key={o}>{o}</option>)}</select></div>
                <div className="field"><label>Plataforma</label><select value={plataforma} onChange={e=>setPlataforma(e.target.value)}>{["Meta Ads","Google Ads","TikTok Ads","Taboola","Kwai"].map(p=><option key={p}>{p}</option>)}</select></div>
                <div className="field"><label>Nicho</label><input type="text" placeholder="Código Divino..." value={nicho} onChange={e=>setNicho(e.target.value)}/></div>
              </div>
              <div className="card">
                <div className="card-title">📐 Benchmarks</div>
                {[["CPA Ideal",`R$${cpaIdeal.toFixed(2)}`],["Lucro/Venda",`R$${lpv.toFixed(2)}`],["ROAS Break-even",`${breakEven.toFixed(2)}x`],["Vendas/Break",`${vendasBreak.toFixed(1)}/dia`],["Receita c/Bumps",`R$${(receita+totalBR).toFixed(0)}`],["ROI c/Bumps",`${roiB.toFixed(0)}%`]].map(([l,v])=>(
                  <div className="bmark-row" key={l}><span className="bmark-label">{l}</span><span className="bmark-value">{v}</span></div>
                ))}
              </div>
            </div>
            <div className="right-panel">
              <div className="card">
                <div className="card-title">📊 Dados da Campanha</div>
                <div className="inputs-grid">
                  {[["CPC(R$)",cpc,setCpc,"0.01"],["CTR(%)",ctr,setCtr,"0.01"],["CPM(R$)",cpm,setCpm,"0.01"],["Impressões",impressoes,setImpressoes,"100"],["CPA(R$)",cpa,setCpa,"0.01"],["Conversões",conversoes,setConversoes,"1"],["ROAS",roas,setRoas,"0.01"],["Frequência",freq,setFreq,"0.1"],["Cliques",cliques,setCliques,"1"],["Alcance",alcance,setAlcance,"100"],["Gasto(R$)",gasto,setGasto,"0.01"],["Checkouts",checkouts,setCheckouts,"1"]].map(([l,v,s,st])=>(
                    <div className="field" style={{margin:0}} key={l}><label>{l}</label><input type="number" value={v} step={st} onChange={e=>s(+e.target.value)}/></div>
                  ))}
                </div>
              </div>
              <div className="card">
                <div className="card-title">🔬 Métricas Derivadas</div>
                <div className="kpi-grid">
                  {[["Receita",`R$${receita.toFixed(0)}`,lucro>0?"green":"red"],["Lucro",`R$${lucro.toFixed(0)}`,lucro>0?"green":"red"],["ROI",`${roi.toFixed(0)}%`,gc(roi>0,roi>-20)],["ROI+Bumps",`${roiB.toFixed(0)}%`,gc(roiB>0,roiB>-10)],["Taxa Conv.",`${taxaConv.toFixed(2)}%`,gc(taxaConv>=1,taxaConv>=0.5)],["Efic.CPA",`${efCpa.toFixed(0)}%`,gc(efCpa>=100,efCpa>=70)],["Hook Rate",`${hookRate.toFixed(2)}%`,gc(hookRate>=3,hookRate>=1)],["CPL",`R$${cpl.toFixed(2)}`,"white"],["CPA Real",`R$${cpa.toFixed(2)}`,gc(cpa<=cpaIdeal,cpa<=cpaIdeal*1.3)],["ROAS",`${roas.toFixed(2)}x`,gc(roas>=breakEven*1.2,roas>=breakEven)],["Proj.Mensal",`R$${projMensal.toFixed(0)}`,projMensal>0?"green":"red"],["Proj.+Bumps",`R$${projBumps.toFixed(0)}`,projBumps>0?"green":"red"]].map(([l,v,c])=>(
                    <div className={`kpi-box ${c}`} key={l}><div className={`kpi-glow ${c}`}/><div className="kpi-label">{l}</div><div className={`kpi-value ${c}`}>{v}</div></div>
                  ))}
                </div>
              </div>
              <div className="card">
                <div className="card-title">🚨 Alertas</div>
                <div className="alerts-list">
                  {alerts.length===0?<div className="alert success">✅ Sem alertas críticos.</div>:alerts.map((a,i)=><div key={i} className={`alert ${a.type}`}>{a.type==="danger"?"🔴":a.type==="warning"?"⚠️":"✅"} {a.msg}</div>)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ ORDER BUMPS ═══ */}
        {tab==="bumps"&&(
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
              {[["Receita Total Bumps",`R$ ${totalBR.toFixed(2)}`,"green"],["🏆 Melhor Bump",bestBump.nome.split(" ").slice(0,3).join(" "),"yellow"],["ROI sem Bumps",`${roi.toFixed(0)}%`,roi>0?"green":"red"],["ROI com Bumps",`${roiB.toFixed(0)}%`,roiB>roi?"green":"yellow"]].map(([l,v,c])=>(
                <div className={`kpi-box ${c}`} key={l} style={{padding:14}}><div className={`kpi-glow ${c}`}/><div className="kpi-label">{l}</div><div className={`kpi-value ${c}`} style={{fontSize:"1.05em"}}>{v}</div></div>
              ))}
            </div>
            <div className="card">
              <div className="card-title">💰 Performance dos 5 Order Bumps — R$ {BP} cada</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10,marginBottom:14}}>
                {bumps.map(b=>(
                  <div key={b.id} className={`bump-card ${b.id===bestBump.id?"winner":""}`} onClick={()=>setBumps(p=>p.map(x=>x.id===b.id?{...x,ativo:!x.ativo}:x))}>
                    {b.id===bestBump.id&&<span style={{position:"absolute",top:7,right:7,fontSize:"0.58em",fontFamily:"Space Mono",color:"var(--yellow)",background:"#ffd06022",padding:"1px 7px",borderRadius:100}}>🏆 TOP</span>}
                    {!b.ativo&&<span style={{position:"absolute",top:7,right:7,fontSize:"0.58em",fontFamily:"Space Mono",color:"var(--muted)",background:"var(--surface3)",padding:"1px 7px",borderRadius:100}}>PAUSADO</span>}
                    <div style={{fontSize:"0.78em",fontWeight:700,marginBottom:3,paddingRight:b.id===bestBump.id?40:0}}>{b.nome}</div>
                    <div style={{fontSize:"0.68em",fontFamily:"Space Mono",color:"var(--muted)",marginBottom:2}}>R$ {BP} · #{b.id}</div>
                    <div className="bump-bar-bg"><div className="bump-bar-fill" style={{width:`${(b.vendas/maxBV)*100}%`,background:b.id===bestBump.id?"linear-gradient(90deg,#ffd060,#ffaa00)":"linear-gradient(90deg,#7c3aed,#a855f7)",opacity:b.ativo?1:0.3}}/></div>
                    <div style={{display:"flex",justifyContent:"space-between"}}>
                      <div><div style={{fontSize:"0.6em",color:"var(--muted)",fontFamily:"Space Mono"}}>Vendas</div><div style={{fontSize:"0.82em",fontWeight:700,fontFamily:"Space Mono",color:b.id===bestBump.id?"var(--yellow)":"var(--text)"}}>{b.vendas}</div></div>
                      <div style={{textAlign:"right"}}><div style={{fontSize:"0.6em",color:"var(--muted)",fontFamily:"Space Mono"}}>Receita</div><div style={{fontSize:"0.82em",fontWeight:700,fontFamily:"Space Mono",color:"var(--green)"}}>R${(b.vendas*BP).toFixed(2)}</div></div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{fontSize:"0.7em",color:"var(--muted)",fontFamily:"Space Mono"}}>Clique para pausar/ativar · Edite as vendas abaixo para simular cenários</div>
            </div>
            <div className="card">
              <div className="card-title">✏️ Simular Vendas por Bump</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10}}>
                {bumps.map(b=><div className="field" style={{margin:0}} key={b.id}><label>{b.nome.split(" ").slice(0,2).join(" ")}</label><input type="number" min={0} value={b.vendas} onChange={e=>setBumps(p=>p.map(x=>x.id===b.id?{...x,vendas:+e.target.value}:x))}/></div>)}
              </div>
            </div>
            <div className="card">
              <div className="card-title">📊 Comparação Visual</div>
              <div style={{height:160}}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bumps.map(b=>({name:b.nome.split(" ")[0],Receita:+(b.vendas*BP).toFixed(2)}))} margin={{top:5,right:10,left:0,bottom:0}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08"/>
                    <XAxis dataKey="name" tick={{fill:"#7070a0",fontSize:10,fontFamily:"Space Mono"}} axisLine={false} tickLine={false}/>
                    <YAxis tick={{fill:"#7070a0",fontSize:10,fontFamily:"Space Mono"}} axisLine={false} tickLine={false} tickFormatter={v=>`R$${v}`}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Bar dataKey="Receita" radius={[6,6,0,0]}>{bumps.map((b,i)=><Cell key={i} fill={b.id===bestBump.id?"#ffd060":"#7c3aed"}/>)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="card" style={{background:"#ffd06008",border:"1px solid #ffd06022"}}>
              <div className="card-title" style={{color:"var(--yellow)"}}>🔥 Tática LTV — Gente Grande</div>
              <div style={{fontSize:"0.78em",lineHeight:1.9,color:"#e8e0b0"}}>
                <strong>"{bestBump.nome}" está vencendo</strong> → crie upsell de R$47-97 logo após a compra com versão premium.<br/>
                <strong>Bumps com poucas vendas?</strong> → mude o nome para algo mais urgente. Ex: "E-book" → "O Código Secreto da Atração Imediata".<br/>
                <strong>Projeção:</strong> Dobrar vendas do bump top = <strong>+R${(bestBump.vendas*BP*30).toFixed(0)}/mês</strong> sem gastar mais em anúncio.
              </div>
            </div>
          </div>
        )}

        {/* ═══ DECISÃO SÊNIOR ═══ */}
        {tab==="decisao"&&(
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div className="two-col">
              <div className="card">
                <div className="card-title">
                  ⚡ Smart Actions
                  <span style={{marginLeft:8,fontSize:"0.75em",padding:"1px 8px",borderRadius:100,fontFamily:"Space Mono",color:smartSrc==="ia"?"var(--purple)":"var(--muted)",background:smartSrc==="ia"?"#a855f720":"var(--surface2)"}}>{smartSrc==="ia"?"🤖 gerado pela IA":"📋 regras fixas"}</span>
                </div>
                {smartActions.length===0?<div className="alert success">✅ Nenhuma ação urgente.</div>:smartActions.map((a,i)=><SmartActionCard key={i} {...a} source={smartSrc}/>)}
                {smartSrc==="regra"&&<div style={{fontSize:"0.65em",color:"var(--muted)",fontFamily:"Space Mono",marginTop:8}}>→ Rode a análise na aba IA para substituir por ações contextuais</div>}
              </div>
              <div className="card">
                <div className="card-title">🎯 Veredito da IA</div>
                {aiParsed?(
                  <div>
                    <div style={{textAlign:"center",padding:"14px 0 10px"}}>
                      <div style={{fontSize:"2em",fontWeight:800,letterSpacing:-1,color:aiParsed.veredito==="ESCALAR"?"var(--green)":aiParsed.veredito==="PAUSAR"?"var(--red)":"var(--yellow)"}}>{aiParsed.veredito}</div>
                      <div style={{fontSize:"0.7em",color:"var(--muted)",fontFamily:"Space Mono",marginTop:3}}>Score de Saúde: {aiParsed.score}/100</div>
                      <div style={{height:6,background:"var(--surface2)",borderRadius:3,margin:"8px 0",overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${aiParsed.score}%`,background:`linear-gradient(90deg,${aiParsed.score<40?"#ff3366":aiParsed.score<70?"#ffd060":"#00ffaa"}, ${aiParsed.score<40?"#ff6699":aiParsed.score<70?"#ffaa00":"#00cc88"})`,borderRadius:3,transition:"width 1s"}}/>
                      </div>
                    </div>
                    {aiParsed.projecao_7d&&(
                      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:7,marginBottom:10}}>
                        {[["Gasto 7d",`R$${aiParsed.projecao_7d.gasto}`],["Receita 7d",`R$${aiParsed.projecao_7d.receita}`],["Lucro 7d",`R$${aiParsed.projecao_7d.lucro}`],["Conv. 7d",aiParsed.projecao_7d.conversoes]].map(([l,v])=>(
                          <div key={l} style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:8,padding:"7px 11px"}}><div style={{fontSize:"0.6em",color:"var(--muted)",fontFamily:"Space Mono",marginBottom:2}}>{l}</div><div style={{fontSize:"0.88em",fontWeight:700,fontFamily:"Space Mono"}}>{v}</div></div>
                        ))}
                      </div>
                    )}
                    {aiParsed.bump_insight&&<div style={{background:"#ffd06010",border:"1px solid #ffd06030",borderRadius:8,padding:"9px 12px",fontSize:"0.74em",color:"#ffe080",marginBottom:8}}>💰 {aiParsed.bump_insight}</div>}
                    {aiParsed.risco_saturacao&&<div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:8,padding:"7px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}><span style={{fontSize:"0.68em",color:"var(--muted)",fontFamily:"Space Mono"}}>Risco saturação</span><span style={{fontSize:"0.74em",fontFamily:"Space Mono",fontWeight:700,color:aiParsed.risco_saturacao==="alto"?"var(--red)":aiParsed.risco_saturacao==="medio"?"var(--yellow)":"var(--green)"}}>{aiParsed.risco_saturacao.toUpperCase()}</span></div>}
                  </div>
                ):(
                  <div style={{textAlign:"center",padding:"28px 0",color:"var(--muted)"}}>
                    <div style={{fontSize:"2em",marginBottom:8}}>🤖</div>
                    <div style={{fontSize:"0.76em",fontFamily:"Space Mono",lineHeight:1.7}}>Rode a análise na aba IA para ver o veredito completo com score, projeção e insight de bumps</div>
                  </div>
                )}
              </div>
            </div>

            {/* PLANO DE AÇÃO DETALHADO */}
            <div className="card">
              <div className="card-title">
                📋 Plano de Ação Detalhado
                <span style={{marginLeft:8,fontSize:"0.75em",padding:"1px 8px",borderRadius:100,fontFamily:"Space Mono",color:"var(--muted)",background:"var(--surface2)"}}>
                  automático · atualiza com seus dados
                </span>
              </div>
              <DetailedRecommendations
                roas={roas} cpa={cpa} ctr={ctr} freq={freq}
                hookRate={hookRate} taxaConv={taxaConv} conversoes={conversoes}
                alcance={alcance} impressoes={impressoes} lucro={lucro}
                breakEven={breakEven} cpaIdeal={cpaIdeal}
                aiAcoes={aiParsed?.acoes}
              />
            </div>

            {/* TABELA DE DECISÃO */}
            <div className="card">
              <div className="card-title">📋 Tabela de Decisão — O Seu "Norte"</div>
              <table className="decision-table">
                <thead><tr><th>Cenário</th><th>Métrica</th><th>Diagnóstico</th><th>Ação Imediata</th></tr></thead>
                <tbody>
                  {[
                    {c:"Lucro alto e estável",  b:"green", m:"ROAS > 3x",          d:"Campanha saudável",                       a:"Escalar: +20% orçamento/48h"},
                    {c:"Cliques, 0 vendas",      b:"red",   m:"Checkout = 0",       d:"Página não converte",                     a:"Revise headline Hostinger + velocidade"},
                    {c:"Checkouts, 0 vendas",    b:"yellow",m:"Abandono alto",      d:"Problema no checkout",                    a:"E-mail/WhatsApp de recuperação"},
                    {c:"Frequência > 3.5",       b:"yellow",m:"CPC subindo",        d:"Público saturou",                         a:"Novo criativo imediato"},
                    {c:"CTR < 1%",               b:"red",   m:"CTR baixo",          d:"Criativo fraco",                          a:"3 novos ganchos (primeiros 3s)"},
                    {c:"CTR alto, conv. baixa",  b:"yellow",m:"Taxa conv < 1%",     d:"Landing page não fecha",                  a:"Revise LP: headline, prova social, CTA"},
                    {c:"CPA explodindo",          b:"red",   m:`CPA > R$${(cpaIdeal*1.5).toFixed(0)}`, d:"Algoritmo perdido/público saturado", a:"Lance com teto + novo público"},
                    {c:"No break-even",           b:"yellow",m:`ROAS = ${breakEven.toFixed(1)}x`,d:"Zero a zero",               a:"Otimize CPA ou aumente ticket/bumps"},
                  ].map((r,i)=>(
                    <tr key={i}>
                      <td><span className={`badge ${r.b}`}>{r.c}</span></td>
                      <td style={{fontFamily:"Space Mono",fontSize:"0.78em",fontWeight:700}}>{r.m}</td>
                      <td style={{color:"var(--muted)",fontSize:"0.78em"}}>{r.d}</td>
                      <td style={{fontSize:"0.78em"}}>{r.a}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PROTOCOLOS */}
            <div className="two-col">
              <div className="card">
                <div className="card-title">📈 Protocolo de Escala</div>
                {[
                  {icon:"⬆️",title:"Escala Vertical",cond:`Quando: ROAS ${breakEven.toFixed(1)}x+ por 3 dias E Freq < 2.5\nComo: +20% a cada 48h. NUNCA dobre — o CPA vai explodir.`},
                  {icon:"↔️",title:"Escala Horizontal",cond:"Quando: Adset cansando (CPA subindo) mas criativo bom\nComo: Duplique para novos países. BR→MX→CL. Interesses→Broad→Lookalike."},
                  {icon:"🧪",title:"Laboratório (10% budget)",cond:"Sempre: novo criativo a cada 7 dias.\nAlerta: Freq 3.0-3.5 = público enjoou. Já viu 3x e não comprou."},
                ].map((p,i)=><div className="protocol-row" key={i}><span className="protocol-icon">{p.icon}</span><div><div className="protocol-title">{p.title}</div><div className="protocol-cond" style={{whiteSpace:"pre-line"}}>{p.cond}</div></div></div>)}
              </div>
              <div className="card">
                <div className="card-title">🚨 Protocolo de Crise</div>
                {[
                  {icon:"📉",title:"CTR < 1% — Criativo",cond:"3 variações de gancho. Primeiros 3 segundos são tudo."},
                  {icon:"🛒",title:"CTR alto + conv. baixa — Página",cond:"Hostinger: velocidade, headline, prova social, CTA."},
                  {icon:"💸",title:"CPA subindo — Saturação",cond:"Mude cor, troque música ou mude público."},
                  {icon:"🔄",title:"Abandono — Recuperação",cond:"E-mail/WhatsApp + garantia + bônus exclusivo."},
                ].map((p,i)=><div className="protocol-row" key={i}><span className="protocol-icon">{p.icon}</span><div><div className="protocol-title">{p.title}</div><div className="protocol-cond">{p.cond}</div></div></div>)}
              </div>
            </div>
          </div>
        )}

        {/* ═══ ANALYTICS ═══ */}
        {tab==="analytics"&&(
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div className="card">
              <div className="card-title">📈 Performance 7 Dias</div>
              <div className="chart-wrap">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{top:10,right:10,left:0,bottom:0}}>
                    <defs>
                      <linearGradient id="gG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#7c3aed" stopOpacity={0.35}/><stop offset="95%" stopColor="#7c3aed" stopOpacity={0}/></linearGradient>
                      <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#00ffaa" stopOpacity={0.35}/><stop offset="95%" stopColor="#00ffaa" stopOpacity={0}/></linearGradient>
                      <linearGradient id="gR" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ffd060" stopOpacity={0.25}/><stop offset="95%" stopColor="#ffd060" stopOpacity={0}/></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08"/>
                    <XAxis dataKey="dia" tick={{fill:"#7070a0",fontSize:11,fontFamily:"Space Mono"}} axisLine={false} tickLine={false}/>
                    <YAxis yAxisId="l" tick={{fill:"#7070a0",fontSize:10,fontFamily:"Space Mono"}} axisLine={false} tickLine={false} tickFormatter={v=>`R$${v}`}/>
                    <YAxis yAxisId="r" orientation="right" tick={{fill:"#7070a0",fontSize:10,fontFamily:"Space Mono"}} axisLine={false} tickLine={false}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Area yAxisId="l" type="monotone" dataKey="Gasto"      stroke="#7c3aed" strokeWidth={2} fill="url(#gG)" dot={false}/>
                    <Area yAxisId="l" type="monotone" dataKey="Receita"    stroke="#ffd060" strokeWidth={2} fill="url(#gR)" dot={false}/>
                    <Area yAxisId="r" type="monotone" dataKey="Conversões" stroke="#00ffaa" strokeWidth={2} fill="url(#gC)" dot={false}/>
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="two-col">
              <div className="card"><div className="card-title">🌪️ Funil</div><FunnelChart impressoes={impressoes} cliques={cliques} checkouts={checkouts} vendas={conversoes}/></div>
              <div className="card"><div className="card-title">⚡ Live Feed</div><LiveFeed/></div>
            </div>
          </div>
        )}

        {/* ═══ LATAM ═══ */}
        {tab==="latam"&&(
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div className="two-col">
              <div className="card">
                <div className="card-title">🌎 Top Países — ROAS</div>
                {GEO.map((g,i)=>(
                  <div className="geo-row" key={i}>
                    <span style={{fontSize:"1.3em"}}>{g.flag}</span>
                    <div style={{flex:1}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:"0.78em",fontWeight:700}}>{g.country}</span>
                        <div style={{textAlign:"right"}}><div style={{fontSize:"0.72em",fontFamily:"Space Mono",color:g.color}}>ROAS {g.roas.toFixed(1)}x</div><div style={{fontSize:"0.68em",color:"var(--muted)",fontFamily:"Space Mono"}}>CPA R${g.cpa}</div></div>
                      </div>
                      <div className="geo-bar" style={{width:`${(g.roas/GEO_MAX)*100}%`,background:g.color,opacity:0.6}}/>
                    </div>
                  </div>
                ))}
              </div>
              <div className="card">
                <div className="card-title">💡 Estratégia por País</div>
                {[{flag:"🇨🇱",c:"Chile",i:"Maior ROAS. Escale aqui primeiro."},
                  {flag:"🇲🇽",c:"México",i:"Maior volume. CPA baixo. Topo de funil."},
                  {flag:"🇧🇷",c:"Brasil",i:"Mais competitivo. Foque em UGC em pt-BR."},
                  {flag:"🇨🇴",c:"Colômbia",i:"CPA baixo. Testar lookalike de compradores."},
                  {flag:"🇦🇷",c:"Argentina",i:"Câmbio favorável. Espanhol neutro."}].map(({flag,c,i})=>(
                  <div key={c} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:"1px solid var(--border)",alignItems:"center"}}>
                    <span style={{fontSize:"1.3em"}}>{flag}</span>
                    <div><div style={{fontWeight:700,fontSize:"0.78em",marginBottom:1}}>{c}</div><div style={{fontSize:"0.7em",color:"var(--muted)",lineHeight:1.5}}>{i}</div></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ═══ IA ═══ */}
        {tab==="ia"&&(
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div className="card">
              <div className="card-title">🤖 Conselheiro de Tráfego — IA</div>
              <div style={{fontSize:"0.68em",color:"var(--muted)",fontFamily:"Space Mono",marginBottom:12,lineHeight:1.7,background:"var(--surface2)",borderRadius:8,padding:"8px 12px"}}>
                🛡️ <strong style={{color:"var(--text)"}}>Anti-Burro:</strong> Parsing seguro com Regex · JSON estruturado + fallback markdown · IA tem prioridade sobre regras fixas · Nunca quebra por "conversinha" no output
              </div>
              <div className="ai-section">
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:4}}>
                  <div className="field" style={{margin:0}}><label>Dias rodando</label><input type="text" placeholder="ex: 3 dias" value={diasRodando} onChange={e=>setDiasRodando(e.target.value)}/></div>
                  <div className="field" style={{margin:0}}><label>Tipo de criativo</label><select value={tipoCriativo} onChange={e=>setTipoCriativo(e.target.value)}>{["UGC","Vídeo longo","Imagem estática","Carrossel","Reels","Stories"].map(t=><option key={t}>{t}</option>)}</select></div>
                  <div className="field" style={{margin:0}}><label>Tipo de público</label><select value={tipoPublico} onChange={e=>setTipoPublico(e.target.value)}>{["Open/Broad","Lookalike 1%","Lookalike 3%","Interesse","Remarketing","Custom"].map(t=><option key={t}>{t}</option>)}</select></div>
                </div>
                <textarea className="ai-textarea" placeholder="Contexto extra: sazonalidade, promoção, concorrência..." value={contexto} onChange={e=>setContexto(e.target.value)}/>
                <button className="btn-primary" onClick={analisar} disabled={loading}>
                  {loading?<><div className="spinner"/>Analisando...</>:"🔍 Diagnóstico Completo + Smart Actions"}
                </button>
                {loading&&<div className="loading"><div className="spinner2"/>Consultando conselheiro... pode levar 10-15s</div>}
                {resultado&&!loading&&<div className="ai-result">{renderMarkdown(resultado)}</div>}
              </div>
            </div>
            {historico.length>0&&(
              <div className="card">
                <div className="card-title">📁 Histórico</div>
                <div className="history-summary">
                  <div className="summary-box"><span className="summary-box-label">ANÁLISES</span><span className="summary-box-value">{historico.length}</span></div>
                  <div className="summary-box"><span className="summary-box-label">MELHOR ROAS</span><span className="summary-box-value">{Math.max(...historico.map(h=>h.roas)).toFixed(2)}x</span></div>
                  <div className="summary-box"><span className="summary-box-label">MENOR CPA</span><span className="summary-box-value">R${Math.min(...historico.map(h=>h.cpa)).toFixed(2)}</span></div>
                  <div className="summary-box"><span className="summary-box-label">MELHOR ROI</span><span className="summary-box-value">{Math.max(...historico.map(h=>h.roi)).toFixed(0)}%</span></div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:7}}>
                  {historico.map((h,i)=>(
                    <div className="history-item" key={h.id} onClick={()=>setExpandedHist(expandedHist===i?null:i)}>
                      <div className="history-header">
                        <span className="history-meta">{h.hora} · {h.plataforma} {h.parsed?"· ✅ JSON":"· 📝 texto"}</span>
                        <div className="history-badges">
                          <span className={`badge ${h.roas>=breakEven?"green":"red"}`}>ROAS {h.roas.toFixed(2)}x</span>
                          <span className={`badge ${h.cpa<=cpaIdeal?"green":"red"}`}>CPA R${h.cpa.toFixed(2)}</span>
                          <span className={`badge ${h.roi>0?"green":"red"}`}>ROI {h.roi.toFixed(0)}%</span>
                        </div>
                      </div>
                      {expandedHist===i&&<div className="history-body">{renderMarkdown(h.resultado)}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ GUARDIAN ═══ */}
        {tab==="guardian"&&(
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div className="card">
              <div className="card-title">🛡️ Guardião de Criativos — Limites de Segurança</div>
              <div style={{fontSize:"0.72em",color:"var(--muted)",fontFamily:"Space Mono",marginBottom:14,lineHeight:1.7}}>
                Monitora em tempo real. Ajuste os limites. 🛡️ Anti-Burro: regras fixas são backup — IA tem prioridade quando disponível.
              </div>
              <GuardianPanel cpc={cpc} ctr={ctr} gasto={gasto} conversoes={conversoes} freq={freq}/>
            </div>
          </div>
        )}




        {/* ═══ MAKE / SaaS ═══ */}
        {tab==="make"&&(()=>{
          const CODE_SERVER = `// server.js — Cole na sua VPS Hostinger e rode com PM2
const express  = require('express');
const mongoose = require('mongoose');
const cors     = require('cors');
const app      = express();

app.use(cors());          // permite o browser chamar a API
app.use(express.json());

// ── MongoDB (opcional — troque pela sua connection string) ──────────────────
mongoose.connect(process.env.MONGO_URI || 'mongodb://localhost/ads-saas');

const MetricSchema = new mongoose.Schema({
  produto:     String,
  plataforma:  { type: String, default: 'Meta Ads' },
  gasto:       Number,
  impressoes:  Number,
  cliques:     Number,
  ctr:         Number,
  cpc:         Number,
  cpm:         Number,
  alcance:     Number,
  frequencia:  Number,
  conversoes:  Number,
  checkouts:   Number,
  roas:        Number,
  historico:   Array,   // [{dia,Gasto,Conversões,Receita}, ...]
  bumps:       Array,   // [{id,nome,vendas,ativo}, ...]
  config:      Object,  // {ticket, margem, nicho}
  timestamp:   { type: Date, default: Date.now }
});
const Metric = mongoose.model('Metric', MetricSchema);

// ── POST /webhook/metrics — Make chama aqui ─────────────────────────────────
app.post('/webhook/metrics', async (req, res) => {
  try {
    const produto = req.body.produto || 'default';
    await Metric.findOneAndUpdate(
      { produto },
      { ...req.body, timestamp: new Date() },
      { upsert: true, new: true }
    );
    console.log(\`[✅] \${produto} | gasto R$\${req.body.conta?.gasto} | conv \${req.body.conta?.conversoes}\`);
    res.json({ ok: true });
  } catch(e) {
    console.error('[❌]', e.message);
    res.status(500).json({ error: e.message });
  }
});

// ── GET /get-metrics — Frontend busca aqui ──────────────────────────────────
app.get('/get-metrics', async (req, res) => {
  const produto = req.query.produto || 'default';
  const doc = await Metric.findOne({ produto }).sort({ timestamp: -1 });
  if (!doc) return res.status(404).json({ error: 'Produto não encontrado' });
  res.json(doc);
});

// ── GET /produtos — Lista todos os produtos cadastrados ─────────────────────
app.get('/produtos', async (req, res) => {
  const lista = await Metric.distinct('produto');
  res.json(lista);
});

// ── Health check ─────────────────────────────────────────────────────────────
app.get('/ping', (req, res) => res.json({ ok: true, ts: new Date() }));

app.listen(3000, () => console.log('🚀 Backend SaaS rodando na porta 3000'));`;

          const CODE_NGINX = `# /etc/nginx/sites-available/ads-saas
# Substitua SEU_DOMINIO pelo seu domínio (ex: app.seusite.com.br)

server {
    listen 80;
    server_name SEU_DOMINIO;

    # ── Frontend React (build) ───────────────────────────────────────────────
    root /var/www/ads-saas/dist;
    index index.html;

    location / {
        try_files $uri $uri/ /index.html;  # SPA routing
    }

    # ── Backend Node (proxy reverso) ────────────────────────────────────────
    location /api/ {
        proxy_pass http://localhost:3000/;
        proxy_http_version 1.1;
        proxy_set_header Upgrade $http_upgrade;
        proxy_set_header Connection 'upgrade';
        proxy_set_header Host $host;
        proxy_cache_bypass $http_upgrade;
    }
}`;

          const CODE_PM2 = `# Na VPS via SSH — instalar e subir o backend
npm install pm2 -g

# Entrar na pasta do projeto
cd /var/www/ads-saas/backend

# Instalar dependências
npm install express mongoose cors

# Subir com PM2 (nunca cai)
pm2 start server.js --name backend-saas

# Salvar para reiniciar automaticamente após reboot da VPS
pm2 save
pm2 startup

# Monitorar logs em tempo real
pm2 logs backend-saas`;

          const CODE_MAKE_JSON = `// JSON que o Make deve enviar via HTTP POST para:
// http://SEU_IP:3000/webhook/metrics

{
  "produto": "codigo-divino",

  "conta": {
    "gasto":      {{gasto_do_dia}},
    "impressoes": {{impressoes}},
    "cliques":    {{cliques}},
    "ctr":        {{ctr}},
    "cpc":        {{cpc}},
    "cpm":        {{cpm}},
    "alcance":    {{alcance}},
    "frequencia": {{frequencia}},
    "conversoes": {{conversoes}},
    "checkouts":  {{checkouts}},
    "roas":       {{roas}}
  },

  "config": {
    "ticket": 70,
    "margem": 40,
    "nicho":  "Manifestação / LATAM"
  },

  "historico": [
    { "dia": "Seg", "Gasto": 120, "Conversões": 4, "Receita": 280 },
    { "dia": "Ter", "Gasto": 95,  "Conversões": 3, "Receita": 210 }
  ],

  "bumps": [
    { "id": 1, "nome": "Áudios de Reprogramação", "vendas": 12, "ativo": true },
    { "id": 2, "nome": "Código Secreto da Atração","vendas": 8,  "ativo": true }
  ]
}`;

          const CODE_ENV = `# .env na raiz do backend — nunca versione esse arquivo no git
MONGO_URI=mongodb+srv://user:senha@cluster.mongodb.net/ads-saas
PORT=3000
NODE_ENV=production`;

          const codeBlock = (title, code, lang="js") => (
            <div style={{marginBottom:16}} key={title}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <span style={{fontSize:"0.62em",fontFamily:"Space Mono",color:"var(--muted)",textTransform:"uppercase",letterSpacing:"1.5px"}}>{title}</span>
                <button
                  onClick={()=>navigator.clipboard?.writeText(code)}
                  style={{fontSize:"0.58em",fontFamily:"Space Mono",background:"var(--surface3)",border:"1px solid var(--border-bright)",color:"var(--muted)",padding:"3px 10px",borderRadius:6,cursor:"pointer"}}>
                  📋 copiar
                </button>
              </div>
              <div style={{background:"#060610",border:"1px solid #00ffaa18",borderRadius:10,padding:"14px 16px",overflow:"auto",maxHeight:320}}>
                <pre style={{margin:0,fontSize:"0.7em",fontFamily:"Space Mono",lineHeight:1.8,color:"#88ffcc",whiteSpace:"pre"}}>{code}</pre>
              </div>
            </div>
          );

          return (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>

              {/* ── STATUS GERAL ── */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                {[
                  {label:"Make Webhook",status:makeStatus,ok:"connected",icon:"⚙️",sub:makeLastSync?`Sync: ${makeLastSync.toLocaleTimeString("pt-BR")}`:"Não configurado"},
                  {label:"VPS Hostinger",status:apiStatus,ok:"ok",icon:"🖥️",sub:apiLastSync?`Sync: ${apiLastSync.toLocaleTimeString("pt-BR")}`:apiUrl?"Configurada mas sem sync":"Não configurada"},
                  {label:"Fonte de Dados",status:apiStatus==="ok"||makeStatus==="connected"?"live":"demo",ok:"live",icon:apiStatus==="ok"?"⚡":"🔗",sub:apiStatus==="ok"?"VPS API":makeStatus==="connected"?"Make Webhook":"Simulação local"},
                ].map(({label,status,ok,icon,sub})=>{
                  const isOk=status===ok;
                  const c=isOk?"var(--green)":status==="fetching"||status==="testing"?"var(--yellow)":"var(--muted)";
                  return (
                    <div key={label} style={{background:"var(--surface2)",border:`1px solid ${isOk?"#00ffaa22":"var(--border)"}`,borderRadius:12,padding:"12px 14px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                        <span style={{fontSize:"0.7em",fontFamily:"Space Mono",color:"var(--muted)",textTransform:"uppercase"}}>{icon} {label}</span>
                        <span style={{fontSize:"0.58em",fontFamily:"Space Mono",color:c,fontWeight:700,padding:"2px 8px",background:`${isOk?"#00ffaa":"#7070a0"}12`,borderRadius:100,border:`1px solid ${isOk?"#00ffaa22":"var(--border)"}`}}>
                          {isOk?"● LIVE":status==="fetching"||status==="testing"?"● SYNCING":"● OFFLINE"}
                        </span>
                      </div>
                      <div style={{fontSize:"0.68em",fontFamily:"Space Mono",color:"var(--muted)"}}>{sub}</div>
                    </div>
                  );
                })}
              </div>

              {/* ── MAKE WEBHOOK (modo simples) ── */}
              <div className="card">
                <div className="card-title">⚙️ Make.com — Webhook Direto (Modo Simples)</div>
                <div style={{fontSize:"0.72em",color:"var(--muted)",fontFamily:"Space Mono",marginBottom:12,lineHeight:1.7}}>
                  Cole a URL do seu cenário Make. O dashboard popula todos os campos automaticamente via GET.
                </div>
                <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                  <input type="text" placeholder="https://hook.eu1.make.com/xxxxxxxxxxxx" value={webhookUrl} onChange={e=>setWebhookUrl(e.target.value)}
                    style={{flex:"1 1 300px",background:"var(--surface2)",border:`1px solid ${makeStatus==="connected"?"#00ffaa44":makeStatus==="error"?"#ff336644":"var(--border-bright)"}`,borderRadius:9,color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.78em",padding:"9px 12px",outline:"none"}}/>
                  <button onClick={testWebhook} disabled={!webhookUrl||makeStatus==="testing"} className="btn-primary" style={{whiteSpace:"nowrap",padding:"9px 16px"}}>
                    {makeStatus==="testing"?"⏳ Testando...":"⚡ Conectar"}
                  </button>
                </div>
                {makeStatus==="connected"&&(
                  <div style={{background:"#00ffaa10",border:"1px solid #00ffaa30",borderRadius:9,padding:"10px 14px",marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div><div style={{color:"var(--green)",fontWeight:700,fontSize:"0.84em"}}>✅ Make conectado!</div><div style={{color:"var(--muted)",fontSize:"0.66em",fontFamily:"Space Mono",marginTop:3}}>Último sync: {makeLastSync?.toLocaleTimeString("pt-BR")}</div></div>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:"0.65em",color:"var(--muted)",fontFamily:"Space Mono"}}>Auto 5min</span>
                        <div onClick={()=>setAutoSync(p=>!p)} style={{width:36,height:20,borderRadius:10,background:autoSync?"var(--accent)":"var(--surface3)",cursor:"pointer",position:"relative",border:"1px solid var(--border-bright)"}}>
                          <div style={{position:"absolute",top:3,left:autoSync?17:3,width:12,height:12,borderRadius:"50%",background:"white",transition:"left 0.2s"}}/>
                        </div>
                        <button onClick={testWebhook} className="btn-secondary" style={{padding:"5px 12px",fontSize:"0.72em"}}>🔄 Sync</button>
                      </div>
                    </div>
                  </div>
                )}
                {makeStatus==="error"&&<div style={{background:"#ff336610",border:"1px solid #ff336630",borderRadius:9,padding:"10px 14px",color:"#ff9aaa",fontSize:"0.76em",marginBottom:10}}>🔴 Falhou — Webhook OFF ou CORS bloqueado. Ative o módulo no Make e tente novamente.</div>}
              </div>

              {/* ── VPS HOSTINGER (Modo Pro) ── */}
              <div className="card">
                <div className="card-title" style={{color:"var(--accent)"}}>🖥️ VPS Hostinger — Modo Pro (Dados Reais 24/7)</div>
                <div style={{fontSize:"0.72em",color:"var(--muted)",fontFamily:"Space Mono",marginBottom:14,lineHeight:1.8}}>
                  Com VPS você desacopla o Make do frontend. O Make grava no servidor, o dashboard lê do servidor. Funciona para qualquer plataforma — Meta, Google, TikTok, Hotmart, Stripe.
                </div>
                <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                  <input type="text" placeholder="http://IP_DA_VPS:3000 ou https://api.seusite.com.br" value={apiUrl} onChange={e=>setApiUrl(e.target.value)}
                    style={{flex:"1 1 260px",background:"var(--surface2)",border:`1px solid ${apiStatus==="ok"?"#7c3aed55":apiStatus==="error"?"#ff336644":"var(--border-bright)"}`,borderRadius:9,color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.78em",padding:"9px 12px",outline:"none"}}/>
                  <input type="text" placeholder="Nome do produto (ex: codigo-divino)" value={apiProduto} onChange={e=>setApiProduto(e.target.value)}
                    style={{flex:"0 1 200px",background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:9,color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.78em",padding:"9px 12px",outline:"none"}}/>
                  <button onClick={fetchFromVPS} disabled={!apiUrl||apiStatus==="fetching"} className="btn-primary" style={{whiteSpace:"nowrap",padding:"9px 16px",background:"linear-gradient(135deg,var(--accent),var(--accent2))"}}>
                    {apiStatus==="fetching"?"⏳ Buscando...":"⚡ Buscar da VPS"}
                  </button>
                </div>
                {apiStatus==="ok"&&(
                  <div style={{background:"#7c3aed12",border:"1px solid #7c3aed30",borderRadius:9,padding:"10px 14px",marginBottom:12}}>
                    <div style={{color:"var(--accent)",fontWeight:700,fontSize:"0.82em"}}>⚡ VPS conectada — dados reais carregados</div>
                    <div style={{color:"var(--muted)",fontSize:"0.64em",fontFamily:"Space Mono",marginTop:3}}>Produto: {apiProduto||"default"} · Sync: {apiLastSync?.toLocaleTimeString("pt-BR")}</div>
                  </div>
                )}
                {apiStatus==="error"&&<div style={{background:"#ff336610",border:"1px solid #ff336630",borderRadius:9,padding:"10px 14px",color:"#ff9aaa",fontSize:"0.76em",marginBottom:12}}>🔴 VPS não respondeu — verifique se o server.js está rodando com PM2 e se a porta 3000 está aberta no firewall.</div>}
              </div>

              {/* ── DEPLOYMENT GUIDE ── */}
              <div className="card">
                <div className="card-title">🚀 Guia de Deploy Completo — Do Zero ao SaaS</div>

                {/* FLUXO VISUAL */}
                <div style={{marginBottom:20}}>
                  <div style={{fontSize:"0.62em",color:"var(--muted)",fontFamily:"Space Mono",textTransform:"uppercase",marginBottom:10}}>Arquitetura</div>
                  <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    {[
                      {icon:"📱",l:"Meta/Google/TikTok",s:"Fonte de dados"},
                      {arrow:"→"},
                      {icon:"⚙️",l:"Make.com",s:"Coleta + transforma"},
                      {arrow:"→"},
                      {icon:"🖥️",l:"VPS Hostinger",s:"server.js + MongoDB"},
                      {arrow:"→"},
                      {icon:"🚀",l:"Este Dashboard",s:"Lê a cada X min"},
                    ].map((x,i)=>x.arrow
                      ?<div key={i} style={{color:"var(--accent)",fontSize:"1.3em",flexShrink:0}}>{x.arrow}</div>
                      :<div key={i} style={{background:"var(--surface2)",border:"1px solid #7c3aed33",borderRadius:10,padding:"9px 12px",textAlign:"center",minWidth:90,flexShrink:0}}>
                        <div style={{fontSize:"1.3em",marginBottom:3}}>{x.icon}</div>
                        <div style={{fontSize:"0.66em",fontWeight:700,lineHeight:1.3}}>{x.l}</div>
                        <div style={{fontSize:"0.58em",color:"var(--muted)",fontFamily:"Space Mono",marginTop:2}}>{x.s}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* STEPS */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12,marginBottom:20}}>
                  {[
                    {n:"1",titulo:"Contratar VPS Hostinger",cor:"#00ffaa",
                      itens:["VPS KVM2 ou superior (2 vCPU, 8GB RAM)",`Ubuntu 22.04 LTS — mais suportado`,`Instale Node.js 20+: curl -fsSL https://deb.nodesource.com/setup_20.x | sudo -E bash - && sudo apt install -y nodejs`,`Instale MongoDB Community Edition (opcional — pode usar MongoDB Atlas grátis)`,`Abra a porta 3000 no firewall: sudo ufw allow 3000`]},
                    {n:"2",titulo:"Subir o Backend (server.js)",cor:"#a855f7",
                      itens:["Crie a pasta: mkdir -p /var/www/ads-saas/backend && cd /var/www/ads-saas/backend","Crie o server.js com o código abaixo","Instale dependências: npm install express mongoose cors","Suba com PM2 — veja comandos abaixo","Teste: curl http://localhost:3000/ping → deve retornar {ok:true}"]},
                    {n:"3",titulo:"Build do Frontend React",cor:"#ffd060",
                      itens:["No seu computador: npm install && npm run build","Isso gera a pasta dist/ com os arquivos estáticos","Envie para a VPS: scp -r dist/ usuario@IP:/var/www/ads-saas/","Configure o Nginx para servir dist/ — veja config abaixo","Ative o site: sudo ln -s /etc/nginx/sites-available/ads-saas /etc/nginx/sites-enabled/ && sudo nginx -t && sudo nginx -s reload"]},
                    {n:"4",titulo:"Configurar Make.com",cor:"#44aaff",
                      itens:["Crie um novo cenário no Make","Trigger: Scheduled (a cada 1h ou 30min)","Módulos: Watch Insights (Meta) + Aggregator","HTTP Request: POST para http://SEU_IP:3000/webhook/metrics","Body: JSON com o schema abaixo","Ative o cenário — o dashboard vai receber dados reais"]},
                    {n:"5",titulo:"SSL / HTTPS (Grátis com Certbot)",cor:"#ff6699",
                      itens:["sudo apt install certbot python3-certbot-nginx","sudo certbot --nginx -d app.seudominio.com","Auto-renova: sudo certbot renew --dry-run","Após SSL, troque http:// por https:// na URL da API aqui no dashboard"]},
                    {n:"6",titulo:"Multi-produto",cor:"#7c3aed",
                      itens:[`Cada produto tem um 'produto' único no JSON: 'codigo-divino', 'outro-produto'`,`O Make envia 'produto': 'nome-do-produto' no body`,`O dashboard filtra pelo nome no campo 'Nome do produto' acima`,`MongoDB cria um documento por produto automaticamente`,`Para ver todos os produtos: GET http://SEU_IP:3000/produtos`]},
                  ].map(({n,titulo,cor,itens})=>(
                    <div key={n} style={{background:"var(--surface2)",border:`1px solid ${cor}22`,borderRadius:12,padding:14}}>
                      <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:10}}>
                        <div style={{width:26,height:26,borderRadius:"50%",background:`${cor}22`,border:`1px solid ${cor}44`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          <span style={{fontSize:"0.7em",fontFamily:"Space Mono",fontWeight:700,color:cor}}>{n}</span>
                        </div>
                        <span style={{fontWeight:700,fontSize:"0.82em",color:cor}}>{titulo}</span>
                      </div>
                      {itens.map((it,i)=>(
                        <div key={i} style={{display:"flex",gap:8,marginBottom:5,alignItems:"flex-start"}}>
                          <span style={{fontSize:"0.62em",color:cor,flexShrink:0,marginTop:2}}>▸</span>
                          <span style={{fontSize:"0.72em",color:"var(--muted)",lineHeight:1.6,fontFamily:"Space Mono"}}>{it}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* CÓDIGO server.js */}
                {codeBlock("📄 server.js — Backend completo (Node.js + Express + MongoDB)", CODE_SERVER)}
                {codeBlock("🌐 nginx.conf — Servir frontend + proxy do backend", CODE_NGINX, "nginx")}
                {codeBlock("⚡ PM2 — Subir e manter o servidor rodando", CODE_PM2, "bash")}
                {codeBlock("📦 .env — Variáveis de ambiente (nunca no git)", CODE_ENV, "env")}
                {codeBlock("📡 JSON Payload — O que o Make deve enviar", CODE_MAKE_JSON)}

              </div>

              {/* ── PAYLOAD VIEWER ── */}
              {makeData&&(
                <div className="card">
                  <div className="card-title">📥 Último Payload Recebido</div>
                  <div style={{background:"#060610",border:"1px solid #00ffaa22",borderRadius:10,padding:14,overflow:"auto",maxHeight:260}}>
                    <pre style={{margin:0,fontSize:"0.68em",fontFamily:"Space Mono",lineHeight:1.8,color:"#66ffcc",whiteSpace:"pre-wrap"}}>{JSON.stringify(makeData,null,2)}</pre>
                  </div>
                </div>
              )}

              {/* ── TOKEN WARNING ── */}
              <div className="card" style={{background:"#ffd06006",border:"1px solid #ffd06020"}}>
                <div className="card-title" style={{color:"var(--yellow)"}}>⚠️ Token Meta — Crítico para Automação 24/7</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <div>
                    <div style={{fontSize:"0.68em",color:"var(--yellow)",fontFamily:"Space Mono",textTransform:"uppercase",marginBottom:8}}>O problema</div>
                    <div style={{fontSize:"0.74em",lineHeight:1.8,color:"#e0c060"}}>
                      Token padrão expira em <strong>1 hora</strong>. Se o Make tentar acessar depois disso, vai falhar silenciosamente — você vai achar que está funcionando mas não está.
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:"0.68em",color:"var(--green)",fontFamily:"Space Mono",textTransform:"uppercase",marginBottom:8}}>A solução (60 dias)</div>
                    <div style={{fontSize:"0.72em",lineHeight:1.9,fontFamily:"Space Mono",color:"#c0e8c0"}}>
                      1. developers.facebook.com/tools/explorer<br/>
                      2. Permissão: <strong>ads_read</strong><br/>
                      3. Access Token Debugger → Extend Token (60d)<br/>
                      4. Make → Connections → Edit → cole o novo token<br/>
                      5. Repita a cada 55 dias (coloque lembrete)
                    </div>
                  </div>
                </div>
              </div>

            </div>
          );
        })()}


        {/* ═══ PLANO DE AÇÃO ═══ */}
        {tab==="plano"&&(
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div className="card">
              <div className="card-title">📋 Plano de Ação Detalhado — Baseado nas Suas Métricas Atuais</div>
              <div style={{fontSize:"0.7em",color:"var(--muted)",fontFamily:"Space Mono",marginBottom:14,lineHeight:1.7}}>
                Lógica automática baseada em ROAS, CTR, frequência, hook rate e taxa de conversão em tempo real. Atualiza conforme você altera os dados no Dashboard.
              </div>
              <DetailedRecommendations
                roas={roas} cpa={cpa} ctr={ctr} freq={freq}
                hookRate={hookRate} taxaConv={taxaConv} conversoes={conversoes}
                alcance={alcance} impressoes={impressoes} lucro={lucro}
                breakEven={breakEven} cpaIdeal={cpaIdeal}
                aiAcoes={aiParsed?.acoes}
                ticket={ticket} margem={margem} checkouts={checkouts} gasto={gasto}
              />
            </div>

            {/* IA INSIGHT quando disponível */}
            {aiParsed && (
              <div className="card" style={{background:"#a855f710",border:"1px solid #a855f730"}}>
                <div className="card-title" style={{color:"var(--purple)"}}>🤖 IA Insight — Prompt Customizado</div>
                <div style={{fontSize:"0.82em",lineHeight:1.8,color:"#c8b0e8",fontStyle:"italic"}}>
                  Veredito da IA: <strong style={{color:"var(--purple)"}}>{aiParsed.veredito}</strong> · Score {aiParsed.score}/100
                  {aiParsed.bump_insight && <><br/>💰 {aiParsed.bump_insight}</>}
                </div>
              </div>
            )}
            {!aiParsed && (
              <div className="card" style={{background:"var(--surface2)",border:"1px solid var(--border)"}}>
                <div className="card-title">🤖 IA Insight — Prompt Customizado</div>
                <div style={{fontSize:"0.82em",color:"var(--muted)",fontStyle:"italic",lineHeight:1.8}}>
                  Rode a análise na aba <strong style={{color:"var(--text)"}}>🤖 IA</strong> para substituir as recomendações automáticas por insights contextuais personalizados da IA — incluindo veredito, score de saúde e bump insight.
                </div>
              </div>
            )}

            {/* CHECKLIST DIÁRIO */}
            <div className="card">
              <div className="card-title">✅ Checklist Diário do Media Buyer</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {[
                  {hora:"☀️ Manhã (8h)",items:["Verificar gasto vs. orçamento","Checar CPA vs. ideal","Ver frequência — acima de 3.0 → alarme","Olhar CTR — abaixo de 1% → trocar criativo"]},
                  {hora:"🌆 Tarde (14h)",items:["Atualizar dashboard com métricas do dia","Checar checkouts vs. conversões (abandono)","Verificar ROAS acumulado","Decidir se escala ou pausa"]},
                  {hora:"🌙 Noite (20h)",items:["Revisar lucro do dia com e sem bumps","Anotar qual criativo performou melhor","Planejar testes de amanhã","Conferir frequência final do dia"]},
                  {hora:"📅 Semanal",items:["Subir novo criativo (mesmo que esteja bom)","Testar novo público ou país","Revisar bumps — mudar nome do que não vende","Analisar histórico de análises da IA"]},
                ].map(({hora,items})=>(
                  <div key={hora} style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:10,padding:14}}>
                    <div style={{fontWeight:700,fontSize:"0.82em",marginBottom:10,color:"var(--text)"}}>{hora}</div>
                    {items.map((item,i)=>(
                      <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:6}}>
                        <span style={{fontSize:"0.7em",marginTop:2,flexShrink:0}}>☐</span>
                        <span style={{fontSize:"0.75em",color:"var(--muted)",lineHeight:1.5}}>{item}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ═══ GLOSSÁRIO ═══ */}
        {tab==="glossario"&&(
          <div style={{display:"flex",flexDirection:"column",gap:14}}>

            <div className="card">
              <div className="card-title">📚 Glossário Pro — Termos Reais de Gestão de Tráfego</div>
              <div style={{fontSize:"0.7em",color:"var(--muted)",fontFamily:"Space Mono",marginBottom:14,lineHeight:1.7}}>
                25+ termos usados por gestores profissionais no dia a dia. Coluna "Seu número" atualiza com seus dados em tempo real.
              </div>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr>
                  {["Termo","O que é","Quando agir","Seu número"].map(h=>(
                    <th key={h} style={{fontFamily:"Space Mono",fontSize:"0.6em",color:"var(--muted)",textTransform:"uppercase",padding:"8px 12px",borderBottom:"1px solid var(--border-bright)",textAlign:"left"}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {[
                    {t:"ROAS",s:"Return on Ad Spend",desc:"Receita ÷ Gasto. A métrica mais importante. Mostra quanto você ganha por real investido.",acao:`Escalar se > ${(breakEven*1.3).toFixed(1)}x | Investigar se < ${breakEven.toFixed(1)}x | Pausar se < ${(breakEven*0.7).toFixed(1)}x`,num:`${roas.toFixed(2)}x`,ok:roas>=breakEven},
                    {t:"CPA",s:"Custo por Aquisição",desc:"Gasto ÷ Conversões. Quanto você paga por venda. Precisa ser menor que o lucro por venda para a campanha ser sustentável.",acao:`Ótimo se < R$${cpaIdeal.toFixed(0)} | Ajustar se > R$${(cpaIdeal*1.2).toFixed(0)} | Crítico se > R$${(cpaIdeal*1.5).toFixed(0)}`,num:`R$${cpa.toFixed(2)}`,ok:cpa<=cpaIdeal},
                    {t:"Break-even ROAS",s:"Ponto de Equilíbrio",desc:"ROAS mínimo para não ter prejuízo = Ticket ÷ Lucro por venda. Abaixo disso, cada venda gera perda. Memorize esse número.",acao:"Toda decisão de escala/pausa deve ser comparada com esse valor. É o seu norte.",num:`${breakEven.toFixed(2)}x`,ok:true},
                    {t:"CTR",s:"Click-Through Rate",desc:"Cliques ÷ Impressões × 100. Mede se o criativo atrai atenção suficiente. Baixo = gancho fraco. Alto com conv. baixa = LP ruim.",acao:"< 1%: troque criativo urgente | 1–2%: ok | > 2%: excelente, monitore escala",num:`${ctr.toFixed(2)}%`,ok:ctr>=1},
                    {t:"CPC",s:"Custo por Clique",desc:"Gasto ÷ Cliques. Deve ser proporcional ao ticket. Regra: CPC máximo ≈ 1–1.5% do ticket para manter a equação saudável.",acao:`Seu limite: ~R$${(ticket*0.012).toFixed(2)} | Atual: R$${cpc.toFixed(2)}`,num:`R$${cpc.toFixed(2)}`,ok:cpc<=ticket*0.015},
                    {t:"CPM",s:"Custo por Mil Impressões",desc:"Custo para exibir 1.000 vezes. Reflete a concorrência no leilão do Meta. CPM alto = leilão competitivo = público disputado.",acao:"LATAM: < R$12 ótimo | R$12–20 normal | > R$20 reveja público ou criativo",num:`R$${cpmReal.toFixed(2)}`,ok:cpmReal<=20},
                    {t:"Hook Rate",s:"Taxa de Retenção Inicial",desc:"% que assistiu ≥ 3 segundos do vídeo. Mede a força do gancho. Sem hook, sem clique — o resto do funil não existe.",acao:"< 1.5%: gancho fraco — troque abertura | 1.5–3%: ok | > 3%: excelente",num:`${hookRate.toFixed(2)}%`,ok:hookRate>=2},
                    {t:"Frequência",s:"Impressões ÷ Alcance",desc:"Quantas vezes a mesma pessoa viu o anúncio. Alta frequência = público saturando, CPM subindo, CTR caindo.",acao:"< 2.5: saudável | 2.5–3.5: monitorar e preparar novo criativo | > 3.5: trocar já",num:`${freq.toFixed(1)}x`,ok:freq<=3},
                    {t:"Taxa de Conversão",s:"Cliques → Vendas",desc:"Vendas ÷ Cliques × 100. Mede a eficiência da landing page + oferta. CTR alto + Conv baixa = LP é o problema.",acao:"< 0.5%: LP crítica | 0.5–2%: ok | > 2%: excelente — hora de escalar",num:`${taxaConv.toFixed(2)}%`,ok:taxaConv>=1},
                    {t:"Taxa de Fechamento",s:"Checkouts → Vendas",desc:"Vendas ÷ Checkouts × 100. Mede eficiência do checkout. Baixo = fricção, preço percebido alto ou bug técnico.",acao:"< 20%: revisar checkout urgente | 20–50%: ok | > 50%: excelente",num:`${checkouts>0?((conversoes/checkouts)*100).toFixed(0):0}%`,ok:checkouts>0&&conversoes/checkouts>=0.2},
                    {t:"CBO",s:"Campaign Budget Optimization",desc:"Orçamento no nível da campanha. O Meta distribui automaticamente entre conjuntos para maximizar resultado global.",acao:"Use quando tem 2+ conjuntos validados e quer escalar. Ideal para escala vertical.",num:"Campanha",ok:true},
                    {t:"ABO",s:"Ad Set Budget Optimization",desc:"Orçamento fixo por conjunto. Mais controle manual, menos automação. Ideal para testes e validação inicial de novos públicos.",acao:"Use para testar novos públicos com budget controlado antes de migrar para CBO.",num:"Conjunto",ok:true},
                    {t:"Lookalike (LAL)",s:"Público Semelhante",desc:"Público criado pelo Meta com perfil similar aos seus compradores. 1% = mais preciso (menor), 3–5% = mais volume (maior).",acao:"Sempre baseie em compradores reais. 1% para CPA baixo, 3–5% para maior volume.",num:"1–5%",ok:true},
                    {t:"Custom Audience",s:"Público Personalizado",desc:"Lista de pessoas específicas: compradores, visitantes, emails, engajadores. Base para lookalikes e remarketing.",acao:"Mínimo 100 pessoas para gerar lookalike. Atualize a cada 30 dias.",num:"Sua lista",ok:true},
                    {t:"Broad / Aberto",s:"Sem segmentação de interesses",desc:"O algoritmo decide quem ver baseado no comportamento do pixel. Funciona com criativos fortes e pixel com dados ricos.",acao:"Teste quando lookalike e interesses saturarem. Requer pixel maduro e ≥ R$100/dia.",num:"Sem filtro",ok:true},
                    {t:"Pixel",s:"Código de Rastreamento Meta",desc:"Script instalado na LP que registra eventos: visualização, AddToCart, InitiateCheckout, Purchase. Treina o algoritmo.",acao:"Sem pixel bem configurado, o algoritmo não sabe quem compra. Fundamental.",num:"Essencial",ok:true},
                    {t:"Fase de Aprendizado",s:"Período de calibração",desc:"Período após criar/editar conjunto onde o Meta testa entrega para encontrar as melhores pessoas. CPAs instáveis.",acao:"Não edite durante o aprendizado. Aguarde 50 conversões ou 7 dias para julgar.",num:"~50 conv",ok:true},
                    {t:"Order Bump",s:"Oferta Extra no Checkout",desc:"Produto complementar adicionado com 1 clique durante o pagamento. Aumenta ticket sem novo custo de aquisição.",acao:"Preço: 20–40% do principal. Nome importa muito — teste variações de copy.",num:`R$3,99/bump`,ok:true},
                    {t:"Upsell",s:"Oferta Pós-Compra",desc:"Produto de maior valor oferecido imediatamente após a compra. Melhor momento — cliente acabou de comprar e está ativado.",acao:`Preço: 60–120% do principal. Apresente em até 60s após o pagamento.`,num:`~R$${(ticket*0.8).toFixed(0)}`,ok:true},
                    {t:"Downsell",s:"Oferta Alternativa Reduzida",desc:"Versão reduzida ou mais barata do upsell apresentada quando o cliente recusa. Recupera receita que seria perdida.",acao:"50–70% do preço do upsell. Mesmo benefício, menos entrega.",num:`~R$${(ticket*0.4).toFixed(0)}`,ok:true},
                    {t:"LTV",s:"Lifetime Value",desc:"Receita total gerada por um cliente ao longo do tempo. Bumps + upsells + recompras. LTV alto = pode pagar mais pelo CPA.",acao:"Aumente o LTV antes de competir por CPA. Quem tem LTV maior vence o leilão.",num:`~R$${(ticket*1.5).toFixed(0)} c/bumps`,ok:true},
                    {t:"UGC",s:"User Generated Content",desc:"Vídeo no estilo feito por usuário real. Autenticidade alta, custo baixo, CTR geralmente superior a produções polidas em LATAM.",acao:"Teste UGC vs vídeo profissional. Em LATAM, UGC costuma ter 30–50% mais CTR.",num:"Testar",ok:true},
                    {t:"Teste A/B",s:"Split Test",desc:"Comparar 2 versões de UM único elemento. Regra de ouro: mudar APENAS 1 variável por vez. Paciência mínima de 48h.",acao:"Mínimo: 48h e 50 conversões por variação. Meça CPA, não só CTR.",num:"1 var./vez",ok:true},
                    {t:"ROAS de Escala",s:"Threshold para aumentar budget",desc:"O ROAS que você precisa atingir antes de escalar. Geralmente 30–50% acima do break-even para ter margem de segurança.",acao:`Seu threshold: ${(breakEven*1.3).toFixed(2)}x. Só suba budget acima desse valor.`,num:`${(breakEven*1.3).toFixed(2)}x`,ok:roas>=(breakEven*1.3)},
                    {t:"ROAS de Corte",s:"Limite para pausar",desc:"O ROAS abaixo do qual você pausa imediatamente. Geralmente 70–80% do break-even. Abaixo disso = prejuízo acelerando.",acao:`Seu corte: ${(breakEven*0.75).toFixed(2)}x. Se cair abaixo — PAUSE.`,num:`${(breakEven*0.75).toFixed(2)}x`,ok:roas>=(breakEven*0.75)},
                  ].map((item,i)=>(
                    <tr key={i} style={{borderBottom:"1px solid var(--border)",background:i%2===0?"transparent":"#ffffff03"}}
                      onMouseEnter={e=>e.currentTarget.style.background="var(--surface2)"}
                      onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"transparent":"#ffffff03"}>
                      <td style={{padding:"9px 12px",verticalAlign:"top",whiteSpace:"nowrap"}}>
                        <div style={{fontWeight:800,color:"var(--accent)",fontSize:"0.82em"}}>{item.t}</div>
                        <div style={{fontSize:"0.6em",color:"var(--muted)",fontFamily:"Space Mono",marginTop:2}}>{item.s}</div>
                      </td>
                      <td style={{padding:"9px 12px",fontSize:"0.76em",lineHeight:1.6,verticalAlign:"top",color:"#c0c0e0",maxWidth:260}}>{item.desc}</td>
                      <td style={{padding:"9px 12px",fontSize:"0.71em",color:"var(--muted)",verticalAlign:"top",lineHeight:1.6,maxWidth:200}}>{item.acao}</td>
                      <td style={{padding:"9px 12px",fontSize:"0.8em",fontFamily:"Space Mono",fontWeight:700,verticalAlign:"top",whiteSpace:"nowrap",color:item.ok?"var(--green)":"var(--red)"}}>{item.num}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* QUANDO ESCALAR */}
            <div className="card">
              <div className="card-title">📈 Quando e Como Escalar — Guia Completo Pro Manager</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12}}>
                {[
                  {tipo:"⬆️ Vertical — Aumentar Orçamento",cor:"#00ffaa",
                    quando:[`ROAS > ${(breakEven*1.3).toFixed(1)}x por 3 dias consecutivos`,`Frequência < 2.5x (público com inventário)`,`Fase de aprendizado concluída (50+ conversões)`],
                    como:"+15–30% a cada 48h. CBO se tiver 2+ conjuntos lucrativos. Nunca dobre.",
                    naofaca:"Não edite mais nada durante a escala — o aprendizado reinicia a cada alteração.",
                    risco:"CPA pode subir 10–20% temporariamente. Normal. Se passar de 30%, recue 15%."},
                  {tipo:"↔️ Horizontal — Novos Públicos",cor:"#a855f7",
                    quando:["Frequência > 3.0x (saturando)","CPA subindo mas CTR ainda ok","Alcance esgotando no público atual"],
                    como:"Duplique o conjunto. Mude APENAS o público: novo interesse, LAL 1% ou novo país.",
                    naofaca:"Não mude criativo ao expandir público — isole as variáveis para saber o que funciona.",
                    risco:"Conversões mais lentas nos 2 primeiros dias com novo público. Normal."},
                  {tipo:"🌎 Geográfica — Novos Países LATAM",cor:"#ffd060",
                    quando:["Saturação no Brasil (freq > 3.5x)","Budget > R$200/dia (para dividir eficientemente)","Criativo campeão validado"],
                    como:"Sequência recomendada: BR → MX → CL → CO → AR. Um país por vez. Ajuste moeda/idioma.",
                    naofaca:"Não misture países no mesmo conjunto — CPMs e comportamentos são muito diferentes.",
                    risco:"CPM varia muito por país. Monitore os primeiros 3 dias antes de escalar."},
                  {tipo:"🎨 De Criativos — Multiplicar Vencedores",cor:"#44aaff",
                    quando:["Freq 2.5–3.0x (prevenir antes de saturar)","CTR caindo semana a semana","Quer crescer sem mudar público"],
                    como:"Crie 3 variações do criativo campeão mudando 1 elemento: gancho, cor, locutor ou CTA.",
                    naofaca:"Não crie criativos completamente diferentes — variações do vencedor têm muito mais chance.",
                    risco:"Diluição de budget se tiver muitos criativos. Máximo 3–5 por conjunto."},
                  {tipo:"💰 De Oferta — Aumentar Ticket",cor:"#ff6699",
                    quando:["ROAS > break-even por 5+ dias consecutivos","Taxa de conversão > 1.5%","Produto validado com bom volume"],
                    como:`Teste A/B: 50% tráfego no preço atual vs novo (+20%). LP separada. Monitore 5 dias.`,
                    naofaca:"Não aumente o ticket sem LP diferente justificando o valor. O cliente percebe.",
                    risco:"Conversão pode cair 10–15%. Se o ROAS mantiver, o lucro total subiu."},
                  {tipo:"📦 De Portfólio — Novos Produtos",cor:"#7c3aed",
                    quando:["LTV atual abaixo do potencial","Campanha lucrativa há 2+ semanas","Compradores com potencial de recompra"],
                    como:`Upsell R$${(ticket*0.7).toFixed(0)}–${(ticket*1.2).toFixed(0)} na página de obrigado. Valide no orgânico antes de anunciar.`,
                    naofaca:"Não anuncie produto novo sem validar organicamente. Não dilua foco antes de dominar o primeiro.",
                    risco:"Diluição de atenção operacional. Domine 1 produto antes de adicionar o segundo."},
                ].map(({tipo,cor,quando,como,naofaca,risco})=>(
                  <div key={tipo} style={{background:"var(--surface2)",border:`1px solid ${cor}22`,borderRadius:12,padding:14}}>
                    <div style={{fontWeight:800,fontSize:"0.85em",marginBottom:10,color:cor}}>{tipo}</div>
                    <div style={{marginBottom:9}}>
                      <div style={{fontSize:"0.6em",fontFamily:"Space Mono",color:cor,textTransform:"uppercase",marginBottom:5}}>✅ Quando usar</div>
                      {quando.map((q,i)=><div key={i} style={{fontSize:"0.74em",color:"var(--muted)",lineHeight:1.6,paddingLeft:8,marginBottom:2}}>• {q}</div>)}
                    </div>
                    <div style={{marginBottom:9}}>
                      <div style={{fontSize:"0.6em",fontFamily:"Space Mono",color:"var(--muted)",textTransform:"uppercase",marginBottom:4}}>🔧 Como fazer</div>
                      <div style={{fontSize:"0.74em",lineHeight:1.6,color:"var(--text)"}}>{como}</div>
                    </div>
                    <div style={{marginBottom:7}}>
                      <div style={{fontSize:"0.6em",fontFamily:"Space Mono",color:"#ff9aaa",textTransform:"uppercase",marginBottom:3}}>🚫 Não faça</div>
                      <div style={{fontSize:"0.71em",color:"#ff9aaa",lineHeight:1.5}}>{naofaca}</div>
                    </div>
                    <div>
                      <div style={{fontSize:"0.6em",fontFamily:"Space Mono",color:"var(--yellow)",textTransform:"uppercase",marginBottom:3}}>⚠️ Risco</div>
                      <div style={{fontSize:"0.71em",color:"#ffe080",lineHeight:1.5}}>{risco}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* QUANDO PAUSAR / DELETAR */}
            <div className="card" style={{background:"#ff336606",border:"1px solid #ff336620"}}>
              <div className="card-title" style={{color:"var(--red)"}}>⛔ Quando Pausar, Criar Novo ou Reformular — Decisões Difíceis</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                {[
                  {s:"Freq > 3.5 + ROAS abaixo do break-even",a:"Pausar conjunto. Não delete — reative com criativo novo em 7 dias.",n:"PAUSAR AGORA"},
                  {s:`Gasto > R$${(cpaIdeal*2).toFixed(0)} sem conversão`,a:"Pausar. Diagnosticar gargalo: criativo → LP → checkout → pixel.",n:"PAUSAR AGORA"},
                  {s:"ROAS < 70% do break-even por 3 dias",a:"Pausar e reformular oferta. Preço pode estar alto demais para o público.",n:"PAUSAR AGORA"},
                  {s:"CPA > 2× ideal + CTR baixo",a:"Criar conjunto novo: criativo + público + configuração do zero.",n:"CRIAR NOVO"},
                  {s:"Aprendizado há 7 dias sem 50 conversões",a:"Pausar, aumentar budget OU simplificar o evento de conversão (AddToCart antes de Purchase).",n:"AJUSTAR"},
                  {s:"CTR caindo semana a semana",a:"Trocar criativo. Micro ajustes não salvam criativo saturado — crie novo.",n:"TROCAR CRIATIVO"},
                  {s:"ROAS ok mas lucro zero (ticket baixo)",a:`Aumentar ticket 20% em A/B. Se conv cair < 10%, o lucro total subiu.`,n:"AUMENTAR TICKET"},
                  {s:"Sem conv em 2 mercados/públicos diferentes",a:"Problema é a oferta/produto, não o tráfego. Reformule antes de gastar mais.",n:"REVER OFERTA"},
                  {s:"Conjunto com ROAS ótimo há 30+ dias",a:"Nunca delete — é o seu criativo campeão. Preserve para benchmark.",n:"PRESERVAR"},
                ].map(({s,a,n})=>{
                  const c=n==="PAUSAR AGORA"?"#ff3366":n==="CRIAR NOVO"?"#ff6699":n==="PRESERVAR"?"#00ffaa":n==="AUMENTAR TICKET"||n==="REVER OFERTA"?"#ffd060":"#44aaff";
                  return (
                    <div key={s} style={{background:"var(--surface2)",border:`1px solid ${c}22`,borderRadius:10,padding:12}}>
                      <span style={{fontSize:"0.58em",fontFamily:"Space Mono",fontWeight:700,color:c,marginBottom:7,padding:"2px 8px",background:`${c}18`,borderRadius:100,display:"inline-block"}}>{n}</span>
                      <div style={{fontSize:"0.71em",color:"var(--muted)",lineHeight:1.5,marginBottom:7,marginTop:6}}>{s}</div>
                      <div style={{fontSize:"0.74em",fontWeight:600,lineHeight:1.5,color:"var(--text)"}}>{a}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* FÓRMULAS */}
            <div className="card">
              <div className="card-title">🔢 Fórmulas Pro — Com Seus Números em Tempo Real</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                {[
                  {f:"ROAS = Receita ÷ Gasto",r:`${roas.toFixed(2)}x`,c:"var(--accent)",b:`Break-even: ${breakEven.toFixed(2)}x | Escala: ${(breakEven*1.3).toFixed(2)}x`},
                  {f:"CPA = Gasto ÷ Conversões",r:`R$${cpa.toFixed(2)}`,c:cpa<=cpaIdeal?"var(--green)":"var(--red)",b:`Ideal: R$${cpaIdeal.toFixed(2)} | Limite: R$${(cpaIdeal*1.5).toFixed(2)}`},
                  {f:"Break-even = Ticket ÷ Lucro/venda",r:`${breakEven.toFixed(2)}x`,c:"var(--yellow)",b:`Threshold de escala: ${(breakEven*1.3).toFixed(2)}x`},
                  {f:"ROI = (Receita − Gasto) ÷ Gasto × 100",r:`${roi.toFixed(0)}%`,c:roi>0?"var(--green)":"var(--red)",b:"Positivo = lucrativo | Negativo = investigar"},
                  {f:"Taxa Conv = Vendas ÷ Cliques × 100",r:`${taxaConv.toFixed(2)}%`,c:taxaConv>=1?"var(--green)":"var(--yellow)",b:"Bom: > 1% | Excelente: > 2%"},
                  {f:"Hook Rate = Cliques ÷ Impressões × 100",r:`${hookRate.toFixed(2)}%`,c:hookRate>=3?"var(--green)":hookRate>=1.5?"var(--yellow)":"var(--red)",b:"Bom: > 2% | Excelente: > 3%"},
                  {f:"CPM Real = Gasto ÷ Impressões × 1000",r:`R$${cpmReal.toFixed(2)}`,c:cpmReal<=15?"var(--green)":cpmReal<=25?"var(--yellow)":"var(--red)",b:"LATAM: < R$12 ótimo | > R$20 investigar"},
                  {f:"Fechamento = Vendas ÷ Checkouts × 100",r:`${checkouts>0?((conversoes/checkouts)*100).toFixed(0):0}%`,c:checkouts>0&&conversoes/checkouts>=0.3?"var(--green)":"var(--red)",b:"Saudável: > 30% | Crítico: < 20%"},
                  {f:"CPA Máx = Ticket × Margem%",r:`R$${cpaIdeal.toFixed(2)}`,c:"var(--accent)",b:"Seu teto absoluto de CPA lucrativo"},
                ].map(({f,r,c,b})=>(
                  <div key={f} style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:9,padding:"11px 13px"}}>
                    <div style={{fontSize:"0.63em",fontFamily:"Space Mono",color:"var(--muted)",marginBottom:5,lineHeight:1.5}}>{f}</div>
                    <div style={{fontSize:"1em",fontWeight:800,fontFamily:"Space Mono",color:c,marginBottom:5}}>{r}</div>
                    <div style={{fontSize:"0.6em",color:"var(--muted)",fontFamily:"Space Mono",lineHeight:1.4}}>{b}</div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        )}

        {/* ═══ CRIATIVOS v2 ═══ */}
        {tab==="criativos"&&(()=>{
          const fmtIcons={imagem:"🖼",video:"🎬",carrossel:"📋",stories:"📱",reels:"🎞"};
          const ctrC=v=>v>=3?"var(--green)":v>=1.5?"var(--yellow)":"var(--red)";
          const cpaC=v=>v<=cpaIdeal?"var(--green)":v<=cpaIdeal*1.5?"var(--yellow)":"var(--red)";
          const roasC=v=>v>=breakEven*1.3?"var(--green)":v>=breakEven?"var(--yellow)":"var(--red)";
          const fmt2=(n,d=2)=>Number(n).toLocaleString("pt-BR",{minimumFractionDigits:d,maximumFractionDigits:d});

          // Médias da conta para comparativo
          const avgCTR_acc=criativos.length?criativos.reduce((s,c)=>s+c.metricas.ctr,0)/criativos.length:0;
          const avgCPA_acc=criativos.length?criativos.reduce((s,c)=>s+c.metricas.cpa,0)/criativos.length:0;
          const avgROAS_acc=criativos.length?criativos.reduce((s,c)=>s+(c.metricas.receita/(c.metricas.gasto||1)),0)/criativos.length:0;

          function sugestao(m){
            const r=m.receita/(m.gasto||1);
            if(m.ctr<1.5)return{icon:"⚠️",txt:"CTR baixo – troque o gancho",cor:"var(--red)"};
            if(m.cpa>cpaIdeal*1.5)return{icon:"🔴",txt:"CPA crítico – revise público",cor:"var(--red)"};
            if(r>=breakEven*1.4)return{icon:"🚀",txt:"Escalar agora – ROAS excelente",cor:"var(--green)"};
            if(m.ctr>=3&&r>=breakEven)return{icon:"✅",txt:"Criativo saudável",cor:"var(--green)"};
            if(r<breakEven)return{icon:"⚡",txt:"ROAS abaixo do break-even",cor:"var(--yellow)"};
            return{icon:"📊",txt:"Monitorar performance",cor:"var(--muted)"};
          }

          // Filtros + busca + ordenação
          const allTags=[...new Set(criativos.flatMap(c=>c.tags||[]))];
          let filtered=criativos.filter(cr=>{
            if(criativoFiltroStatus!=="todos"&&cr.status!==criativoFiltroStatus)return false;
            if(criativoCampanha!=="todas"&&cr.campanha!==criativoCampanha)return false;
            if(criativoFiltroTag!=="todas"&&!(cr.tags||[]).includes(criativoFiltroTag))return false;
            if(criativoSearch&&!cr.nome.toLowerCase().includes(criativoSearch.toLowerCase())&&!(cr.tags||[]).some(t=>t.includes(criativoSearch.toLowerCase())))return false;
            return true;
          });
          const sortFns={
            ctr:(a,b)=>b.metricas.ctr-a.metricas.ctr,
            cpa:(a,b)=>a.metricas.cpa-b.metricas.cpa,
            roas:(a,b)=>(b.metricas.receita/(b.metricas.gasto||1))-(a.metricas.receita/(a.metricas.gasto||1)),
            impressoes:(a,b)=>b.metricas.impressoes-a.metricas.impressoes,
            nome:(a,b)=>a.nome.localeCompare(b.nome),
          };
          filtered=[...filtered].sort(sortFns[criativoSortBy]||sortFns.ctr);

          const totalImpr=criativos.reduce((s,c)=>s+c.metricas.impressoes,0);
          const totalGasto=criativos.reduce((s,c)=>s+(c.metricas.gasto||0),0);
          const totalReceita=criativos.reduce((s,c)=>s+(c.metricas.receita||0),0);
          const melhor=criativos.length?[...criativos].sort((a,b)=>b.metricas.ctr-a.metricas.ctr)[0]:null;

          // ── Sparkline simples (SVG inline, sem lib extra) ──
          function Sparkline({data,key1="ctr",color="var(--accent)",h=40,w=120}){
            if(!data||!data.length)return null;
            const vals=data.map(d=>d[key1]);
            const mn=Math.min(...vals),mx=Math.max(...vals);
            const range=mx-mn||1;
            const pts=vals.map((v,i)=>`${(i/(vals.length-1))*w},${h-((v-mn)/range)*h}`).join(" ");
            return(
              <svg width={w} height={h} style={{display:"block"}}>
                <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
                <circle cx={(vals.length-1)/(vals.length-1)*w} cy={h-((vals[vals.length-1]-mn)/range)*h} r="3" fill={color}/>
              </svg>
            );
          }

          // ── Modal de detalhe (gráfico + info) ──
          function DetalheModal({cr,onClose}){
            const m=cr.metricas;
            const roas_cr=m.receita/(m.gasto||1);
            const lucro_cr=m.receita-m.gasto;
            const sg=sugestao(m);
            const camp=CAMPANHAS_MOCK.find(c=>c.id===cr.campanha);
            const st=STATUS_CR[cr.status]||STATUS_CR.rascunho;
            return(
              <div style={{position:"fixed",inset:0,background:"#00000090",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
                <div style={{background:"var(--surface)",border:"1px solid var(--border-bright)",borderRadius:16,width:"100%",maxWidth:680,maxHeight:"90vh",overflowY:"auto",padding:24}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:18}}>
                    <div>
                      <div style={{fontSize:"1em",fontWeight:800}}>{cr.nome}</div>
                      <div style={{fontSize:"0.68em",color:"var(--muted)",fontFamily:"Space Mono",marginTop:3}}>{camp?.name||"Sem campanha"} · {cr.briefing.formato}</div>
                    </div>
                    <button onClick={onClose} style={{background:"none",border:"none",color:"var(--muted)",fontSize:18,cursor:"pointer"}}>✕</button>
                  </div>
                  {/* Sugestão */}
                  <div style={{padding:"9px 14px",borderRadius:8,background:sg.cor+"18",border:`1px solid ${sg.cor}33`,marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:18}}>{sg.icon}</span>
                    <span style={{fontSize:"0.82em",fontWeight:700,color:sg.cor}}>{sg.txt}</span>
                  </div>
                  {/* KPIs */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:18}}>
                    {[
                      ["ROAS",roas_cr.toFixed(2)+"x",roasC(roas_cr),`Média: ${avgROAS_acc.toFixed(2)}x`,roas_cr>avgROAS_acc],
                      ["CTR",fmt2(m.ctr)+"%",ctrC(m.ctr),`Média: ${fmt2(avgCTR_acc)}%`,m.ctr>avgCTR_acc],
                      ["CPA","R$"+fmt2(m.cpa),cpaC(m.cpa),`Média: R$${fmt2(avgCPA_acc)}`,m.cpa<avgCPA_acc],
                      ["Lucro est.","R$"+fmt2(lucro_cr),lucro_cr>0?"var(--green)":"var(--red)","Receita – Gasto",null],
                    ].map(([l,v,c,sub,acima])=>(
                      <div key={l} style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:9,padding:"10px 12px",textAlign:"center"}}>
                        <div style={{fontSize:"0.58em",color:"var(--muted)",fontFamily:"Space Mono",textTransform:"uppercase",marginBottom:4}}>{l}</div>
                        <div style={{fontSize:"0.95em",fontWeight:800,fontFamily:"Space Mono",color:c}}>{v}</div>
                        <div style={{fontSize:"0.58em",color:"var(--muted)",marginTop:3,fontFamily:"Space Mono"}}>{sub}</div>
                        {acima!==null&&<div style={{fontSize:"0.55em",marginTop:2,color:acima?"var(--green)":"var(--red)",fontFamily:"Space Mono"}}>{acima?"↑ acima da média":"↓ abaixo da média"}</div>}
                      </div>
                    ))}
                  </div>
                  {/* Gráfico 7 dias */}
                  <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:10,padding:14,marginBottom:16}}>
                    <div style={{fontSize:"0.6em",fontFamily:"Space Mono",color:"var(--muted)",textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>📈 Evolução – Últimos 7 dias</div>
                    <div style={{height:80}}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={cr.hist7} margin={{top:4,right:4,bottom:0,left:0}}>
                          <defs>
                            <linearGradient id="gCTR" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor="#7c3aed" stopOpacity={0.4}/>
                              <stop offset="95%" stopColor="#7c3aed" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08"/>
                          <XAxis dataKey="dia" tick={{fontSize:9,fill:"#7070a0",fontFamily:"Space Mono"}} axisLine={false} tickLine={false}/>
                          <YAxis hide/>
                          <Tooltip contentStyle={{background:"#1c1c2e",border:"1px solid #ffffff1a",fontSize:"0.7em",fontFamily:"Space Mono"}}/>
                          <Area type="monotone" dataKey="ctr" name="CTR %" stroke="#7c3aed" fill="url(#gCTR)" strokeWidth={2}/>
                          <Area type="monotone" dataKey="cpc" name="CPC R$" stroke="#00ffaa" fill="none" strokeWidth={1.5} strokeDasharray="3 3"/>
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  {/* Copies */}
                  <div style={{marginBottom:14}}>
                    <div style={{fontSize:"0.6em",fontFamily:"Space Mono",color:"var(--muted)",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>📝 Copies</div>
                    {cr.copies.map((c,i)=>(
                      <div key={i} style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:7,padding:"7px 11px",fontSize:"0.78em",color:"#a0a0c8",fontStyle:"italic",marginBottom:6}}>"{c}"</div>
                    ))}
                  </div>
                  {/* Tags */}
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
                    {(cr.tags||[]).map(t=>(
                      <span key={t} style={{fontSize:"0.6em",fontFamily:"Space Mono",background:"var(--accent)18",color:"var(--accent)",border:"1px solid var(--accent)33",padding:"2px 8px",borderRadius:100}}>{t}</span>
                    ))}
                  </div>
                  <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                    <button onClick={()=>{toggleFavCriativo(cr.id);onClose();}} style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:8,padding:"7px 14px",color:cr.favorito?"var(--yellow)":"var(--muted)",fontFamily:"Space Mono",fontSize:"0.72em",cursor:"pointer"}}>
                      {cr.favorito?"★ Favoritado":"☆ Favoritar"}
                    </button>
                    <button onClick={()=>{duplicarCriativo(cr);onClose();}} style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:8,padding:"7px 14px",color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.72em",cursor:"pointer"}}>⎘ Duplicar</button>
                    <button onClick={()=>{setCriativoModal(cr);onClose();}} style={{background:"linear-gradient(135deg,var(--accent),var(--accent2))",border:"none",borderRadius:8,padding:"7px 16px",color:"#fff",fontWeight:700,fontSize:"0.78em",cursor:"pointer"}}>✏️ Editar</button>
                  </div>
                </div>
              </div>
            );
          }

          // ── Modal de cadastro/edição ──
          function CriativoModal({c,onClose}){
            const isNew=!c;
            const [form,setForm]=useState(c||{nome:"",campanha:"",formato:"imagem",status:"rascunho",thumb:null,
              tags:[],copies:[""],briefing:{cores:["#7c3aed","#080810"],referencia:"",formato:""},
              metricas:{impressoes:0,cliques:0,ctr:0,cpc:0,conversoes:0,cpa:0,gasto:0,receita:0}});
            const inpS={width:"100%",background:"var(--surface2)",border:"1px solid var(--border-bright)",
              borderRadius:7,color:"var(--text)",fontFamily:"Space Mono,monospace",fontSize:"0.8em",
              padding:"7px 9px",outline:"none",boxSizing:"border-box"};
            const lbl={display:"block",fontSize:"0.62em",color:"var(--muted)",fontFamily:"Space Mono,monospace",
              textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:5};
            const imgRef=useRef();
            return(
              <div style={{position:"fixed",inset:0,background:"#00000090",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
                <div style={{background:"var(--surface)",border:"1px solid var(--border-bright)",borderRadius:16,width:"100%",maxWidth:620,maxHeight:"90vh",overflowY:"auto",padding:24}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                    <div style={{fontSize:"0.9em",fontWeight:800}}>{isNew?"➕ Novo Criativo":"✏️ Editar Criativo"}</div>
                    <button onClick={onClose} style={{background:"none",border:"none",color:"var(--muted)",fontSize:18,cursor:"pointer"}}>✕</button>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                    <div style={{gridColumn:"1/-1"}}>
                      <label style={lbl}>Nome do criativo</label>
                      <input value={form.nome} onChange={e=>setForm(p=>({...p,nome:e.target.value}))} placeholder="Ex: VSL Principal v3" style={inpS}/>
                    </div>
                    <div>
                      <label style={lbl}>Campanha</label>
                      <select value={form.campanha} onChange={e=>setForm(p=>({...p,campanha:e.target.value}))} style={inpS}>
                        <option value="">Selecionar...</option>
                        {CAMPANHAS_MOCK.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Formato</label>
                      <select value={form.formato} onChange={e=>setForm(p=>({...p,formato:e.target.value}))} style={inpS}>
                        {["imagem","video","carrossel","stories","reels"].map(f=><option key={f} value={f}>{f.charAt(0).toUpperCase()+f.slice(1)}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Status</label>
                      <select value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))} style={inpS}>
                        {Object.entries(STATUS_CR).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Proporção / Placement</label>
                      <input value={form.briefing.formato} onChange={e=>setForm(p=>({...p,briefing:{...p.briefing,formato:e.target.value}}))} placeholder="Ex: 9:16 – Stories" style={inpS}/>
                    </div>
                  </div>
                  {/* Upload thumb */}
                  <div style={{marginTop:14}}>
                    <label style={lbl}>Thumbnail / Preview</label>
                    <div onClick={()=>imgRef.current.click()} style={{width:"100%",height:form.thumb?140:90,border:"2px dashed var(--border-bright)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",overflow:"hidden",background:"var(--surface2)"}}>
                      {form.thumb?<img src={form.thumb} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                        :<div style={{textAlign:"center",color:"var(--muted)"}}>
                          <div style={{fontSize:22,marginBottom:4}}>📷</div>
                          <div style={{fontSize:"0.7em",fontFamily:"Space Mono"}}>Arraste ou clique para subir imagem</div>
                        </div>}
                      <input ref={imgRef} type="file" accept="image/*" style={{display:"none"}}
                        onChange={e=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=ev=>setForm(p=>({...p,thumb:ev.target.result}));r.readAsDataURL(f);}}/>
                    </div>
                  </div>
                  <div style={{marginTop:14}}>
                    <label style={lbl}>Referência / Tom</label>
                    <input value={form.briefing.referencia} onChange={e=>setForm(p=>({...p,briefing:{...p.briefing,referencia:e.target.value}}))} placeholder="Tom direto, urgência, screenshot de resultado" style={inpS}/>
                  </div>
                  {/* Paleta */}
                  <div style={{marginTop:14}}>
                    <label style={lbl}>Paleta de cores</label>
                    <div style={{display:"flex",gap:12}}>
                      {form.briefing.cores.map((cor,i)=>(
                        <div key={i} style={{display:"flex",alignItems:"center",gap:6}}>
                          <input type="color" value={cor} onChange={e=>{const nc=[...form.briefing.cores];nc[i]=e.target.value;setForm(p=>({...p,briefing:{...p.briefing,cores:nc}}));}}
                            style={{width:30,height:30,border:"none",borderRadius:6,cursor:"pointer",background:"none"}}/>
                          <span style={{fontSize:"0.65em",fontFamily:"Space Mono",color:"var(--muted)"}}>{cor}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Tags */}
                  <div style={{marginTop:14}}>
                    <label style={lbl}>Tags</label>
                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {ALL_TAGS.map(t=>{
                        const sel=(form.tags||[]).includes(t);
                        return(<span key={t} onClick={()=>setForm(p=>({...p,tags:sel?p.tags.filter(x=>x!==t):[...(p.tags||[]),t]}))}
                          style={{fontSize:"0.62em",fontFamily:"Space Mono",cursor:"pointer",padding:"3px 9px",borderRadius:100,
                            background:sel?"var(--accent)":"var(--surface2)",color:sel?"#fff":"var(--muted)",border:`1px solid ${sel?"var(--accent)":"var(--border-bright)"}`,transition:"all .15s"}}>{t}</span>);
                      })}
                    </div>
                  </div>
                  {/* Copies */}
                  <div style={{marginTop:14}}>
                    <label style={lbl}>Copies / Headlines</label>
                    {form.copies.map((cp,i)=>(
                      <div key={i} style={{display:"flex",gap:8,marginBottom:8}}>
                        <input value={cp} onChange={e=>{const nc=[...form.copies];nc[i]=e.target.value;setForm(p=>({...p,copies:nc}));}} placeholder={`Copy ${i+1}`} style={{...inpS,flex:1}}/>
                        {form.copies.length>1&&<button onClick={()=>setForm(p=>({...p,copies:p.copies.filter((_,j)=>j!==i)}))} style={{background:"none",border:"1px solid #ff336640",borderRadius:7,color:"var(--red)",cursor:"pointer",padding:"0 10px",fontSize:12}}>✕</button>}
                      </div>
                    ))}
                    <button onClick={()=>setForm(p=>({...p,copies:[...p.copies,""]}))} style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:7,padding:"5px 12px",color:"var(--muted)",fontSize:"0.72em",fontFamily:"Space Mono",cursor:"pointer"}}>+ Add copy</button>
                  </div>
                  {/* Métricas */}
                  <div style={{marginTop:16,padding:14,background:"var(--surface2)",borderRadius:10,border:"1px solid var(--border)"}}>
                    <div style={{fontSize:"0.6em",fontFamily:"Space Mono",color:"var(--muted)",textTransform:"uppercase",letterSpacing:1,marginBottom:12}}>📊 Métricas de performance</div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
                      {[["impressoes","Impressões",0],["cliques","Cliques",0],["ctr","CTR (%)",2],["cpc","CPC (R$)",2],["conversoes","Conversões",0],["cpa","CPA (R$)",2],["gasto","Gasto (R$)",2],["receita","Receita (R$)",2]].map(([key,label,dec])=>(
                        <div key={key}>
                          <label style={{...lbl,marginBottom:3}}>{label}</label>
                          <input type="number" step={dec>0?0.01:1} value={form.metricas[key]||0}
                            onChange={e=>setForm(p=>({...p,metricas:{...p.metricas,[key]:parseFloat(e.target.value)||0}}))}
                            style={{...inpS,textAlign:"right"}}/>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:20}}>
                    <button onClick={onClose} style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:8,padding:"8px 18px",color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.75em",cursor:"pointer"}}>Cancelar</button>
                    <button onClick={()=>saveCriativo(form)} style={{background:"linear-gradient(135deg,var(--accent),var(--accent2))",border:"none",borderRadius:8,padding:"8px 20px",color:"#fff",fontWeight:700,fontSize:"0.82em",cursor:"pointer"}}>
                      {isNew?"Criar Criativo":"Salvar"}
                    </button>
                  </div>
                </div>
              </div>
            );
          }

          return(
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {/* Stats rápidas */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10}}>
                {[
                  {label:"Ativos",value:criativos.filter(c=>c.status==="ativo").length,icon:"✅",color:"var(--green)"},
                  {label:"Impressões Totais",value:(totalImpr/1000).toFixed(1)+"k",icon:"👁",color:"var(--text)"},
                  {label:"Gasto Total",value:"R$"+(totalGasto/1000).toFixed(1)+"k",icon:"💸",color:"var(--yellow)"},
                  {label:"Receita Total",value:"R$"+(totalReceita/1000).toFixed(1)+"k",icon:"💰",color:totalReceita>totalGasto?"var(--green)":"var(--red)"},
                  {label:"Melhor CTR",value:melhor?melhor.metricas.ctr.toFixed(2)+"%":"—",icon:"🏆",color:"var(--green)",sub:melhor?.nome},
                ].map((s,i)=>(
                  <div key={i} className="card" style={{position:"relative",overflow:"hidden"}}>
                    <div style={{fontSize:"0.6em",color:"var(--muted)",fontFamily:"Space Mono",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>{s.icon} {s.label}</div>
                    <div style={{fontSize:"1.3em",fontWeight:800,fontFamily:"Space Mono",color:s.color}}>{s.value}</div>
                    {s.sub&&<div style={{fontSize:"0.6em",color:"var(--muted)",marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.sub}</div>}
                  </div>
                ))}
              </div>

              {/* Toolbar */}
              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                {/* Busca */}
                <input value={criativoSearch} onChange={e=>setCriativoSearch(e.target.value)} placeholder="🔍 Buscar por nome ou tag..."
                  style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:7,color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.75em",padding:"6px 12px",outline:"none",minWidth:180}}/>
                {/* Filtro status */}
                <select value={criativoFiltroStatus} onChange={e=>setCriativoFiltroStatus(e.target.value)}
                  style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:7,color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.72em",padding:"6px 10px",outline:"none"}}>
                  <option value="todos">Todos status</option>
                  {Object.entries(STATUS_CR).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                </select>
                {/* Filtro campanha */}
                <select value={criativoCampanha} onChange={e=>setCriativoCampanha(e.target.value)}
                  style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:7,color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.72em",padding:"6px 10px",outline:"none"}}>
                  <option value="todas">Todas campanhas</option>
                  {CAMPANHAS_MOCK.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {/* Filtro tag */}
                <select value={criativoFiltroTag} onChange={e=>setCriativoFiltroTag(e.target.value)}
                  style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:7,color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.72em",padding:"6px 10px",outline:"none"}}>
                  <option value="todas">Todas tags</option>
                  {allTags.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
                {/* Sort */}
                <select value={criativoSortBy} onChange={e=>setCriativoSortBy(e.target.value)}
                  style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:7,color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.72em",padding:"6px 10px",outline:"none"}}>
                  {[["ctr","↓ CTR"],["cpa","↑ CPA"],["roas","↓ ROAS"],["impressoes","↓ Impressões"],["nome","A–Z Nome"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}
                </select>
                <button onClick={()=>setCriativoModal("novo")} style={{marginLeft:"auto",background:"linear-gradient(135deg,var(--accent),var(--accent2))",border:"none",borderRadius:8,padding:"7px 16px",color:"#fff",fontWeight:700,fontSize:"0.78em",cursor:"pointer",boxShadow:"0 4px 16px #7c3aed33"}}>
                  ＋ Novo Criativo
                </button>
                <div style={{display:"flex",background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:8,overflow:"hidden"}}>
                  {[["cards","⊞"],["tabela","☰"]].map(([v,lbl])=>(
                    <button key={v} onClick={()=>setCriativoView(v)} style={{padding:"6px 12px",fontSize:"0.78em",fontWeight:700,border:"none",cursor:"pointer",background:criativoView===v?"var(--accent)":"transparent",color:criativoView===v?"#fff":"var(--muted)",transition:"all .15s"}}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              {/* Conteúdo */}
              {filtered.length===0?(
                <div style={{textAlign:"center",padding:"60px 0",color:"var(--muted)"}}>
                  <div style={{fontSize:40,marginBottom:12}}>🎨</div>
                  <div>Nenhum criativo encontrado</div>
                </div>
              ):criativoView==="cards"?(
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(275px,1fr))",gap:14}}>
                  {filtered.map(cr=>{
                    const m=cr.metricas;
                    const roas_cr=m.receita/(m.gasto||1);
                    const camp=CAMPANHAS_MOCK.find(c=>c.id===cr.campanha);
                    const st=STATUS_CR[cr.status]||STATUS_CR.rascunho;
                    const sg=sugestao(m);
                    return(
                      <div key={cr.id} className="card" style={{padding:0,overflow:"hidden",cursor:"pointer",transition:"border-color .2s,transform .15s"}}
                        onMouseEnter={e=>{e.currentTarget.style.borderColor="#7c3aed44";e.currentTarget.style.transform="translateY(-2px)";}}
                        onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.transform="translateY(0)";}}>
                        {/* Thumb */}
                        <div style={{height:115,background:"var(--surface2)",position:"relative",overflow:"hidden"}} onClick={()=>setCriativoModal({mode:"detalhe",cr})}>
                          {cr.thumb?<img src={cr.thumb} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                            :<div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",
                              background:`linear-gradient(135deg,${cr.briefing.cores[0]}33,${cr.briefing.cores[1]}33)`}}>
                              <span style={{fontSize:26}}>{fmtIcons[cr.formato]||"📄"}</span>
                            </div>}
                          {/* Sugestão badge */}
                          <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"4px 8px",background:"#080810cc",fontSize:"0.58em",fontFamily:"Space Mono",color:sg.cor,display:"flex",alignItems:"center",gap:4}}>
                            {sg.icon} {sg.txt}
                          </div>
                          {/* Ações */}
                          <div style={{position:"absolute",top:6,right:6,display:"flex",gap:3}}>
                            <button onClick={e=>{e.stopPropagation();toggleFavCriativo(cr.id);}} style={{background:"#080810cc",border:"none",borderRadius:5,padding:"2px 6px",fontSize:11,cursor:"pointer",color:cr.favorito?"var(--yellow)":"#ffffff60"}}>★</button>
                            <button onClick={e=>{e.stopPropagation();duplicarCriativo(cr);}} style={{background:"#080810cc",border:"none",borderRadius:5,padding:"2px 6px",fontSize:11,cursor:"pointer",color:"#ffffff60"}}>⎘</button>
                            <button onClick={e=>{e.stopPropagation();setCriativoModal(cr);}} style={{background:"#080810cc",border:"none",borderRadius:5,padding:"2px 6px",fontSize:10,cursor:"pointer",color:"var(--muted)"}}>✏️</button>
                            <button onClick={e=>{e.stopPropagation();apiDeleteCriativo(cr.id);}} style={{background:"#080810cc",border:"none",borderRadius:5,padding:"2px 6px",fontSize:10,cursor:"pointer",color:"var(--red)"}}>🗑</button>
                          </div>
                        </div>
                        <div style={{padding:"11px 13px"}} onClick={()=>setCriativoModal({mode:"detalhe",cr})}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:5}}>
                            <div>
                              <div style={{fontSize:"0.82em",fontWeight:800,marginBottom:1}}>{cr.nome}</div>
                              <div style={{fontSize:"0.6em",color:"var(--muted)",fontFamily:"Space Mono"}}>{camp?.name||"Sem campanha"}</div>
                            </div>
                            <span style={{fontSize:"0.56em",fontWeight:700,fontFamily:"Space Mono",textTransform:"uppercase",padding:"2px 6px",borderRadius:100,background:st.color+"22",color:st.color,border:`1px solid ${st.color}44`,whiteSpace:"nowrap"}}>{st.label}</span>
                          </div>
                          {/* Tags */}
                          <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:8}}>
                            {(cr.tags||[]).slice(0,3).map(t=>(
                              <span key={t} style={{fontSize:"0.55em",fontFamily:"Space Mono",background:"var(--accent)18",color:"var(--accent)",padding:"1px 6px",borderRadius:100}}>{t}</span>
                            ))}
                          </div>
                          {/* KPIs + Sparkline */}
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:5,flex:1}}>
                              {[["CTR",fmt2(m.ctr)+"%",ctrC(m.ctr)],["ROAS",roas_cr.toFixed(1)+"x",roasC(roas_cr)],["CPA","R$"+fmt2(m.cpa),cpaC(m.cpa)]].map(([l,v,c])=>(
                                <div key={l} style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:6,padding:"4px 6px",textAlign:"center"}}>
                                  <div style={{fontSize:"0.52em",color:"var(--muted)",fontFamily:"Space Mono",textTransform:"uppercase"}}>{l}</div>
                                  <div style={{fontSize:"0.75em",fontWeight:700,fontFamily:"Space Mono",color:c}}>{v}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                          {/* vs média */}
                          <div style={{display:"flex",gap:8,fontSize:"0.58em",fontFamily:"Space Mono",color:"var(--muted)"}}>
                            <span style={{color:m.ctr>avgCTR_acc?"var(--green)":"var(--red)"}}>CTR {m.ctr>avgCTR_acc?"↑":"↓"} média</span>
                            <span style={{color:m.cpa<avgCPA_acc?"var(--green)":"var(--red)"}}>CPA {m.cpa<avgCPA_acc?"↑":"↓"} média</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ):(
                // ── TABELA com sort e busca ──
                <div className="card" style={{padding:0,overflow:"hidden"}}>
                  <div style={{overflowX:"auto"}}>
                    <table className="decision-table" style={{fontSize:"0.73em"}}>
                      <thead>
                        <tr>
                          {[["nome","Nome"],["ctr","CTR"],["roas","ROAS"],["cpa","CPA"],["impressoes","Impressões"],["cpc","CPC"],["conversoes","Conv."],["gasto","Gasto"],["receita","Receita"]].map(([k,h])=>(
                            <th key={k} onClick={()=>setCriativoSortBy(k)} style={{cursor:"pointer",userSelect:"none",color:criativoSortBy===k?"var(--text)":"var(--muted)",transition:"color .15s"}}>
                              {h} {criativoSortBy===k?"↓":""}
                            </th>
                          ))}
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map(cr=>{
                          const m=cr.metricas;
                          const roas_cr=m.receita/(m.gasto||1);
                          const st=STATUS_CR[cr.status]||STATUS_CR.rascunho;
                          const sg=sugestao(m);
                          return(
                            <tr key={cr.id} style={{cursor:"pointer"}} onClick={()=>setCriativoModal({mode:"detalhe",cr})}>
                              <td>
                                <div style={{fontWeight:700}}>{fmtIcons[cr.formato]||"📄"} {cr.nome}</div>
                                <div style={{display:"flex",gap:3,marginTop:3,flexWrap:"wrap"}}>
                                  {(cr.tags||[]).map(t=><span key={t} style={{fontSize:"0.75em",fontFamily:"Space Mono",background:"var(--accent)18",color:"var(--accent)",padding:"0px 5px",borderRadius:100}}>{t}</span>)}
                                </div>
                                <div style={{fontSize:"0.72em",marginTop:3,color:sg.cor,fontFamily:"Space Mono"}}>{sg.icon} {sg.txt}</div>
                              </td>
                              <td style={{fontFamily:"Space Mono",fontWeight:700,color:ctrC(m.ctr)}}>{fmt2(m.ctr)}%</td>
                              <td style={{fontFamily:"Space Mono",fontWeight:700,color:roasC(roas_cr)}}>{roas_cr.toFixed(2)}x</td>
                              <td style={{fontFamily:"Space Mono",fontWeight:700,color:cpaC(m.cpa)}}>R${fmt2(m.cpa)}</td>
                              <td style={{fontFamily:"Space Mono",color:"var(--muted)"}}>{m.impressoes.toLocaleString("pt-BR")}</td>
                              <td style={{fontFamily:"Space Mono",color:"var(--muted)"}}>R${fmt2(m.cpc)}</td>
                              <td style={{fontFamily:"Space Mono",color:"var(--muted)"}}>{m.conversoes}</td>
                              <td style={{fontFamily:"Space Mono",color:"var(--yellow)"}}>R${fmt2(m.gasto||0)}</td>
                              <td style={{fontFamily:"Space Mono",color:m.receita>m.gasto?"var(--green)":"var(--red)"}}>R${fmt2(m.receita||0)}</td>
                              <td onClick={e=>e.stopPropagation()}>
                                <div style={{display:"flex",gap:5}}>
                                  <button onClick={()=>duplicarCriativo(cr)} title="Duplicar" style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:6,padding:"3px 8px",fontSize:10,cursor:"pointer",color:"var(--muted)"}}>⎘</button>
                                  <button onClick={()=>setCriativoModal(cr)} title="Editar" style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:6,padding:"3px 8px",fontSize:10,cursor:"pointer",color:"var(--muted)"}}>✏️</button>
                                  <button onClick={()=>apiDeleteCriativo(cr.id)} title="Deletar" style={{background:"var(--surface2)",border:"1px solid #ff336640",borderRadius:6,padding:"3px 8px",fontSize:10,cursor:"pointer",color:"var(--red)"}}>🗑</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Modais */}
              {criativoModal&&criativoModal.mode==="detalhe"&&<DetalheModal cr={criativoModal.cr} onClose={()=>setCriativoModal(null)}/>}
              {criativoModal&&criativoModal.mode!=="detalhe"&&<CriativoModal c={criativoModal==="novo"?null:criativoModal} onClose={()=>setCriativoModal(null)}/>}
            </div>
          );
        })()}

        {/* ═══ ESPIONAGEM ═══ */}
        {tab==="espionagem"&&(()=>{
          const plataformas=["Meta","Google","TikTok","Todos"];
          const paises=[["BR","🇧🇷 Brasil"],["MX","🇲🇽 México"],["CL","🇨🇱 Chile"],["CO","🇨🇴 Colômbia"],["Todos","🌎 Todos"]];
          const midias=[["todos","Todos"],["imagem","Imagem"],["video","Vídeo"],["carrossel","Carrossel"]];
          const fmtPlat={Meta:"#1877f2",TikTok:"#ff0050",Google:"#34a853"};
          const isFav=(id)=>espFavs.some(f=>f.id===id);

          function EspCard({ad,onOpen}){
            const fav=isFav(ad.id);
            return(
              <div className="card" style={{padding:0,overflow:"hidden",cursor:"pointer",transition:"border-color .2s,transform .15s"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=(fmtPlat[ad.plataforma]||"#7c3aed")+"55";e.currentTarget.style.transform="translateY(-2px)";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.transform="translateY(0)";}}>
                {/* Thumb */}
                <div style={{height:130,background:"var(--surface2)",position:"relative",overflow:"hidden"}} onClick={()=>onOpen(ad)}>
                  <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",
                    background:`linear-gradient(135deg,${fmtPlat[ad.plataforma]||"#7c3aed"}22,#0f0f1a)`}}>
                    <span style={{fontSize:36}}>{ad.formato==="video"?"🎬":ad.formato==="carrossel"?"📋":"🖼"}</span>
                  </div>
                  {/* Plataforma badge */}
                  <div style={{position:"absolute",top:8,left:8,fontSize:"0.58em",fontFamily:"Space Mono",fontWeight:700,
                    background:fmtPlat[ad.plataforma]||"#7c3aed",color:"#fff",padding:"2px 7px",borderRadius:100}}>{ad.plataforma}</div>
                  {/* Fav button */}
                  <button onClick={e=>{e.stopPropagation();toggleEspFav(ad);}} style={{position:"absolute",top:6,right:6,background:"#080810cc",border:"none",borderRadius:5,padding:"3px 7px",fontSize:13,cursor:"pointer",color:fav?"var(--yellow)":"#ffffff50"}}>★</button>
                </div>
                <div style={{padding:"11px 13px"}} onClick={()=>onOpen(ad)}>
                  <div style={{fontSize:"0.78em",fontWeight:800,marginBottom:3}}>{ad.anunciante}</div>
                  <div style={{fontSize:"0.72em",color:"var(--muted)",lineHeight:1.55,marginBottom:8,
                    display:"-webkit-box",WebkitLineClamp:3,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
                    {ad.copy}
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{display:"flex",gap:10,fontSize:"0.62em",fontFamily:"Space Mono",color:"var(--muted)"}}>
                      <span>❤️ {ad.stats.likes.toLocaleString("pt-BR")}</span>
                      <span>💬 {ad.stats.comments.toLocaleString("pt-BR")}</span>
                      <span>🔁 {ad.stats.shares.toLocaleString("pt-BR")}</span>
                    </div>
                    <span style={{fontSize:"0.58em",fontFamily:"Space Mono",color:"var(--muted)"}}>
                      {new Date(ad.inicio).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                </div>
              </div>
            );
          }

          function EspModal({ad,onClose}){
            const fav=isFav(ad.id);
            return(
              <div style={{position:"fixed",inset:0,background:"#00000090",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
                <div style={{background:"var(--surface)",border:"1px solid var(--border-bright)",borderRadius:16,width:"100%",maxWidth:640,maxHeight:"90vh",overflowY:"auto",padding:24}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:18}}>
                    <div>
                      <div style={{fontSize:"0.95em",fontWeight:800}}>{ad.anunciante}</div>
                      <div style={{fontSize:"0.62em",fontFamily:"Space Mono",color:"var(--muted)",marginTop:2}}>
                        <span style={{background:fmtPlat[ad.plataforma]||"#7c3aed",color:"#fff",padding:"1px 6px",borderRadius:100,marginRight:6}}>{ad.plataforma}</span>
                        {ad.formato} · {paises.find(p=>p[0]===ad.pais)?.[1]||ad.pais} · desde {new Date(ad.inicio).toLocaleDateString("pt-BR")}
                      </div>
                    </div>
                    <button onClick={onClose} style={{background:"none",border:"none",color:"var(--muted)",fontSize:18,cursor:"pointer"}}>✕</button>
                  </div>
                  {/* Preview */}
                  <div style={{background:"var(--surface2)",borderRadius:12,height:180,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:18,
                    background:`linear-gradient(135deg,${fmtPlat[ad.plataforma]||"#7c3aed"}22,var(--surface2))`}}>
                    <span style={{fontSize:52}}>{ad.formato==="video"?"🎬":ad.formato==="carrossel"?"📋":"🖼"}</span>
                  </div>
                  {/* Copy */}
                  <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:10,padding:14,marginBottom:14}}>
                    <div style={{fontSize:"0.58em",fontFamily:"Space Mono",color:"var(--muted)",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>📝 Copy principal</div>
                    <div style={{fontSize:"0.85em",lineHeight:1.7,color:"var(--text)"}}>{ad.copy}</div>
                  </div>
                  {/* Stats */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:14}}>
                    {[["❤️ Curtidas",ad.stats.likes.toLocaleString("pt-BR")],["💬 Comentários",ad.stats.comments.toLocaleString("pt-BR")],["🔁 Compartilhamentos",ad.stats.shares.toLocaleString("pt-BR")]].map(([l,v])=>(
                      <div key={l} style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:8,padding:"8px 12px",textAlign:"center"}}>
                        <div style={{fontSize:"0.6em",fontFamily:"Space Mono",color:"var(--muted)",marginBottom:4}}>{l}</div>
                        <div style={{fontSize:"0.95em",fontWeight:800,fontFamily:"Space Mono",color:"var(--text)"}}>{v}</div>
                      </div>
                    ))}
                  </div>
                  {/* CTA e LP */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                    <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:8,padding:"8px 12px"}}>
                      <div style={{fontSize:"0.58em",fontFamily:"Space Mono",color:"var(--muted)",textTransform:"uppercase",marginBottom:4}}>Call to Action</div>
                      <div style={{fontSize:"0.8em",fontWeight:700,color:"var(--accent)"}}>{ad.cta}</div>
                    </div>
                    <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:8,padding:"8px 12px"}}>
                      <div style={{fontSize:"0.58em",fontFamily:"Space Mono",color:"var(--muted)",textTransform:"uppercase",marginBottom:4}}>Landing Page</div>
                      <a href={ad.lp} target="_blank" rel="noreferrer" style={{fontSize:"0.75em",color:"var(--blue)",textDecoration:"none",wordBreak:"break-all"}}>{ad.lp}</a>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                    <button onClick={()=>{toggleEspFav(ad);}} style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:8,padding:"7px 14px",color:fav?"var(--yellow)":"var(--muted)",fontFamily:"Space Mono",fontSize:"0.72em",cursor:"pointer"}}>
                      {fav?"★ Salvo":"☆ Salvar"}
                    </button>
                    <a href={`https://www.facebook.com/ads/library/?id=${ad.adId}`} target="_blank" rel="noreferrer" style={{background:"linear-gradient(135deg,#1877f2,#0d5dbf)",border:"none",borderRadius:8,padding:"7px 16px",color:"#fff",fontWeight:700,fontSize:"0.78em",cursor:"pointer",textDecoration:"none",display:"flex",alignItems:"center",gap:6}}>
                      🔗 Ver na Ad Library
                    </a>
                  </div>
                </div>
              </div>
            );
          }

          return(
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              {/* Header + Config API */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
                <div>
                  <div style={{fontSize:"1em",fontWeight:800,marginBottom:4}}>🕵️ Espionagem de Anúncios</div>
                  <div style={{fontSize:"0.72em",fontFamily:"Space Mono",color:"var(--muted)"}}>Monitore concorrentes e encontre inspiração de criativos vencedores</div>
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <input value={espApiKey} onChange={e=>setEspApiKey(e.target.value)} placeholder="🔑 Token Meta Ad Library (opcional)"
                    style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:7,color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.7em",padding:"6px 10px",outline:"none",width:240}}/>
                </div>
              </div>

              {/* Abas busca / favoritos */}
              <div style={{display:"flex",background:"var(--surface)",border:"1px solid var(--border-bright)",borderRadius:10,padding:4,width:"fit-content",gap:4}}>
                {[["busca","🔍 Busca"],["favoritos",`★ Favoritos (${espFavs.length})`]].map(([v,l])=>(
                  <button key={v} onClick={()=>setEspAba(v)} style={{padding:"5px 14px",fontSize:"0.72em",fontWeight:700,border:"none",cursor:"pointer",borderRadius:7,
                    background:espAba===v?"var(--surface3)":"transparent",color:espAba===v?"var(--text)":"var(--muted)",transition:"all .15s"}}>{l}</button>
                ))}
              </div>

              {espAba==="busca"&&<>
                {/* Filtros */}
                <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                  <input value={espSearch} onChange={e=>setEspSearch(e.target.value)} placeholder="Buscar por marca, copy, nicho..."
                    onKeyDown={e=>e.key==="Enter"&&buscarEspionagem()}
                    style={{flex:1,minWidth:200,background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:7,color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.75em",padding:"8px 12px",outline:"none"}}/>
                  {/* Plataforma */}
                  <div style={{display:"flex",background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:8,overflow:"hidden"}}>
                    {plataformas.map(p=>(
                      <button key={p} onClick={()=>setEspPlataforma(p)} style={{padding:"6px 11px",fontSize:"0.68em",fontWeight:700,border:"none",cursor:"pointer",
                        background:espPlataforma===p?(fmtPlat[p]||"var(--accent)"):"transparent",color:espPlataforma===p?"#fff":"var(--muted)",transition:"all .15s"}}>{p}</button>
                    ))}
                  </div>
                  {/* País */}
                  <select value={espPais} onChange={e=>setEspPais(e.target.value)}
                    style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:7,color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.72em",padding:"6px 10px",outline:"none"}}>
                    {paises.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                  </select>
                  {/* Mídia */}
                  <select value={espMidia} onChange={e=>setEspMidia(e.target.value)}
                    style={{background:"var(--surface2)",border:"1px solid var(--border-bright)",borderRadius:7,color:"var(--text)",fontFamily:"Space Mono",fontSize:"0.72em",padding:"6px 10px",outline:"none"}}>
                    {midias.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                  </select>
                  <button onClick={buscarEspionagem} disabled={espLoading}
                    style={{background:"linear-gradient(135deg,var(--accent),var(--accent2))",border:"none",borderRadius:8,padding:"7px 20px",color:"#fff",fontWeight:700,fontSize:"0.78em",cursor:"pointer",display:"flex",alignItems:"center",gap:8,opacity:espLoading?0.7:1}}>
                    {espLoading?<><div className="spinner"/>Buscando...</>:"🔍 Buscar"}
                  </button>
                </div>

                {/* Resultados */}
                {espLoading&&(
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"40px 0",gap:12,color:"var(--muted)",fontFamily:"Space Mono",fontSize:"0.8em"}}>
                    <div className="spinner2"/> Consultando Ad Library...
                  </div>
                )}
                {!espLoading&&espResultados.length===0&&(
                  <div style={{textAlign:"center",padding:"60px 0",color:"var(--muted)"}}>
                    <div style={{fontSize:40,marginBottom:12}}>🕵️</div>
                    <div style={{marginBottom:6}}>Clique em "Buscar" para encontrar anúncios</div>
                    <div style={{fontSize:"0.72em",fontFamily:"Space Mono"}}>Em modo demo, mostramos dados simulados. Para dados reais, insira um token da Meta Ad Library API.</div>
                  </div>
                )}
                {!espLoading&&espResultados.length>0&&(
                  <>
                    <div style={{fontSize:"0.68em",fontFamily:"Space Mono",color:"var(--muted)"}}>
                      {espResultados.length} anúncio{espResultados.length!==1?"s":""} encontrado{espResultados.length!==1?"s":""}
                      {!espApiKey&&<span style={{color:"var(--yellow)",marginLeft:8}}>⚠️ Modo demo – dados simulados</span>}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(270px,1fr))",gap:14}}>
                      {espResultados.map(ad=><EspCard key={ad.id} ad={ad} onOpen={setEspModal}/>)}
                    </div>
                  </>
                )}
              </>}

              {espAba==="favoritos"&&(
                espFavs.length===0?(
                  <div style={{textAlign:"center",padding:"60px 0",color:"var(--muted)"}}>
                    <div style={{fontSize:40,marginBottom:12}}>★</div>
                    <div>Nenhum anúncio favorito ainda</div>
                    <div style={{fontSize:"0.72em",fontFamily:"Space Mono",marginTop:6}}>Busque anúncios e clique em ★ para salvar</div>
                  </div>
                ):(
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(270px,1fr))",gap:14}}>
                    {espFavs.map(ad=><EspCard key={ad.id} ad={ad} onOpen={setEspModal}/>)}
                  </div>
                )
              )}

              {espModal&&<EspModal ad={espModal} onClose={()=>setEspModal(null)}/>}
            </div>
          );
        })()}

      </div>
    </>
  );
}

